import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import LmsTabPages from "../ui/LmsTabPages";
import {
  useFetchData,
  useForm,
  useParseRouteActions,
  IHandleSubmitOptions,
  getPercentageOfAmount,
  removeProp,
  getDropdownSemanticOption,
  parsePercentValue,
  parseCurrencyValue,
  getDropdownSemanticOptionsFromData,
  getArrayFromInputsWithProps,
  appendIdToPath
} from "../../../helpers/GlobalHelper";
import LmsFormGenerator, { BCFormController, IFormGeneratorFieldOptions, FormActionType, IFormGeneratorCustomAction } from "../ui/LmsFormGenerator";
import LmsTable, { ITableActionCustomButton } from "../ui/LmsTable";
import { GlobalConstants, YesNoDropdownOptions, LmsVotingFixedRateChangeOptions, LmsRateTypeOptions, WipPaidToOptions, LmsTransactionSplitOptions, LmsServiceFeeMethod, LmsConversionTypeOptions, unitDropdownOptions } from "../../constants/GlobalConstants";
import { TABLES } from "../../constants/GlobalLabelsDictionaries";
import { Button, Icon } from "semantic-ui-react";
import "../../../styles/lms/lms-loan-pages.scss";
import { IDefaultPagesProps } from "../../interfaces/Lms";
import {
  LmsLoanPropertyTable,
  LmsLoanDetailsTable,
  LmsLoanCommentsTable,
  LmsLoanConditionsTable,
  LmsLoanConditionsExportOnlyTable,
  loanDetailsEntitiesInlineTableEditingFields
} from "../ui/LmsSharedTables";
import BCModal from "../../ui/BCModal";
import { LmsEndpoints } from "../../constants/LmsEndpoints";
import { addContactFormFields, UploadBudgetForm, BudgetDetailsPage } from "../ui/LmsSharedForms";
import { MainServicingDrawTable } from "../Servicing/LmsServicingDraws";
import { FeatureFlags } from "../../constants/LocalServerConstants";
import BCLoader from "../../ui/BCLoader";
import { LmsLoanApplicationConditionsTable } from "./LmsLoanApplications";
import { LmsServicingInspectionCompanyMessagesMainPage } from "../Servicing/LmsServicingInspectionCompanyMessages";
import { LmsModifyBudget } from "../Servicing/LmsServicingModifyBudgets";
import { LoanTransactionHistoriesTable } from "../Servicing/LmsServicingLoanHistory";
import { AmendmentsTable } from "../Servicing/LmsServicingAmendments";
import { ModalSizesType } from "../../interfaces/Global";
import LmsActionInput from "../ui/LmsActionInput";
import { ViewAdditionalInfoForm } from "../Organizations/LmsOrganizationsContacts";
import { guid } from "../../../Helpers";
import DrawManagement from "../Servicing/LmsServicingDrawManagement";
import { useWindowSize } from "../../../hooks/UseWindowsSizeHook";
import { DocuSignTable } from "../Documents/LmsDocuSignStatus";
import { ViewAdditionalBorrowerInfoForm } from "../Organizations/LmsOrganizationsBorrowers";

const addConditionFields = (myForm) => {
  myForm.addCenteredGroup([myForm.getDropdownField("Borrower", "metadata/list-borrower-organizations", { name: "organizationContactId", disabled: true })]);
  myForm.addCenteredGroup([myForm.getDisplayField("Application #", { name: "applicationNumber", disabled: true })]);
  myForm.addCenteredGroup([myForm.getDisplayField("Loan", { disabled: true, name: "loan" })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Status", "metadata/list-condition-statuses", { name: "conditionStatusId", required: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Timing", "metadata/list-condition-timings", { name: "conditionTimingId", required: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Loan Condition Phase", "metadata/list-loan-condition-phases", { name: "loanConditionPhaseId" })]);
  myForm.addCenteredGroup([myForm.getInputField("Source", { disabled: true })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Use as Escrow Instruction", { name: "escrowInstruction" })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Condition", { name: "description", required: true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Reason")]);
  myForm.addCenteredGroup([myForm.getTextareaField("External Notes")]);
  return myForm;
};

const AddConditionForm = ({ conditionSource = "Loan", loanDetailsId }) => {
  const [{ data: loanDetailsData = {}, isLoading }] = useFetchData(loanDetailsId ? `loandetails/${loanDetailsId}` : "");
  const { applicationNumberDisplay, loanNumberDisplay, borrowerOrganizationId, loanId } = loanDetailsData;

  const initialValues: any = {
    source: conditionSource,
    conditionStatusId: GlobalConstants.LOAN_CONDITION_STATUSES.OPEN,
    applicationNumber: applicationNumberDisplay,
    organizationContactId: borrowerOrganizationId,
    loan: loanNumberDisplay
  };

  const myForm = addConditionFields(new BCFormController(initialValues, {
    values: { loanId },
    submitSuffix: "loandetails/conditions",
    excludeFromSubmit: ["loan", "applicationNumber", "organizationContactId"]
  }));

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading}/>;
};

const UploadBudget = ({ loanId = "", setUploadModalOpen = undefined as any }) => {
  const [budgetDocId, setBudgetDocId] = useState("");
  const handleClose = () => {
    if (setUploadModalOpen) {
      setUploadModalOpen(false);
    }
  };

  const onSubmitSuccess = (response) => {
    if (response?.dataUploadId) {
      setBudgetDocId(response?.dataUploadId);
    }
  };

  const myForm = new BCFormController({}, {
    submitSuffix: "budget/uploads",
    values: { loanId },
    onSubmitSuccess,
    payloadDataType: "FormData"
  });

  myForm.addCenteredGroup([myForm.getFileField("Attachment", { name: "attachmentFile", required: true })]);
  const formActions = ["save", "close"];

  return <>
    {!budgetDocId && <LmsFormGenerator myForm={myForm} actions={formActions} handleClose={handleClose} displayMessageLocally />}
    {budgetDocId && <BudgetDetailsPage documentId={budgetDocId} handleClose={handleClose} />}
  </>;
};

const EditConditionForm = ({ id }) => {
  const getConditionSuffix = `loandetails/condition/${id}`;
  const [{ data = {}, isLoading, error }] = useFetchData(getConditionSuffix);
  const loanId = data?.loanId || "";
  const [{ data: loanData = {}, isLoading: isLoanLoading }] = useFetchData(loanId ? `loans/${loanId}` : "");
  const { applicationNumberDisplay, loanNumberDisplay, borrowerOrganizationId } = loanData;

  const initialValues = {
    ...data,
    applicationNumber: applicationNumberDisplay,
    loan: loanNumberDisplay,
    organizationContactId: borrowerOrganizationId
  };

  const formActions = ["save", "save-close", "delete", "close"];

  const myForm = addConditionFields(new BCFormController(initialValues, {
    values: { loanId },
    submitSuffix: "loandetails/conditions",
    deleteSuffix: "loandetails/conditions",
    clearSuffixesOnSubmit: [getConditionSuffix]
  }));

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading || isLoanLoading} error={error} actions={formActions}/>;
};

const AccessReportForm = ({ id }) => {
  if (FeatureFlags.IS_DEBUG) {
     console.log(id);
  }

  const initialValues = {
    selectAll: false,
    loanAgreement: false,
    promissoryNote: false,
    securityInstrument: false,
    guaranty: false,
    indemnityAgreement: false,
    consentToAssignmentOfPlansOrContracts: false,
    uccExhibits: false,
    borrowerConsentAndAuthorization: false,
    budgetDoc: false,
    thirdPartyWireInstructions: false,
    disclosures: false,
    borrowerBrokerStatement: false
  };

  const handleSelectAllClick = (options) => {
    if (options?.data?.value && options?.setInputs) {
      const allSelectedValues = {};
      Object.entries(initialValues).map(([key]) => {
        allSelectedValues[key] = true;
      });
      options?.setInputs(allSelectedValues);
    }
    else {
      const allUnSelectedValues = {};
      Object.entries(initialValues).map(([key]) => {
        allUnSelectedValues[key] = false;
      });
      if (options?.setInputs) {
        options?.setInputs(allUnSelectedValues);
      }
    }
  };

  const myForm = new BCFormController(initialValues, { blankWidth: 8 });
  myForm.addCenteredGroup([myForm.getCheckboxField("Select All/Unselect All", { inlineRight: true, width: 5, name: "selectAll", onChange: handleSelectAllClick })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Loan Agreement", { inlineRight: true, width: 5 })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Promissory Note", { inlineRight: true, width: 5 })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Security Instrument", { inlineRight: true, width: 5 })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Guaranty", { inlineRight: true, width: 5 })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Indemnity Agreement", { inlineRight: true, width: 5 })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Consent to Assignment of Plans or Contracts", { inlineRight: true, width: 5 })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("UCC Exhibits", { inlineRight: true, width: 5, name: "uccExhibits" })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Borrower Consent and Authorization for Loan - LLC or LP", { inlineRight: true, width: 5, name: "borrowerConsentAndAuthorization" })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Budget Doc", { inlineRight: true, width: 5 })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Third Party Wire Instructions", { inlineRight: true, width: 5 })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Disclosures", { inlineRight: true, width: 5 })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Borrower-BrokerStatement", { inlineRight: true, width: 5, name: "borrowerBrokerStatement" })]);

  return <LmsFormGenerator myForm={myForm} actions={["close"]}/>;
};

const EditStatusHistoryForm = ({ loanId, reFetchStatusHistory }) => {
  const [statusChangeConfig, setStatusChangeConfig] = useState(null as any);
  const [{ data = {}, isLoading, error, fetchData }, reFetchData] = useFetchData(loanId ? `loans/loan-status-detail/${loanId}` : "");

  const myForm = new BCFormController(data, {
    values: { loanId },
    submitSuffix: "loans/change-loan-status",
    clearSuffixesOnSubmit: [`loans/loan-status-detail/${loanId}`],
    initIsDisabled: true,
    exitEditOnClose: true,
    onSubmitSuccess: () => {
      reFetchStatusHistory();
      reFetchData();
    },
  });

  myForm.addCenteredGroup([myForm.getCheckboxField("Watch List")]);
  myForm.addCenteredGroup([myForm.getCalendarField("Watch List Effective Date", { required: true })], { isHidden: { watchList: false }, isHiddenOperation: "isFalsy" });
  myForm.addCenteredGroup([myForm.getTextareaField("Watch List Comments", { required: true })], { isHidden: { watchList: false }, isHiddenOperation: "isFalsy" });
  myForm.addCenteredGroup([myForm.getDropdownField("Loan Phase", "loans/list-loan-phases", { required: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Loan Status", loanId ? `loans/list-loan-statuses?loanId=${loanId}&loanPhaseId={id}` : "", {
    dependencyField: "loanPhaseId",
    required: true
  })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Loan Sub Status", loanId ? `loans/list-loan-sub-statuses?loanId=${loanId}&loanStatusId={id}` : "", {
    required: true,
    dependencyField: "loanStatusId"
  })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Comments", {
    name: "changeStatusComment",
    isDisabledOn: data?.loanSubStatusId ? { loanSubStatusId: data?.loanSubStatusId } : {},
    isHiddenOperation: "isEditOnly"
  })]);

  const useFormInstance = useForm(myForm?.values || {}, { myForm }) as any;

  useEffect(() => {
    const loanPhaseId = useFormInstance?.inputs?.loanPhaseId;
    const loanSubStatusId = useFormInstance?.inputs?.loanSubStatusId;

    if (loanPhaseId && loanSubStatusId && !statusChangeConfig) {
      fetchData(`loandetails/loan-status-change-config/${loanId}?loanPhaseId=${loanPhaseId}&loanSubStatusId=${loanSubStatusId}`)
        .then(res => {
          if (res && res.data) {
            setStatusChangeConfig(res.data);
          }
        })
        .catch(e => console.log(e));
    }
  }, [useFormInstance.inputs]);

  return (
    <LmsFormGenerator
      myForm={myForm}
      useFormInstance={useFormInstance}
      isLoading={isLoading}
      error={error}
      actions={["edit", "save", "save-close", "close"]}
      hideBottomActionsOnEdit
    />
  );
};

const AddNewEscrowOfficerForm = ({ termsOptions = {} as any, setAddEscrowOfficerIsOpen }) => {
  const onSubmitSuccess = (responseData) => {
    if (responseData?.id) {
      if (FeatureFlags.IS_DEBUG) {
        console.log(responseData?.id, termsOptions);
      }
    }
  };

  const myForm = new BCFormController({}, { submitSuffix: LmsEndpoints.Orgs.POST.CONTACT_ORG, onSubmitSuccess: onSubmitSuccess });

  myForm.addCenteredGroup([myForm.getDropdownField("Organization", "orgs/list-orgs", { required: true, name: "organizationId", value: termsOptions?.inputs?.organizationId })]);
  addContactFormFields(myForm, true);
  return <LmsFormGenerator myForm={myForm} handleClose={() => setAddEscrowOfficerIsOpen(false)}/>;
};

const LoanCommentsModal = ({ commentId, setIsLoanCommentModalOpen }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`loandetails/comment/${commentId}`);
  const myForm = new BCFormController(data, { initIsDisabled: true });
  myForm.addCenteredGroup([myForm.getDropdownField("Category", "metadata/list-comment-categories", { name: "commentCategoryId", required: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Comment Subject", "metadata/list-comment-subjects", {
    name: "commentSubjectId",
    dependencyField: "commentCategoryId",
    required: true
  })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Comments", { name: "description", required: true })]);

  return <>
    <ViewDetailsForm loanDetailsData={{...data, borrowerOrganization: data?.borrowerOrganizationDisplay}} isShortVersion />
    <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} handleClose={() => setIsLoanCommentModalOpen(false)} actions={["close"]}/>
    <LmsTable
      apiSuffix={`loandetails/comment-contacts/${commentId}`}
      actionButtons={["sendemail"]}
      title="Contacts"
      sendEmailSuffix={`loandetails/send-comment-email/${commentId}`}
      columnLabelsDictionary={{
        officePhone: "Business Phone",
        mlsnumber: "MLS Number",
        description: "Comments"
      }}
      skipClick
    />
  </>;
};

const AmendmentCommentsTable = () => {
  return <div className="amendment-comments-table">
    <div className="amendment-comments-table-container">
      <div className="amendment-comments-table-title">Amendment Comments</div>
      <div><span>Subject</span><span>Created On</span></div>
      <div><span>Description</span><span>Created By</span></div>
    </div>
  </div>;
};

const addAmendmentsFields = (myForm, data) => {
  myForm.addGroupFields([
    myForm.getDropdownField("Amendment Type", "metadata/list-extension-types", {
      name: "extensionTypeId",
      value: data?.extensionTypeId || GlobalConstants.DEFAULT_LIST_VALUES.EXTENSION_TYPES.BUILT_IN,
      disabled: true,
      required: true
    }),
    myForm.getDropdownField("Review Decision", "metadata/list-extension-decisions", {
      name: "extensionDecisionId",
      disabled: true
    }),
    myForm.getInputField("Amendment #", { name: "extensionNumber", disabled: true }),
    myForm.getNumberField("Amendment Days", { name: "extensionDays", required: true }),
  ]);
  myForm.addGroupFields([
    myForm.getInputField("Rate Change %", { fieldType: "percent", name: "extensionRateChangePercentage", decimalsLimit: 2, disabled: true, allowNegativeValue: true }),
    myForm.getInputField("Revised Rate", { fieldType: "percent", name: "newInterestRate", decimalsLimit: 2 }),
    myForm.getInputField("Original Interest Rate", { fieldType: "percent", name: "interestRateOriginal", disabled: true }),
    myForm.getInputField("Current Interest Rate", { fieldType: "percent", name: "interestRateCurrent", disabled: true }),
  ]);
  myForm.addGroupFields([
    myForm.getLabelField("Fee Based Off", { position: "top-right" }),
    myForm.getGroupRadioField("Original Committed Loan Amount"),
    myForm.getGroupRadioField("Current Balance", { required: true }),
    myForm.getBlankField()
  ], { radioGroupName: "feeBasedOffOption", radioGroupValue: data?.feeBasedOffOption || "Original Committed Loan Amount" });
  myForm.addGroupFields([
    myForm.getInputField("Fee %", { fieldType: "percent", name: "extensionFeePercentage", required: true }),
    myForm.getCurrencyField("Fee Amount", { name: "extensionFeeAmount", disabled: true }),
    myForm.getCheckboxField("Amendment Signature Received", { name: "extensionSignatureReceived" }),
    myForm.getBlankField(),
  ]);
  myForm.addGroupFields([
    myForm.getLabelField("Payment Method", { position: "top-right" }),
    myForm.getGroupRadioField("Borrower Pays"),
    myForm.getGroupRadioField("Paid at Payoff", { required: true }),
    myForm.getBlankField()
  ], { radioGroupName: "paymentMethodOption", radioGroupValue: data?.paymentMethodOption || "Paid at Payoff" });
  myForm.addGroupFields([
    myForm.getComponent(<AmendmentCommentsTable/>, { width: 8 }),
    myForm.getButtonField("View Comments", undefined, {
      alignLeft: true,
      modalSize: "fullscreen",
      modalContent: () => (
        <LmsTable
          apiSuffix={`loans/loan-comments/${myForm?.values?.loanId}`}
          onClickModalContent={(setIsModalOpen, { clickedRowData }) => <LoanCommentsModal commentId={clickedRowData?.id} setIsLoanCommentModalOpen={setIsModalOpen} />}
          columnLabelsDictionary={{
            loanNumber: "Loan #",
            description: "Comments",
            name: "Project Name",
            applicationNumber: "Application #"
          }}
        />)
    }),
    myForm.getBlankField()
  ], { widths: 4 });
  return myForm;
};

export const AddNewBuiltInAmendmentForm = ({ termsOptions = {} as any, handleAddAmendmentClose, loanData }) => {
  const { loanId, loanAmountOriginal } = loanData;
  const [{ data = {}, isLoading, error, fetchData }] = useFetchData(`loandetails/terms/loan-extension-initial-details/${loanId}`);
  const extensionStatusId = data?.extensionStatusId || "";
  const extensionDecisionId = data?.extensionDecisionId || "";
  const onAddNewBuiltInAmendmentSuccess = (responseData) => {
    if (termsOptions) {
      termsOptions?.setInputs({ ...termsOptions?.inputs, lastAddedAmendmentId: responseData?.id || guid() });
    }
  };

  const myForm = addAmendmentsFields(new BCFormController(data, {
    values: { extensionStatusId, extensionDecisionId, loanId },
    submitSuffix: "loandetails/terms/built-in-amendment",
    submitMethod: "POST",
    onSubmitSuccess: onAddNewBuiltInAmendmentSuccess
  }), data);

  const onFormChanged = async ({ inputs, setInputs }, lastUpdated) => {
    if (lastUpdated === "feeBasedOffOption") {
      const extensionFeeAmount = inputs?.extensionFeeAmount;
      const data = {
        amendedLoanAmount: extensionFeeAmount,
        extensionFeePercentage: inputs.extensionFeePercentage,
        loanAmountOriginal: loanAmountOriginal,
        feeBasedOff: inputs.feeBasedOffOption,
        loanId
      };

      const { data: calculatedData } = await fetchData("amendments/calculate-loan-extension-fee", { fetchMethod: "POST", data, skipCache: true });

      if (calculatedData?.calculatedFee !== undefined && extensionFeeAmount !== calculatedData?.calculatedFee) {
        setInputs({ ...inputs, extensionFeeAmount: calculatedData?.calculatedFee });
      }
    }
  };

  const onBlur = async (fieldName, fieldValue, { inputs, setInputs }) => {
    const extensionFeeAmount = inputs.extensionFeeAmount;
    const feeBasedOff = inputs.feeBasedOffOption;
    if (fieldName === "extensionFeePercentage") {
      const extensionFeePercentage = parsePercentValue(fieldValue);
      const data = {
        amendedLoanAmount: extensionFeeAmount,
        extensionFeePercentage,
        loanAmountOriginal: loanAmountOriginal,
        feeBasedOff: feeBasedOff,
        loanId
      };

      const { data: calculatedData } = await fetchData("amendments/calculate-loan-extension-fee", { fetchMethod: "POST", data, skipCache: true });

      if (calculatedData?.calculatedFee !== undefined && extensionFeeAmount !== calculatedData?.calculatedFee) {
        setInputs({ ...inputs, extensionFeeAmount: calculatedData?.calculatedFee, extensionFeePercentage });
      }
    }

    if (fieldName === "newInterestRate") {
      if (fieldValue) {
        const newInterestRate = parsePercentValue(fieldValue);
        const rateChangePercentage = newInterestRate - (inputs.interestRateOriginal || 0);
        setInputs({ ...inputs, extensionRateChangePercentage: rateChangePercentage, newInterestRate: newInterestRate?.toFixed(2) });
      }
      else {
        setInputs({ ...inputs, extensionRateChangePercentage: null });
      }
    }
  };

  return (
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      error={error}
      actions={["save-close", "close"]}
      handleClose={handleAddAmendmentClose}
      onBlur={onBlur}
      onFormChanged={onFormChanged}
      displayMessageLocally
    />
  );
};

const LmsLoanDetailsStatusHistoryPage = ({ loanIdFromDetails }) => {
  const [{ data = {}, isLoading, error }, reFetchStatusHistory] = useFetchData(loanIdFromDetails ? `loandetails/list-status-history/${loanIdFromDetails}` : "");
  return <React.Fragment>
    <LmsTable
      data={data}
      isLoading={isLoading}
      error={error}
      columnLabelsDictionary={{ description: "Comment" }}
      hideProps={["updatedOn", "sortOrder"]}
      isDisabled
      dataRules={[{ field: "date", rule: "formatDate", display: "datefullformatted" }]}
    />
    <EditStatusHistoryForm loanId={loanIdFromDetails} reFetchStatusHistory={reFetchStatusHistory}/>
  </React.Fragment>;
};

const BuiltInAmendmentsTable = ({ loanId, options }) => {
  return <LmsTable
    apiSuffix={loanId ? `loandetails/terms/loan-extension-details/${loanId}` : ""}
    key={`loandetails/terms/loan-extension-details/${loanId}`}
    displayOnlyProps={["extensionDays", "extensionRateChangePercentage", "extensionFeePercentage", "extensionFeeAmount", "paymentMethodOption"]}
    columnLabelsDictionary={{
      extensionDays: "Days",
      extensionRateChangePercentage: "Rate Change %",
      extensionFeePercentage: "Fee %",
      extensionFeeAmount: "Fee $",
      paymentMethodOption: "Payment Method"
    }}
    rowTitle="Built in Amendment"
    refreshDataDependency={options?.inputs?.lastAddedAmendmentId}
    compact
    isDisabled
  />;
};

const CreateMiniLoansModal = ({ setIsModalOpen, loanDetails, options }) => {
  const loanId = loanDetails?.loanId;
  const onSubmitSuccess = (responseData) => {
    if (responseData) {
      const parsedPath = appendIdToPath({ path: `loanapplications/addminiloan/${responseData?.miniLoanTempId}` });
      options?.setModalIframeSrcPath?.(`${window.location.origin}${parsedPath}`);
      options?.setModalIframeSize?.("fullscreen");
      setIsModalOpen(false);
    }
  };

  const myForm = new BCFormController({ loanNumberDisplay: loanDetails?.loanNumberDisplay }, {
    submitSuffix: "loandetails/mini-loan-temp",
    values: { loanId },
    styles: { minHeight: "450px" },
    onSubmitSuccess,
    blankWidth: 3
  });

  myForm.addCenteredGroup([myForm.getDropdownDisplayField("Loan", { name: "loanNumberDisplay" })]);
  myForm.addCenteredGroup([
    myForm.getLabelField("Revolver Type Option", { width: "2", wrapLabel: true }),
    myForm.getGroupRadioField("Dev", { value: "Dev" }),
    myForm.getGroupRadioField("Lot", { value: "Lot" }),
    myForm.getGroupRadioField("Vertical", { value: "Vertical" }),
  ], { radioGroupName: "revolverTypeOption", radioGroupValue: "Dev" });
  myForm.addCenteredGroup([
    myForm.getLabelField("Parent Option", { width: "2", wrapLabel: true }),
    myForm.getGroupRadioField("Revolver", { value: "Revolver" }),
    myForm.getGroupRadioField("Dev", { value: "Dev",  isHidden: { revolverTypeOption: "Dev" } }),
    myForm.getGroupRadioField("Lot", { value: "Lot", isHidden: { revolverTypeOption: "Vertical" }, isHiddenOperation: "notequal" }),
  ], { radioGroupName: "parentOption", radioGroupValue: "Revolver" });
  myForm.addCenteredGroup([
    myForm.getDropdownField("Parent Loan", `loandetails/list-mini-loans/${loanId}`, {
      required: true,
      dependencyField: "parentOption",
      dependencyFieldQuery: "parentOption"
    }),
  ], { isHidden: { parentOption: "Revolver" } });
  myForm.addCenteredGroup([
    myForm.getLabelField("New Or Copy", { width: "2", wrapLabel: true }),
    myForm.getGroupRadioField("New", { value: "New" }),
    myForm.getGroupRadioField("Copy Existing", { value: "Copy Existing" }),
  ], { radioGroupName: "newOrCopy", radioGroupValue: "New" });
  myForm.addCenteredGroup([
    myForm.getDropdownField("Copy From Loan", `loandetails/list-mini-loan/${loanId}`, { required: true }),
  ], { isHidden: { newOrCopy: "New" } });

  const formActions = [
    { text: "Next", action: "save" },
    "close"
  ];

  return <LmsFormGenerator myForm={myForm} actions={formActions} handleClose={() => setIsModalOpen(false)} displayMessageLocally />;
};

const SelectPropertiesForm = ({ options }) => {
  const [{ data, isLoading }] = useFetchData(options?.inputs?.miniLoanId ? `loandetails/list-loan-temp-properties/${options?.inputs?.miniLoanId}` : "");
  const myForm = new BCFormController({}, {
    isCompact: true,
    centeredTitle: "Select Properties",
  });

  if (data) {
    data.map((property) => {
      myForm.addCenteredGroup([
        myForm.getCheckboxField(property.name, {
          name: property.id,
          inlineRight: true,
          position: "center",
        })
      ]);
    });
  }

  const onFormChanged = ({ inputs }, lastUpdated) => {
    const loanPropertyIds = options?.inputs?.loanPropertyIds || [];
    let updatedProperties = [] as any[];
    if (inputs[lastUpdated] && !loanPropertyIds.includes(lastUpdated)) {
      updatedProperties.push(lastUpdated);
    }
    else if (!inputs[lastUpdated] && !loanPropertyIds.includes(lastUpdated)) {
      updatedProperties = loanPropertyIds?.filter((option) => option !== lastUpdated);
    }

    options.updateInput("loanPropertyIds", updatedProperties);
  };

  const selectAllProperties = (_, handleSubmit, useFormInstance) => {
    const loanPropertyIds = options?.inputs?.loanPropertyIds || [];
    const allPropertiesInputs = {} as any;
    const allProperties = data?.map((property) => {
      allPropertiesInputs[property.id] = true;
      return property.id;
    });
    const updatedProperties = loanPropertyIds?.length === allProperties?.length ? [] : allProperties;
    options.updateInput("loanPropertyIds", updatedProperties);
    useFormInstance?.setInputs(allPropertiesInputs);
  };

  const clearAllProperties = (_, handleSubmit, useFormInstance) => {
    options.updateInput("loanPropertyIds", []);
    useFormInstance?.setInputs({});
  };

  const formActions = [{ text: "Select All", handleClick: selectAllProperties }, { text: "Clear All", handleClick: clearAllProperties }];

  return (
    <LmsFormGenerator
      myForm={myForm}
      actions={formActions}
      onFormChanged={onFormChanged}
      renderAs="div"
      isLoading={isLoading}
    />
  );
};

const SelectUnitsForm = ({ options }) => {
  const [{ data, isLoading }] = useFetchData("loandetails/list-loan-temp-propertyUnits", { method: "POST", payloadData: options?.inputs?.loanPropertyIds });
  const myForm = new BCFormController({}, {
    isCompact: true,
    centeredTitle: "Select Units",
  });

  if (data) {
    data.map((unit) => {
      myForm.addCenteredGroup([
        myForm.getCheckboxField(unit.name, {
          name: unit.id,
          inlineRight: true,
          position: "center",
        })
      ]);
    });
  }

  const onFormChanged = ({ inputs }, lastUpdated) => {
    const loanPropertyUnitReconveyanceIds = options?.inputs?.loanPropertyUnitReconveyanceIds || [];
    let updatedUnits = [] as any[];
    if (inputs[lastUpdated] && !loanPropertyUnitReconveyanceIds.includes(lastUpdated)) {
      updatedUnits.push(lastUpdated);
    }
    else if (!inputs[lastUpdated] && loanPropertyUnitReconveyanceIds.includes(lastUpdated)) {
      updatedUnits = loanPropertyUnitReconveyanceIds?.filter((option) => option !== lastUpdated);
    }

    options.updateInput("loanPropertyUnitReconveyanceIds", updatedUnits);
  };

  const selectAllUnits = (_, handleSubmit, useFormInstance) => {
    const loanPropertyUnitReconveyanceIds = options?.inputs?.loanPropertyUnitReconveyanceIds || [];
    const allPropertiesInputs = {} as any;
    const allProperties = data?.map((unit) => {
      allPropertiesInputs[unit.id] = true;
      return unit.id;
    });

    const updatedProperties = loanPropertyUnitReconveyanceIds?.length === allProperties?.length ? [] : allProperties;
    options.updateInput("loanPropertyUnitReconveyanceIds", updatedProperties);
    useFormInstance?.setInputs(allPropertiesInputs);
  };

  const clearAllUnits = (_, handleSubmit, useFormInstance) => {
    options.updateInput("loanPropertyUnitReconveyanceIds", []);
    useFormInstance?.setInputs({});
  };

  const formActions = [{ text: "Select All", handleClick: selectAllUnits }, { text: "Clear All", handleClick: clearAllUnits }];

  return (
    <LmsFormGenerator
      myForm={myForm}
      actions={formActions}
      onFormChanged={onFormChanged}
      renderAs="div"
      isLoading={isLoading}
    />
  );
};

const PayoffAndConvertModal = ({ setIsModalOpen, loanDetails }) => {
  const loanId = loanDetails?.loanId;

  const myForm = new BCFormController({ loanNumberDisplay: loanDetails?.loanNumberDisplay }, {
    values: {
      masterLoanId: loanId,
      conversionTypeEnum: LmsConversionTypeOptions[0]?.value?.replaceAll(" ", "")
    },
    styles: { minHeight: "450px" },
    excludeFromSubmit: ["conversionTypeEnum"],
    blankWidth: 3
  });

  myForm.addCenteredGroup([
    myForm.getDropdownField("Conversion Type", LmsConversionTypeOptions, {
      name: "conversionType",
      value: LmsConversionTypeOptions[0].value,
      skipClearable: true,
      isDisabledOnFunc: ({ metaData }) => !!metaData?.miniLoanIdSubmitted,
    })
  ]);
  myForm.addCenteredGroup([
    myForm.getDropdownField("Mini Loan", `loandetails/list-conversion-type-mini-loans/${loanId}`, {
      skipClearable: true,
      dependencyField: "conversionTypeEnum",
      dependencyFieldQuery: "conversionType",
      isDisabledOnFunc: ({ metaData }) => !!metaData?.miniLoanIdSubmitted,
    })
  ]);
  myForm.addCenteredGroup([
    myForm.getComponent((options) => (
      <SelectPropertiesForm options={options} key="select-properties-component" />
    ))
  ], {
    isHiddenFunc: ({ metaData }) => !metaData?.miniLoanIdSubmitted || metaData?.propertiesSubmitted,
  });
  myForm.addCenteredGroup([
    myForm.getComponent((options) => (
      <SelectUnitsForm options={options} key="select-units-component" />
    ))
  ], {
    isHiddenFunc: ({ metaData }) => !metaData?.miniLoanIdSubmitted || !metaData?.propertiesSubmitted
  });

  const handleClick = async (inputs, handleSubmit, options) => {
    const { metaData, setMetaData } = options;
    if (!metaData?.miniLoanIdSubmitted) {
      setMetaData({ miniLoanIdSubmitted: true });
    }
    else {
      if (!metaData?.propertiesSubmitted) {
        if (inputs?.loanPropertyIds?.length > 0) {
          options.setMetaData({ ...options.metaData, propertiesSubmitted: true });
        }
        else {
          alert("Please select at least one property"); // TODO: Replace with from alert
        }
      }
      else {
        if (inputs?.loanPropertyUnitReconveyanceIds?.length > 0) {
          const onSuccessFunc = (responseData) => {
            if (responseData?.loanTempId) {
              options.history.push(`/loanapplications/loantempproperty/${responseData?.loanTempId}`);
            }
          };

          await handleSubmit("loandetails/loan-temp", { method: "POST", data: inputs, onSuccessFunc });
        }
        else {
          alert("Please select at least one unit"); // TODO: Replace with from alert
        }
      }
    }
  };

  const formActions = [
    { text: "Next", handleClick: handleClick },
    { text: "Refresh", icon: GlobalConstants.Icons.CARET_UP, action: "refresh" },
    "close"
  ];

  const onFormChanged = ({ inputs, updateInput }, lastUpdated) => {
    if (lastUpdated === "conversionType") {
      updateInput("conversionTypeEnum", inputs?.[lastUpdated]?.replaceAll(" ", ""));
    }
  };

  return (
    <LmsFormGenerator
      myForm={myForm}
      actions={formActions}
      handleClose={() => setIsModalOpen(false)}
      onFormChanged={onFormChanged}
      displayMessageLocally
    />
  );
};

const LmsLoanRevolverMasterLoanTerms = ({ id, loanDetails }) => {
  const [{ data, isLoading, error }] = useFetchData(`loandetails/terms-master/${id}`);
  const myForm = new BCFormController(data, {
    initIsDisabled: true,
    values: { loanId: data?.loanId, loanProgramId: data?.loanProgramId },
    submitSuffix: "loandetails/terms-master"
  });

  myForm.addGroupFields([
    myForm.getDropdownDisplayField("Loan Program", { name: "loanProgramName" }),
    myForm.getInputField("Loan Term", { required: true, fieldType: "number" }),
    myForm.getCurrencyField("Master Loan Amount", { name: "loanAmountOriginal" }),
    myForm.getInputField("Renewal Term", { fieldType: "number" }),
  ], { widths: "4" });
  myForm.addGroupFields([
    myForm.getCurrencyField("Original Interest Reserve", {
      name: "interestReserveOriginal",
      defaultValue: "0.00"
    }),
    myForm.getInputField("Original Interest Rate", { fieldType: "float", name: "interestRateOriginal" }),
    myForm.getDropdownField("Interest Calculated On", "metadata/list-interest-calculation-methods", { name: "interestCalculationMethodId" }),
    myForm.getBlankField()
  ]);
  myForm.addGroupFields([
    myForm.getInputField("Cumulative Project Experience", { required: true, fieldType: "number" }),
    myForm.getInputField("Current Interest Rate", { required: true, fieldType: "number" , name: "interestRateCurrent", disabled: true }),
    myForm.getDropdownField("Rate Type", LmsRateTypeOptions, { name: "rateTypeOption" }),
    myForm.getBlankField()
  ]);

  myForm.addDividerField();

  myForm.addGroupFields([
    myForm.getBlankField({ width: "3" }),
    myForm.getLabelField("Development", { width: "3", position: "center" }),
    myForm.getLabelField("Lot", { width: "3", position: "center" }),
    myForm.getLabelField("Vertical", { width: "3", position: "center" }),
    myForm.getLabelField("Master", { width: "3", position: "center" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "1" });
  myForm.addGroupFields([
    myForm.getLabelField("Recommended LTV", { width: "3", position: "right-center" }),
    myForm.getInputField("", { name: "approvedDevLtv", width: "3", fieldType: "float" }),
    myForm.getInputField("", { name: "approvedLotLtv", width: "3", fieldType: "float" }),
    myForm.getInputField("", { name: "approvedVerticalLtv", width: "3", fieldType: "float" }),
    myForm.getInputField("", { name: "approvedRevolverLtv", width: "3", fieldType: "float" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "1" });
  myForm.addGroupFields([
    myForm.getLabelField("Recommended LTC", { width: "3", position: "right-center" }),
    myForm.getInputField("", { name: "approvedDevLtc", width: "3", fieldType: "float" }),
    myForm.getInputField("", { name: "approvedLotLtc", width: "3", fieldType: "float" }),
    myForm.getInputField("", { name: "approvedVerticalLtc", width: "3", fieldType: "float" }),
    myForm.getInputField("", { name: "approvedRevolverLtc", width: "3", fieldType: "float" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "1" });
  myForm.addGroupFields([
    myForm.getLabelField("% of Total Line", { width: "3", position: "right-center" }),
    myForm.getInputField("", { name: "devLoanPercent", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getInputField("", { name: "lotLoanPercent", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getInputField("", { name: "verticalLoanPercent", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getBlankField({ width: "3" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "1" });
  myForm.addGroupFields([
    myForm.getLabelField("Total Committed Amount", { width: "3", position: "right-center" }),
    myForm.getCurrencyField("", { name: "devLoanAmount", width: "3", disabled: true }),
    myForm.getCurrencyField("", { name: "lotLoanAmount", width: "3", disabled: true }),
    myForm.getCurrencyField("", { name: "verticalLoanAmount", width: "3", disabled: true }),
    myForm.getBlankField({ width: "3" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "1" });
  myForm.addGroupFields([
    myForm.getLabelField("Primary Funded Balance", { width: "3", position: "right-center" }),
    myForm.getInputField("", { name: "devDrawnPercent", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getInputField("", { name: "lotDrawnPercent", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getInputField("", { name: "verticalDrawnPercent", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getBlankField({ width: "3" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "1" });
  myForm.addGroupFields([
    myForm.getLabelField("Primary Capacity", { width: "3", position: "right-center" }),
    myForm.getCurrencyField("", { name: "devDrawnBalanceLimit", width: "3", disabled: true }),
    myForm.getCurrencyField("", { name: "lotDrawnBalanceLimit", width: "3", disabled: true }),
    myForm.getCurrencyField("", { name: "verticalDrawnBalanceLimit", width: "3", disabled: true }),
    myForm.getBlankField({ width: "3" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "1" });
  myForm.addGroupFields([
    myForm.getLabelField("Temporary Reserve Capacity", { width: "3", position: "right-center", wrapLabel: true }),
    myForm.getCurrencyField("", { name: "devReserveCapacity", width: "3", disabled: true }),
    myForm.getCurrencyField("", { name: "lotReserveCapacity", width: "3", disabled: true }),
    myForm.getCurrencyField("", { name: "verReserveCapacity", width: "3", disabled: true }),
    myForm.getBlankField({ width: "3" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "1" });
  myForm.addGroupFields([
    myForm.getLabelField("Interest Reserve", { width: "3", position: "right-center" }),
    myForm.getCurrencyField("", { name: "devInterestReserve", width: "3", disabled: true }),
    myForm.getCurrencyField("", { name: "lotInterestReserve", width: "3", disabled: true }),
    myForm.getCurrencyField("", { name: "verticalInterestReserve", width: "3", disabled: true }),
    myForm.getCurrencyField("", { name: "revInterestReserve", width: "3", disabled: true }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "1" });

  myForm.addDividerField();

  myForm.addGroupFields([
    myForm.getLabelField("Limit Above Commitment Amount (%)", { width: "3", position: "right-center", wrapLabel: true }),
    myForm.getInputField("", { name: "devLimitPercentAboveCommitmentAmount", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getInputField("", { name: "lotLimitPercentAboveCommitmentAmount", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getInputField("", { name: "verticalLimitPercentAboveCommitmentAmount", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getBlankField({ width: "3" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "1" });
  myForm.addGroupFields([
    myForm.getLabelField("Fee Charge (%)", { width: "3", position: "right-center" }),
    myForm.getInputField("", { name: "devPenalInterestPercent", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getInputField("", { name: "lotPenalInterestPercent", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getInputField("", { name: "verticalPenalInterestPercent", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getBlankField({ width: "3" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "1" });
  myForm.addGroupFields([
    myForm.getLabelField("Valid Days", { width: "3", position: "right-center" }),
    myForm.getInputField("", { name: "devValidDays", width: "3", fieldType: "number" }),
    myForm.getInputField("", { name: "lotValidDays", width: "3", fieldType: "number" }),
    myForm.getInputField("", { name: "verticalValidDays", width: "3", fieldType: "number" }),
    myForm.getBlankField({ width: "3" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "1" });
  myForm.addGroupFields([
    myForm.getLabelField("Increase In Interest Rate (%)", { width: "3", position: "right-center", wrapLabel: true }),
    myForm.getInputField("", { name: "devPercentIncreaseInInterest", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getInputField("", { name: "lotPercentIncreaseInInterest", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getInputField("", { name: "verticalPercentIncreaseInInterest", width: "3", fieldType: "percent", maxPercentage: 100 }),
    myForm.getBlankField({ width: "3" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "1" });

  const calculateDevLotVerValues = (masAmount, percent) => {
    return [
      masAmount * (percent / 100),
      0, // Find out the calc logic
      0 // Find out the calc logic
    ];
  };

  const handleBlur = (fieldName, value, options) => {
    if (fieldName === "loanAmountOriginal") {
      const [devLoanAmount, devDrawnBalanceLimit, devReserveCapacity] = calculateDevLotVerValues(value, data.devLoanPercent);
      const [lotLoanAmount, lotDrawnBalanceLimit, lotReserveCapacity] = calculateDevLotVerValues(value, data.lotLoanPercent);
      const [verLoanAmount, verDrawnBalanceLimit, verReserveCapacity] = calculateDevLotVerValues(value, data.verticalLoanPercent);
      options.setInputs({
        ...options.inputs,
        devLoanAmount,
        devDrawnBalanceLimit,
        devReserveCapacity,
        lotLoanAmount,
        lotDrawnBalanceLimit,
        lotReserveCapacity,
        verLoanAmount,
        verDrawnBalanceLimit,
        verReserveCapacity
      });
    }
  };

  const topActions = [
    "edit",
    {
      text: "Create Mini Loans",
      icon: GlobalConstants.Icons.CARET_UP,
      modalContent: (setIsModalOpen, options) => <CreateMiniLoansModal setIsModalOpen={setIsModalOpen} loanDetails={loanDetails} options={options} />
    },
    {
      text: "Payoff and Convert",
      icon: GlobalConstants.Icons.CARET_UP,
      modalContent: (setIsModalOpen) => <PayoffAndConvertModal setIsModalOpen={setIsModalOpen} loanDetails={loanDetails} />
    }
  ] as IFormGeneratorCustomAction[];

  return (
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      error={error}
      actions={["save", "save-close", "close"]}
      onBlur={handleBlur}
      topActions={topActions}
      hideBottomActionsOnEdit
    />
  );
};

export const addMiniLoanTableFields = (myForm) => {
  myForm.addDividerField({ dividerIsHidden: true });

  myForm.addCenteredGroup([
    myForm.getBlankField({ width: "2" }),
    myForm.getGroupRadioField("Mini Loans in Servicing", { width: "2" }),
    myForm.getGroupRadioField("Mini Loans in Pipeline", { width: "2" }),
    myForm.getBlankField({ width: "1" })
  ], { radioGroupName: "submittedToLrcminiLoansOption" });
  myForm.addCenteredGroup([
    myForm.getBlankField({ width: "2" }),
    myForm.getLabelField("Current", { width: "2", position: "top-center" }),
    myForm.getLabelField("After Mini Approval", { width: "2", position: "top-center" }),
  ],{ groupClassName: "mini-loans-form-table-row", isCompact: true });
  myForm.addCenteredGroup([
    myForm.getLabelField("Remaining Dev Capacity", { width: "2", position: "top-right", wrapLabel: true }),
    myForm.getCurrencyField("", { name: "submittedToLrccurrentBucketCapacity", width: "2", disabled: true, isCompact: true }),
    myForm.getCurrencyField("", {
      name: "submittedToLrcbucketCapacityWithMiniLoansInPipeline",
      width: "2",
      disabled: true,
      isHidden: { submittedToLrcminiLoansOption: "Mini Loans in Servicing" },
      isCompact: true
    }),
    myForm.getCurrencyField("", {
      name: "submittedToLrcbucketCapacityWithThisMiniLoan",
      width: "2",
      disabled: true,
      isHidden: { submittedToLrcminiLoansOption: "Mini Loans in Pipeline" },
      isCompact: true
    }),
  ], { groupClassName: "mini-loans-form-table-row", isCompact: true });
  myForm.addCenteredGroup([
    myForm.getLabelField("Dev Temporary Reserve Capacity", { width: "2", position: "top-right", wrapLabel: true }),
    myForm.getCurrencyField("", { name: "submittedToLrccurrentTrc", width: "2", disabled: true, isCompact: true }),
    myForm.getCurrencyField("", {
      name: "submittedToLrctrcwithMiniLoansInPipeline",
      width: "2",
      disabled: true,
      isHidden: { submittedToLrcminiLoansOption: "Mini Loans in Servicing" },
      isCompact: true
    }),
    myForm.getCurrencyField("", {
      name: "submittedToLrctrcwithThisMiniLoan",
      width: "2",
      disabled: true,
      isHidden: { submittedToLrcminiLoansOption: "Mini Loans in Pipeline" },
      isCompact: true
    }),
  ], { groupClassName: "mini-loans-form-table-row", isCompact: true });
  myForm.addDividerField({ dividerIsHidden: true });

  return myForm;
};

const LmsLoanDetailsTerms = ({ id, loanId = "", loanDetails, reFetchLoanDetails }) => {
  const [addEscrowOfficerIsOpen, setAddEscrowOfficerIsOpen] = useState(false);
  const [{ data = {}, isLoading, error }, refetchTerms] = useFetchData(`loandetails/terms/${id}`);
  const loanStatusDisabledPhases = [
    "Application",
    "Submitted to UW",
    "LRC Voting"
  ];

  const applicationLabel = loanDetails?.loanStatus === "Application" ? "red" : undefined;
  const processingLabel = loanDetails?.loanStatus === "Processing" ? "red" : undefined;
  const disabled = loanDetails?.loanPhase === "Servicing";
  const FLAT_RATE_ID = "7f7f538e-d5a6-49c3-a9fe-074e14f33273";
  const myForm = new BCFormController(data, {
    values: { loanId: id, loanPhase: loanDetails?.loanPhase },
    initIsDisabled: true,
    submitSuffix: "loandetails/terms",
    clearSuffixesOnSubmit: [`loandetails/terms/${id}`],
    exitEditOnClose: true,
    hiddenFields: {
      cumulativeProjectExperience: loanDetails?.isMini,
      includeInterestReserveInMasterLoan: !loanDetails?.isMini,
    },
    onSubmitSuccess: () =>{
      refetchTerms();
      reFetchLoanDetails();
    }
  });

  const addEscrowOfficerActionOptions = {
    actionFunc: () => setAddEscrowOfficerIsOpen(true),
    actionLabel: "Add Escrow Officer"
  };

  const isPhaseOneHidden = { isHidden: { loanProgramId: GlobalConstants.LoanProgramIds.BRIDGE_TO_BUILD }, isHiddenOperation: "notequal" } as any;
  if (loanDetails?.isMini && data) {
    const defaultData = { submittedToLrcminiLoansOption: "Mini Loans in Pipeline" };
    const initialData = !data?.submittedToLrcminiLoansOption ? { ...data, ...defaultData } : data;
    myForm.addComponent(<LmsFormGenerator myForm={addMiniLoanTableFields(new BCFormController(initialData, { noBorder: true }))} skipActions />);
  }

  myForm.addCenteredGroup([
    myForm.getInputField("Source Channel",  { disabled: true }),
  ], { widths: "1", blankWidth: 6 });
  myForm.addGroupFields([
    myForm.getDropdownField("Loan Program", "metadata/list-loan-programs?includeRevolver=false", { labelColor: applicationLabel, disabled }),
    myForm.getDropdownField("Loan Sub Type", "metadata/list-loan-sub-types", {
      disabled: loanStatusDisabledPhases.includes(loanDetails?.loanStatus) || disabled,
      required: true
    }),
    myForm.getCurrencyField("Original Loan Amount", { name: "loanAmountOriginal", labelColor: applicationLabel, disabled }),
    myForm.getNumberField("Loan Term", { required: true, disabled }),
    myForm.getInputField("Promotion Pricing", { disabled }),
  ], { widths: "4" });
  myForm.addGroupFields([
    myForm.getDropdownField("Interest Calculated On", "metadata/list-interest-calculation-methods", {
      name: "interestCalculationMethodId",
      labelColor: applicationLabel,
      disabled
    }),
    myForm.getInputField("Original Interest Rate", { fieldType: "percent", name: "interestRateOriginal", labelColor: applicationLabel, disabled }),
    myForm.getCurrencyField("Original Int Reserve Amount", {
      name: "interestReserveOriginal",
      isDisabledOn: { useInterestReserve: true },
      isDisabledOnOperation: "notequal",
      labelColor: applicationLabel,
      defaultValue: "0.00",
      disabled
    }),
    myForm.getInputField("Phase 1 Loan Term", { name: "phase1Term", fieldType: "number", required: true, labelColor: applicationLabel, ...isPhaseOneHidden, disabled, keepHiddenPosition: true }),
    myForm.getCurrencyField("Phase1 Amount", { name: "phase1Amount", disabled: true, ...isPhaseOneHidden, keepHiddenPosition: true }),
  ], { widths: "4" });

  if (loanDetails?.isMini) {
    myForm.addGroupFields([
      myForm.getCheckboxField("Include Interest Reserve In Master Loan"),
    ], { widths: "4" });
  }

  myForm.addGroupFields([
    myForm.getCheckboxField("Use Interest Reserve", { width: 2, disabled }),
    myForm.getCheckboxField("Direct Pay", { width: 2, disabled }),
    myForm.getCheckboxField("Forced Draw", { width: 2, disabled }),
    myForm.getCheckboxField("ACH", { name: "ach", width: 2, isDisabledOn: { directPay: true }, isDisabledOnOperation: "notequal", disabled }),
    myForm.getCheckboxField("Escrow Pay", { width: 2 }),
    myForm.getActionDropdownField("Escrow Officer", "loandetails/terms/list-org-escrow-officers", addEscrowOfficerActionOptions, {
      name: "escrowOfficerContactId",
      isHidden: { escrowPay: true },
      isHiddenOperation: "notequal",
      isActionHiddenOnDisabled: true,
      width: 6,
      disabled: !!data?.escrowOfficerContactId
    }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Portfolio Wide Sweep", { position: "right-center", width: 4, disabled }),
    myForm.getCheckboxField("", { name: "sweep", disabled }),
    myForm.getDropdownField("", "metadata/list-sweep-options", {
      name: "sweepOptionId",
      isDisabledOn: { sweep: true },
      isDisabledOnOperation: "notequal",
      width: 8,
      disabled
    }),
    myForm.getBlankField({ width: 4 }),
  ], { widths: 1 });
  myForm.addGroupFields([
    myForm.getInputField("Sweep Percentage", { fieldType: "percent", isDisabledOn: { sweep: true }, isDisabledOnOperation: "notequal", width: 4, disabled }),
    myForm.getTextareaField("Sweep Conditions", {
      isDisabledOn: { sweep: true },
      isDisabledOnOperation: "notequal",
      required: { sweep: true },
      labelColor: processingLabel,
      width: 4,
      disabled
    }),
    myForm.getCurrencyField("Sweep Amount", { isDisabledOn: { sweep: true }, isDisabledOnOperation: "notequal", width: 4, labelColor: processingLabel, disabled }),
  ], { widths: "1" });
  myForm.addGroupFields([
    myForm.getCheckboxField("Additional Collateral", { name: "additionalCollateralRequired" , disabled: true }),
    myForm.getCurrencyField("Collateral Amount", { name: "additionalCollateralAmount", isDisabledOn: { additionalCollateralRequired: true }, isDisabledOnOperation: "notequal", disabled }),
    myForm.getTextareaField("Collateral Release Description", {
      name: "additionalCollateralDescription",
      isDisabledOn: { additionalCollateralRequired: true },
      isDisabledOnOperation: "notequal",
      disabled
    }),
    myForm.getInputField("Additional Collateral DOT Number", {
      name: "additionalCollateralDotnumber",
      isDisabledOn: { additionalCollateralRequired: true },
      isDisabledOnOperation: "notequal",
      disabled
    }),
  ], { widths: "4" });
  myForm.addGroupFields([
    myForm.getLabelField("Partial Deed Release", { position: "right-center", width: 4, disabled }),
    myForm.getCheckboxField("", { name: "partialDeedReleaseApplicable", disabled }),
    myForm.getDropdownField("", "metadata/list-partial-deed-release-options", {
      name: "partialDeedReleaseOptionId",
      isDisabledOn: { partialDeedReleaseApplicable: true },
      isDisabledOnOperation: "notequal",
      width: 8,
      disabled
    }),
    myForm.getBlankField({ width: 4 }),
  ], { widths: 1 });
  myForm.addGroupFields([
    myForm.getCheckboxField("Override Partial Deed Release Amount", {
      isDisabledOn: { partialDeedReleaseApplicable: true },
      isDisabledOnOperation: "notequal",
      labelColor: processingLabel,
      disabled
    }),
    myForm.getCurrencyField("Partial Deed Release Amount", { isDisabledOn: { overridePartialDeedReleaseAmount: true }, isDisabledOnOperation: "notequal", disabled }),
    myForm.getTextareaField("Partial Deed Release Description", { isDisabledOn: { partialDeedReleaseApplicable: true }, isDisabledOnOperation: "notequal", disabled }),
    myForm.getInputField("Partial Deed Release Sweep Percentage", {
      fieldType: "percent",
      isDisabledOn: { partialDeedReleaseOptionId: FLAT_RATE_ID },
      labelColor: processingLabel
    }),
  ], { widths: "4" });
  myForm.addGroupFields([
    myForm.getCheckboxField("Exit Fee Applicable", { disabled }),
    myForm.getInputField("Exit Fee Percentage", {
      fieldType: "percent",
      isDisabledOn: { exitFeeApplicable: true },
      isDisabledOnOperation: "notequal",
      required: { exitFeeApplicable: true },
      maxPercentage: 100,
      disabled
    }),
  ], { widths: "4" });
  myForm.addGroupFields([
    myForm.getCheckboxField("Right Of First Refusal", { disabled }),
    myForm.getCurrencyField("Right Of First Refusal Value", {
      isDisabledOn: { rightOfFirstRefusal: true },
      isDisabledOnOperation: "notequal",
      required: { rightOfFirstRefusal: true },
      disabled
    }),
    myForm.getTextareaField("Right Of First Refusal Description", {
      isDisabledOn: { rightOfFirstRefusal: true },
      isDisabledOnOperation: "notequal",
      required: { rightOfFirstRefusal: true },
      disabled
    }),
    myForm.getInputField("Right Of First Refusal Percentage", {
      fieldType: "percent",
      isDisabledOn: { rightOfFirstRefusal: true },
      isDisabledOnOperation: "notequal",
      required: { rightOfFirstRefusal: true },
      maxPercentage: 100,
      disabled
    }),
  ], { widths: "4" });
  myForm.addGroupFields([
    myForm.getInputField("Datedown Order Frequency", { disabled }),
    myForm.getNumberField("Cumulative Project Experience", { disabled }),
    myForm.getInputField("Broker Fee Rate", { fieldType: "percent", disabled: true }),
    myForm.getInputField("Max Combined Broker Fee", { name: "maxCombinedBrokerFee", fieldType: "float", maxValue: 99.99, disabled }),
  ], { widths: "4" });
  const isHiddenRateFields = { isHidden: { rateTypeOption: "Fixed Term Rate" } } as any;
  myForm.addGroupFields([
    myForm.getDropdownField("Rate Type", LmsRateTypeOptions, { 
      name: "rateTypeOption", 
      required: true, 
      defaultValue: "Fixed Term Rate",
      isDisabledOn: { loanPhase: "Production" }, 
      isDisabledOnOperation: "notequal"
    }),
    myForm.getDropdownField("Variable Index", "metadata/list-variable-rate-index", { 
      name: "variableRateIndexId", 
      required: true, 
      isDisabledOn: { loanPhase: "Production" }, 
      isDisabledOnOperation: "notequal",
      ...isHiddenRateFields
    }),
    myForm.getDisplayField("Variable Index Rate", { 
      name: "baseRateDisplay", 
      isHiddenFunc: ({ inputs, isDisabled }) => !data || !isDisabled || inputs?.rateTypeOption === "Fixed Term Rate"
    }),
    myForm.getNumberField("Fixed Rate Term In Months", { 
      isHidden: { rateTypeOption: "Fixed + Adj Rate" }, 
      isHiddenOperation: "notequal", 
      required: true,
      isDisabledOn: { loanPhase: "Production" }, 
      isDisabledOnOperation: "notequal",
    })
  ], { widths: "4" });
  myForm.addGroupFields([
    myForm.getNumberField("No Of Adjustments", { 
      maxValue: 99999, 
      isDisabledOn: { loanPhase: "Production" }, 
      isDisabledOnOperation: "notequal",
      ...isHiddenRateFields 
    }),
  ], { widths: "4" });
  myForm.addGroupFields([
    myForm.getPercentField("Margin", 9999.999, { name: "spreadRate", required: true, isDisabledOn: { loanPhase: "Production" }, isDisabledOnOperation: "notequal" }),
    myForm.getPercentField("Floor", 9999.999, { name: "rateFloor", required: true, isDisabledOn: { loanPhase: "Production" }, isDisabledOnOperation: "notequal" }),
    myForm.getPercentField("Ceiling", 9999.999, { name: "rateCeiling", required: true, isDisabledOn: { loanPhase: "Production" }, isDisabledOnOperation: "notequal" }),
    myForm.getNumberField("Adjustment Interval In Months", { maxValue: 99999, required: true, isDisabledOn: { loanPhase: "Production" }, isDisabledOnOperation: "notequal" })
  ], {
    widths: "4",
    ...isHiddenRateFields, 
    isDisabled: { loanPhase: "Production" }, 
    isDisabledOnOperation: "notequal"
  });
  myForm.addCenteredGroup([
    myForm.getComponent((options) => <BuiltInAmendmentsTable loanId={loanId} options={options} />, { width: 12 }),
    myForm.getButtonField("Add Built in Amendment", undefined, {
      modalContent: (setIsAddFormModalOpen, options) => (
        <AddNewBuiltInAmendmentForm termsOptions={options} handleAddAmendmentClose={() => setIsAddFormModalOpen(false)} loanData={data} />
      ),
      modalSize: "large",
      alignLeft: true,
      isHiddenFunc: ({ isDisabled }) => isDisabled,
      width: 4,
      keepHiddenPosition: true,
    })
  ], { blankWidth: 1 });
  myForm.addGroupFields([
    myForm.getTextareaField("Additional Loan Comments", { name: "description", disabled }),
  ], { widths: "4" });

  const onFormChange = (options, lastUpdated) => {
    const { inputs, setInputs } = options;

    if (lastUpdated === "partialDeedReleaseApplicable") {
      if (inputs?.partialDeedReleaseApplicable) {
        const partialDeedReleaseAmount = (data?.loanAmountOriginal || 0) / (data?.totalUnits || 1);
        setInputs({
          ...inputs,
          partialDeedReleaseAmount: partialDeedReleaseAmount,
        });
      }
      else {
        setInputs({
          ...inputs,
          partialDeedReleaseAmount: 0,
          overridePartialDeedReleaseAmount: undefined,
          partialDeedReleaseOptionId: undefined,
          partialDeedReleaseSweepPercentage: undefined,
          partialDeedReleaseDescription: undefined
        });
      }
    }

    if (lastUpdated === "sweep") {
      if (!inputs?.sweep) {
        setInputs({ ...inputs, sweepOptionId: undefined, sweepPercentage: undefined, sweepAmount: undefined, sweepConditions: undefined });
      }
    }

    if (lastUpdated === "exitFeeApplicable") {
      if (inputs?.exitFeeApplicable) {
        if (inputs?.rightOfFirstRefusal) {
          setInputs({
            ...inputs,
            rightOfFirstRefusal: false,
            rightOfFirstRefusalValue: null,
            rightOfFirstRefusalDescription: null,
            rightOfFirstRefusalPercentage: null
          });
        }
      }
      else {
        setInputs({
          ...inputs,
          exitFeePercentage: null,
        });
      }
    }

    if (lastUpdated === "rightOfFirstRefusal") {
      if (inputs?.rightOfFirstRefusal) {
        if (inputs?.exitFeeApplicable) {
          setInputs({
            ...inputs,
            exitFeeApplicable: false,
            exitFeePercentage: null
          });
        }
      }
      else {
        setInputs({
          ...inputs,
          rightOfFirstRefusalValue: null,
          rightOfFirstRefusalDescription: null,
          rightOfFirstRefusalPercentage: null
        });
      }
    }

    if (lastUpdated === "directPay" && !inputs.directPay && inputs.ach) {
      setInputs({ ...inputs, ach: false });
    }

    if (lastUpdated === "useInterestReserve") {
      if (!inputs.useInterestReserve) {
        setInputs({ ...inputs, interestReserveOriginal: 0 });
      }
      else {
        setInputs({ ...inputs, interestReserveOriginal: data?.interestReserveOriginal || 0 });
      }
    }

    if (lastUpdated === "partialDeedReleaseOptionId") {
      if (inputs?.partialDeedReleaseOptionId === FLAT_RATE_ID) {
        setInputs({ ...inputs, partialDeedReleaseSweepPercentage: undefined });
      }
    }
  };

  const onBlur = (fieldName, value, { inputs, setInputs }) => {
    const loanAmountOriginal = inputs.loanAmountOriginal as number;
    const percentage = inputs.rightOfFirstRefusalPercentage as string;
    if (fieldName === "phase1Term" && value > 255) {
      setInputs({ ...inputs, phase1Term: 255 });
    }

    if (fieldName === "sweepAmount") {
      const sweepAmountValue = parseCurrencyValue(value, 1);
      if (sweepAmountValue && inputs?.sweepPercentage) {
        setInputs({ ...inputs, sweepAmount: sweepAmountValue, sweepPercentage: undefined });
      }
    }

    if (fieldName === "sweepPercentage") {
      const sweepPercentValue = parsePercentValue(value, 200);
      if (sweepPercentValue) {
        setInputs({ ...inputs, sweepPercentage: sweepPercentValue?.toFixed(3), sweepAmount: undefined });
      }
    }

    if (fieldName === "rightOfFirstRefusalValue") {
      if (loanAmountOriginal && value) {
        const calculatedPercentage = parseCurrencyValue(value) / loanAmountOriginal * 100;
        if (calculatedPercentage <= 100) {
          setInputs({ ...inputs, rightOfFirstRefusalPercentage: calculatedPercentage?.toFixed(3) || "" });
        }
      }
    }

    if (fieldName === "rightOfFirstRefusalPercentage") {
      if (loanAmountOriginal && percentage !== undefined) {
        const percentFloat = parsePercentValue(percentage);
        const parsedPercent = percentFloat > 100 ? 100 : percentFloat < 0 ? 0 : percentFloat;
        const calculatedValue = loanAmountOriginal * parsedPercent / 100;
        const calculatedValueFixed = calculatedValue ? calculatedValue.toFixed(2) : 0;
        if (calculatedValueFixed !== value) {
          setInputs({ ...inputs, rightOfFirstRefusalValue: calculatedValueFixed, rightOfFirstRefusalPercentage: parsedPercent?.toFixed(3) });
        }
        else {
          if (percentFloat > 100) {
            setInputs({ ...inputs, rightOfFirstRefusalPercentage: "100.000" });
          }
        }
      }
    }
  };

  return <>
    <BCModal
      isModalOpen={addEscrowOfficerIsOpen}
      size="large"
      closeIcon
      onClose={() => setAddEscrowOfficerIsOpen(false)}
      content={<AddNewEscrowOfficerForm setAddEscrowOfficerIsOpen={setAddEscrowOfficerIsOpen} />}
    />
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      error={error}
      actions={["save", "save-close", "edit", "close"]}
      onFormChanged={onFormChange}
      onBlur={onBlur}
      hideBottomActionsOnEdit
    />
  </>;
};

const LmsLoanDetailsTermsTab = ({ id, loanId = "", loanDetails, reFetchLoanDetails }) => {
  const isRevolverMaster = loanDetails?.loanProgram?.toLowerCase().startsWith("revolver");

  return <>
    {loanDetails && !isRevolverMaster && <LmsLoanDetailsTerms loanId={loanId} loanDetails={loanDetails} id={id} reFetchLoanDetails={reFetchLoanDetails} />}
    {loanDetails && isRevolverMaster && <LmsLoanRevolverMasterLoanTerms id={id} loanDetails={loanDetails} />}
  </>;
};

const GenerateInvoiceModal = ({ loanDetails, costsData, setIsGenerateInvoiceOpen }) => {
  const costsId = costsData?.id;
  const transactionTypeId = useMemo(() => {
    if (costsData?.budgetReviewFee && costsData?.budgetReviewFee > 0) {
      return GlobalConstants.TransactionTypes["Budget Review Fee"];
    }
    else if (costsData?.projectFeasibilityReviewEngineerFee && costsData?.projectFeasibilityReviewEngineerFee > 0) {
      return GlobalConstants.TransactionTypes["Project Feasibility Review Engineer Fee"];
    }
    else if (costsData?.projectFeasibilityReviewFee && costsData?.projectFeasibilityReviewFee > 0) {
      return GlobalConstants.TransactionTypes["Project Feasibility Review Fee"];
    }
    else {
      return "";
    }
  }, [costsData]);
  const [{ data, isLoading, fetchData }] = useFetchData(transactionTypeId ? `loandetails/costs/${costsId}/invoice-amount/${transactionTypeId}` : "", { skipCache: true });
  const handleClose = () => {
    setIsGenerateInvoiceOpen(false);
  };
  const myForm = new BCFormController({ amount: data?.toFixed(2), transactionTypeId }, {
    values: { id: costsId },
    submitSuffix: "loandetails/costs/generate-invoice",
    submitMethod: "POST",
    onSubmitSuccess: handleClose,
  });
  myForm.addCenteredGroup([myForm.getDropdownField("Transaction Type", "metadata/list-transaction-types-by-filter/invoice")]);
  myForm.addCenteredGroup([myForm.getCurrencyField("Invoice Amount", { required: true, name: "amount", disabled: true })]);

  const onFormChanged = async ({ inputs, setInputs }, lastUpdated) => {
    if (lastUpdated === "transactionTypeId" && inputs.transactionTypeId) {
      const { error, data } = await fetchData(`loandetails/costs/${costsId}/invoice-amount/${inputs.transactionTypeId}`);

      if (!error && data !== undefined) {
        setInputs({ ...inputs, amount: data.toFixed(2) });
      }
    }
  };

  return <>
    <ViewDetailsForm loanDetailsData={loanDetails} />
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      actions={[{ action: "save", text: "Generate Invoice" }, "close"]}
      onFormChanged={onFormChanged}
      handleClose={handleClose}
      displayMessageLocally
    />
  </>;
};

const InvoiceCostPaidValues = ({ options, fieldName, data }) => {
  return <div className="invoice-cost-paid-container">
    {!options.isDisabled && <span className={data?.[fieldName] ? "invoice-red" : ""}>$0.00</span>}
    <span>$0.00</span>
    <span>$0.00</span>
    {options.isDisabled && <span></span>}
  </div>;
};

const LmsLoanDetailsCosts = ({ loanId, loanDetails = {} }) => {
  const [isGenerateInvoiceOpen, setIsGenerateInvoiceOpen] = useState(false);
  const costsRecordSuffix = loanId ? `loandetails/costs/${loanId}` : "";
  const noAdditionalInterestBasis = GlobalConstants.LoanCostAdditionalInterestBasis.NO_ADDITIONAL_INTEREST;
  const [{ data = {}, isLoading, error }, refreshData] = useFetchData(costsRecordSuffix);
  const myForm = new BCFormController(data, {
    values: { loanId },
    submitSuffix: "loandetails/costs",
    initIsDisabled: true,
    clearSuffixesOnSubmit: [costsRecordSuffix],
    exitEditOnClose: true,
    onSubmitSuccess: refreshData,
  });

  myForm.addGroupFields([
    myForm.getLabelField("", { width: "3", position: "center" }),
    myForm.getLabelField("Add to Wire", { width: "1", position: "center" }),
    myForm.getLabelField("Financed?", { width: "1", position: "center" }),
    myForm.getLabelField("Cost Payable to Loan Company", { width: "3", position: "center" }),
    myForm.getCheckboxField("", { width: "1", disabled: true, position: "right-center" }),
    myForm.getLabelField("Cost Payable to Broker", { width: "2", position: "left-center" }),
    myForm.getCheckboxField("", { width: "1", disabled: true, position: "right-center" }),
    myForm.getLabelField("Cost Payable to Lender", { width: "2", inline: true, position: "left-center" }),
    myForm.getLabelField("Invoice", { width: "1", position: "center" }),
    myForm.getLabelField("Costs Paid", { width: "1", position: "center" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Additional Interest Basis", { width: "3", position: "right-center", required: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
    myForm.getDropdownField("", "metadata/list-additional-interest-basis", { name: "additionalInterestBasisId", required: true, width: "3" }),
    myForm.getDropdownField("", "metadata/list-additional-interest-basis", { name: "brokerInAdditionalInterestBasisId", required: true, width: "3", disabled: true }),
    myForm.getDropdownField("", "metadata/list-additional-interest-basis", { name: "brokerOutAdditionalInterestBasisId", required: true, width: "3", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Additional Interest Fee %", { width: "3", position: "right-center" }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
    myForm.getInputField("", { fieldType: "percent", name: "additionalInterestRate", width: "3", isDisabledOn: { additionalInterestBasisId: noAdditionalInterestBasis } }),
    myForm.getInputField("", { fieldType: "percent", name: "brokerInAdditionalInterestRate", width: "3", isDisabledOn: { brokerInAdditionalInterestBasisId: noAdditionalInterestBasis } }),
    myForm.getInputField("", { fieldType: "percent", name: "brokerOutAdditionalInterestRate", width: "3", isDisabledOn: { brokerOutAdditionalInterestBasisId: noAdditionalInterestBasis } }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Additional Interest Floor", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { name: "addAdditionalInterestFloorToWire", width: "1" }),
    myForm.getCheckboxField("", { name: "additionalInterestFloorFinanced", width: "1" }),
    myForm.getInputField("", { fieldType: "dollar", width: "3", name: "additionalInterestFloor", disabled: true }),
    myForm.getInputField("", { fieldType: "dollar", width: "3", name: "brokerInAdditionalInterestFloor", disabled: true }),
    myForm.getInputField("", { fieldType: "dollar", width: "3", name: "brokerOutAdditionalInterestFloor", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Origination Fee %", { width: "3", position: "right-center" }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
    myForm.getInputField("", { fieldType: "percent", width: "3", name: "originationFeePercentage" }),
    myForm.getInputField("", { fieldType: "percent", width: "3", name: "brokerInOriginationFeePercentage", disabled: true }),
    myForm.getInputField("", { fieldType: "percent", width: "3", name: "brokerOutOriginationFeePercentage", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Origination Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addOriginationFeeToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "originationFeeFinanced" }),
    myForm.getCurrencyField("", { width: "3", name: "originationFee", disabled: true }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInOriginationFee", disabled: true }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutOriginationFee", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Appraisal Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addAppraisalFeeToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "appraisalFeeFinanced" }),
    myForm.getCurrencyField("", { width: "3", name: "appraisalFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInAppraisalFee", disabled: true }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutAppraisalFee", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Credit Report Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addCreditReportFeeToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "creditReportFeeFinanced" }),
    myForm.getCurrencyField("", { width: "3", name: "creditReportFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInCreditReportFee", disabled: true }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutCreditReportFee", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Flood Determination Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addFloodFeeToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "floodFeeFinanced" }),
    myForm.getCurrencyField("", { width: "3", name: "floodFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInFloodFee", disabled: true }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutFloodFee", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Processing Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addProcessingFeeToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "processingFeeFinanced" }),
    myForm.getInputField("", { fieldType: "dollar", width: "3", name: "processingFee" }),
    myForm.getInputField("", { fieldType: "dollar", width: "3", name: "brokerInProcessingFee", disabled: true }),
    myForm.getInputField("", { fieldType: "dollar", width: "3", name: "brokerOutProcessingFee", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Legal Processing Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addLegalFeeToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "legalFeeFinanced" }),
    myForm.getCurrencyField("", { width: "3", name: "legalFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInLegalFee", disabled: true }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutLegalFee", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Engineering Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addEngineeringFeeToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "engineeringFeeFinanced" }),
    myForm.getCurrencyField("", { fieldType: "dollar", width: "3", name: "engineeringFee" }),
    myForm.getCurrencyField("", { fieldType: "dollar", width: "3", name: "brokerInEngineeringFee" }),
    myForm.getCurrencyField("", { fieldType: "dollar", width: "3", name: "brokerOutEngineeringFee" }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Budget Review Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addBudgetReviewFeeToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "budgetReviewFeeFinanced" }),
    myForm.getCurrencyField("", { width: "3", name: "budgetReviewFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInBudgetReviewFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutBudgetReviewFee" }),
    myForm.getComponent((options) => <InvoiceCostPaidValues options={options} fieldName="budgetReviewFee" key="budgetReviewFee" data={data} />, { width: "2" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Project Feasibility Review Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addProjectFeasibilityReviewFeeToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "projectFeasibilityReviewFeeFinanced" }),
    myForm.getCurrencyField("", { width: "3", name: "projectFeasibilityReviewFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInProjectFeasibilityReviewFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutProjectFeasibilityReviewFee" }),
    myForm.getComponent((options) => <InvoiceCostPaidValues options={options} fieldName="projectFeasibilityReviewFee" key="projectFeasibilityReviewFee" data={data} />, { width: "2" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Project Feasibility Review Engineer Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addProjectFeasibilityReviewEngineerFeeToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "projectFeasibilityReviewEngineerFeeFinanced" }),
    myForm.getCurrencyField("", { width: "3", name: "projectFeasibilityReviewEngineerFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInProjectFeasibilityReviewEngineerFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutProjectFeasibilityReviewEngineerFee" }),
    myForm.getComponent((options) => <InvoiceCostPaidValues options={options} fieldName="projectFeasibilityReviewEngineerFee" key="projectFeasibilityReviewFee" data={data} />, { width: "2" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Property / Budget Setup Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addAssignmentFeeToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "assignmentFeeFinanced" }),
    myForm.getCurrencyField("", { width: "3", name: "assignmentFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInAssignmentFee", disabled: true }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutTitleAndEscrowFee", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Title And Escrow Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addTitleAndEscrowFeeToWire", disabled: true }),
    myForm.getCheckboxField("", { width: "1", name: "titleAndEscrowFeeFinanced" }),
    myForm.getCurrencyField("", { width: "3", name: "titleAndEscrowFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInTitleAndEscrowFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutTitleAndEscrowFee" }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Assignment Fee POC", { width: "3", position: "right-center" }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
    myForm.getCheckboxField("", { width: "3", name: "assignmentFeePoc" }),
    myForm.getBlankField({ width: "3" }),
    myForm.getBlankField({ width: "3" }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getInputField("", { name: "otherFee1Description", placeHolder: "Other Fee 1 Name", width: "3", textAlignRight: true }),
    myForm.getCheckboxField("", { width: "1", name: "addOtherFee1ToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "otherFee1Financed" }),
    myForm.getCurrencyField("", { width: "3", name: "otherFee1" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInOtherFee1", disabled: true }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutOtherFee1", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getInputField("", { name: "otherFee2Description", placeHolder: "Other Fee 2 Name", width: "3", textAlignRight: true }),
    myForm.getCheckboxField("", { width: "1", name: "addOtherFee2ToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "otherFee2Financed" }),
    myForm.getCurrencyField("", { width: "3", name: "otherFee2" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInOtherFee2", disabled: true }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutOtherFee2", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Broker Fee Rate", { width: "3", position: "right-center" }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
    myForm.getInputField("", { fieldType: "percent", width: "3", name: "brokerFeeRate", decimalsLimit: 2 }),
    myForm.getInputField("", { fieldType: "percent", width: "3", name: "brokerInBrokerFeeRate", disabled: true }),
    myForm.getInputField("", { fieldType: "percent", width: "3", name: "brokerOutBrokerFeeRate", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Broker Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addBrokerFeeToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "feeFinanced" }),
    myForm.getCurrencyField("", { width: "3", name: "fee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInFee", disabled: true }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutFee", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("WL Broker Fee Rate", { width: "3", position: "right-center" }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
    myForm.getInputField("", { fieldType: "percent", width: "3", name: "wlbrokerFeeRate", decimalsLimit: 2 }),
    myForm.getInputField("", { fieldType: "percent", width: "3", name: "brokerInWlbrokerFeeRate", disabled: true }),
    myForm.getInputField("", { fieldType: "percent", width: "3", name: "brokerOutWlbrokerFeeRate", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("WL Broker Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addWlbrokerFeeToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "wlbrokerFeeFinanced" }),
    myForm.getCurrencyField("", { width: "3", name: "wlbrokerFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInWlbrokerFee", disabled: true }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutWlbrokerFee", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Draw Administration Fee", { width: "3", position: "right-center" }),
    myForm.getCheckboxField("", { width: "1", name: "addDrawAdministrationFeeToWire" }),
    myForm.getCheckboxField("", { width: "1", name: "drawAdministrationFeeFinanced" }),
    myForm.getCurrencyField("", { width: "3", name: "drawAdministrationFee" }),
    myForm.getCurrencyField("", { width: "3", name: "brokerInDrawAdministrationFee", disabled: true }),
    myForm.getCurrencyField("", { width: "3", name: "brokerOutDrawAdministrationFee", disabled: true }),
    myForm.getBlankField({ width: "1" }),
    myForm.getBlankField({ width: "1" }),
  ], { widths: "2" });
  myForm.addGroupFields([
    myForm.getLabelField("Difference of Original and Calculated", { width: "8", position: "right-center" }),
    myForm.getInputField("", { fieldType: "dollar", width: "3", name: "loanAmountBalance", disabled: true, valueColor: "red" }),
  ], { widths: "2" });

  const onFormChanged = ({ inputs, setInputs }, lastUpdated) => {
    const loanAmountBalance = data?.loanAmountBalance;

    if (lastUpdated === "additionalInterestBasisId") {
      if (inputs.additionalInterestBasisId === GlobalConstants.LoanCostAdditionalInterestBasis.NO_ADDITIONAL_INTEREST) {
        setInputs({ ...inputs, additionalInterestRate: "0" });
      }
    }

    if (lastUpdated === "originationFeePercentage" && inputs?.originationFeePercentage !== undefined) {
      let originationFee = loanAmountBalance * inputs?.originationFeePercentage / 100 as any;
      originationFee = isNaN(originationFee) || originationFee === undefined ? "0" : originationFee.toFixed(2);
      setInputs({ ...inputs, originationFee: originationFee });
    }

    if (lastUpdated === "fee" && inputs?.fee !== undefined) {
      let brokerFeeRate = inputs?.fee / loanAmountBalance * 100 as any;
      brokerFeeRate = isNaN(brokerFeeRate) || brokerFeeRate === undefined ? "0" : brokerFeeRate.toFixed(3);
      setInputs({ ...inputs, brokerFeeRate });
    }

    if (lastUpdated === "brokerFeeRate" && inputs?.brokerFeeRate !== undefined && inputs?.brokerFeeRate !== null) {
      const { percentRes, amountRes } = getPercentageOfAmount(loanAmountBalance, inputs?.brokerFeeRate, { percentLimit: 99.999 });
      setInputs({ ...inputs, fee: amountRes, brokerFeeRate: percentRes });
    }
  };

  const handleIsDisabled = (isDisabled, { lastUpdated }) => {
    return isDisabled || !!lastUpdated;
  };

  const formActions = [
    "save",
    "save-close",
    {
      text: "Generate Invoice",
      handleClick: () => setIsGenerateInvoiceOpen(true),
      icon: GlobalConstants.Icons.SAVE,
      isDisabled: handleIsDisabled
    },
    "edit",
    "close"
  ];

  return <>
    <BCModal
      isModalOpen={isGenerateInvoiceOpen}
      size="fullscreen"
      closeIcon
      onClose={() => setIsGenerateInvoiceOpen(false)}
      content={<GenerateInvoiceModal loanDetails={loanDetails} costsData={data} setIsGenerateInvoiceOpen={setIsGenerateInvoiceOpen}/>}
    />
    <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={formActions} onFormChanged={onFormChanged} hideBottomActionsOnEdit />
  </>;
};

const LmsLoanDetailsBudgets = ({ id, loanId }) => {
  const [placeholderValue, setPlaceholderValue] = useState("");
  const editPath = `/loandetails/editbudget/{id}?loanId=${loanId}&loanDetailsId=${id}`;
  const editPropertyPath = `/loanapplications/editproperty/{id}?loanId=${loanId}`;
  const editPathFunc = (rowData) => rowData?.tableOption === "LoanBudget" ? editPath : editPropertyPath;

  const handleSelectedIds = (selectedIds) => {
    return { ids: selectedIds, placeholderAmount: placeholderValue };
  };

  const actionButtons = [
    { action: "add", path: `/loandetails/addbudget/${id}?loanId=${loanId}` },
    { text: "Edit", path: editPathFunc, isDisabled: (checkedItems) => checkedItems?.length !== 1 },
    "remove",
    "export",
    { text: "Transfer Budget", icon: GlobalConstants.Icons.DELETE, path: "/loandetails/transferbudget", isDisabled: () => true },
    {
      action: "submitselected",
      text: "Add Placeholder",
      submitSuffix: "budget/add-placeholder",
      method: "POST",
      icon: GlobalConstants.Icons.CHEVRON_DOWN,
      data: handleSelectedIds,
      refreshDataOnSuccess: true,
      isDisabled: (checkedItems) => !(checkedItems?.length > 0 && placeholderValue)
    },
    <LmsActionInput type="number" handleChange={setPlaceholderValue} value={placeholderValue} /> as any,
    {
      text: "Upload Budgets",
      modalContent: (setUploadModalOpen) => <UploadBudget loanId={loanId} setUploadModalOpen={setUploadModalOpen} />,
      modalSize: "fullscreen"
    },
  ];

  const handleRowClick = (options) => {
    if (options?.clickedRow?.tableOption?.toLowerCase().includes("property")) {
      options.handleActionClick({ id: options?.clickedRow.id, path: `loanapplications/editproperty/{id}?loanId=${loanId}` });
    }
    else {
      options.handleActionClick(options);
    }
  };

  return <>
    <LmsTable
      actionButtons={actionButtons}
      apiSuffix={loanId ? `loandetails/${loanId}/budgets` : ""}
      deleteSuffix="loandetails/budgets"
      onClickPath={editPathFunc}
      title="Budgets"
      handleClick={handleRowClick}
      columnLabelsDictionary={{
        loanNumber: "Loan #",
        applicationNumber: "Application #",
        borrowerOrganization: "Borrower"
      }}
    />
  </>;
};

const AddBudgetForm = ({ id, loanId, tempId }) => {
  const [{ data, fetchData, error, setError }] = useFetchData(`loandetails/${id}`);
  const initialData = data ? { borrowerOrganizationId: data?.borrowerOrganizationId } : {};
  const [budgetUploadStep, setBudgetUploadStep] = useState("step1");
  const [loanBudgetTempId, setLoanBudgetTempId] = useState(tempId || "");
  const [isPropertiesTableOpen, setIsPropertiesTableOpen] = useState(true);
  const [isReadyToSubmit, setIsReadyToSubmit] = useState(false);
  const [propertiesAdded, setPropertiesAdded] = useState(false);

  const onSubmitSuccess = (responseData) => {
    if (responseData?.id) {
      setLoanBudgetTempId(responseData?.id);
    }

    setBudgetUploadStep("step2");
  };

  const handleSubmit = (data, submitOptions) => {
    if (budgetUploadStep === "step1" && !loanBudgetTempId) {
      submitOptions.handleSubmit("loandetails/budgets/temp", {
        method: "POST",
      } as IHandleSubmitOptions);
    }
    else {
      setBudgetUploadStep("step2");
    }
  };

  const myFormStep1 = new BCFormController(initialData, { submitSuffix: "loandetails/budgets/temp", values: { loanDetailId: id, loanId }, blankWidth: 4, onSubmitSuccess });
  myFormStep1.addCenteredGroup([
    myFormStep1.getDropdownField("Borrower", "loandetails/list-budget-loan-borrowers", { name: "borrowerOrganizationId", required: true })
  ]);
  myFormStep1.addCenteredGroup([
    myFormStep1.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", { dependencyField: "borrowerOrganizationId" })
  ]);
  myFormStep1.addCenteredGroup([
    myFormStep1.getDropdownField("Application #", "loandetails/list-budget-loan-applications?borrowerOrganizationId={id}", {
      dependencyField: "borrowerOrganizationId",
      name: "loanId",
      labelWarningText: "Only Applications that have Properties which have no Budgets will be displayed"
    })
  ]);
  myFormStep1.addCenteredGroup([
    myFormStep1.getLabelField("Budget Template Option", { width: 3, position: "top-right", errorLabel: "" }),
    myFormStep1.getGroupRadioField("Template"),
    myFormStep1.getGroupRadioField("Upload"),
  ], { radioGroupName: "budgetTemplateOption", radioGroupValue: "Template" });

  const useFormInstanceStep1 = useForm(myFormStep1.values, { myForm: myFormStep1 }) as any;

  const myFormStepTwo = new BCFormController(useFormInstanceStep1.inputs, {
    values: { loanBudgetTempId },
    submitSuffix: loanBudgetTempId ? `loandetails/budgets/${loanBudgetTempId}` : "",
    blankWidth: 4,
    allDropdownOptionsAreReady: !!loanBudgetTempId
  });
  myFormStepTwo.addCenteredGroup([myFormStepTwo.getDropdownField("Borrower", "loandetails/list-budget-loan-borrowers", { name: "borrowerOrganizationId", disabled: true })]);
  myFormStepTwo.addCenteredGroup([
    myFormStepTwo.getDropdownField("Application #", "loandetails/list-budget-loan-applications?borrowerOrganizationId={id}", {
      dependencyField: "borrowerOrganizationId",
      name: "loanId",
      disabled: true
    })
  ]);
  myFormStepTwo.addCenteredGroup([
    myFormStepTwo.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", {
      dependencyField: "borrowerOrganizationId",
      disabled: true
    })
  ]);
  myFormStepTwo.addCenteredGroup([
    myFormStepTwo.getDropdownField("Selected Property", loanBudgetTempId ? `loandetails/budgets/${loanBudgetTempId}/properties` : "", {
      name: "loanPropertyIds",
      disabled: true,
      dropdownMultiple: true,
      dropdownListKey: "street"
    })
  ]);
  myFormStepTwo.addCenteredGroup([
    myFormStepTwo.getDropdownField("Selected Template", "budget/templates", { name: "loanBudgetTemplateId", disabled: true })
  ]);

  const useFormInstanceStep2 = useForm(myFormStepTwo.values, { myForm: myFormStepTwo }) as any;

  const onSelectedPropertyClick = async (options) => {
    if (options?.getCheckedIds) {
      const checkedIds = options?.getCheckedIds();
      const addedProperties = useFormInstanceStep2?.inputs?.loanPropertyIds || [];
      if (checkedIds) {
        const isAnyIncluded = addedProperties.length > 0 && checkedIds.some((id) => useFormInstanceStep2?.inputs?.loanPropertyIds?.includes(id));
        if (isAnyIncluded) {
          options?.setAlertMessage("One or more properties have already been selected.", () => options.setCheckedItems([]));
        }
        else {
          const data = { loanBudgetTempId, loanPropertyIds: [...checkedIds] };
          const { error: postError } = await fetchData("loandetails/budgets/save-budget-temp-property", { fetchMethod: "POST", data });
          if (postError) {
            setError(postError);
          }
          else {
            useFormInstanceStep2.handleChange(null, { name: "loanPropertyIds", value: [...checkedIds, ...addedProperties] });
            setPropertiesAdded(true);
            options.setCheckedItems([]);
            setIsPropertiesTableOpen(false);
          }
        }
      }
    }
  };

  const propertiesGridActions = [
    "export",
    "save",
    "cancel",
    { text: "Select Property", icon: GlobalConstants.Icons.CARET_UP, handleClick: onSelectedPropertyClick, isDisabled: (checkedItems) => checkedItems?.length === 0 }
  ];

  const onSelectedTemplateClick = async (options) => {
    if (options?.firstCheckedRow?.id) {
      const data = { id: loanBudgetTempId, loanBudgetTemplateId: options?.firstCheckedRow?.id };
      const { error: putError } = await fetchData("loandetails/budgets/save-budget-temp-template", { fetchMethod: "PUT", data });
      if (putError) {
        setError(putError);
      }
      else {
        options.setCheckedItems([]);
        useFormInstanceStep2.handleChange(null, { name: "loanBudgetTemplateId", value: options?.firstCheckedRow?.id });
      }
    }

    setIsReadyToSubmit(true);
  };

  const budgetTemplatesGridActions = [
    "export",
    { text: "Select Template", icon: GlobalConstants.Icons.CARET_UP, handleClick: onSelectedTemplateClick, isDisabled: (checkedItems) => checkedItems?.length !== 1 }
  ];

  const handlePrevClick = () => {
    if (budgetUploadStep === "step2") {
      if (isPropertiesTableOpen) {
        setBudgetUploadStep("step1");
      }
      else {
        setIsPropertiesTableOpen(true);
      }
    }
  };

  const closeOrNext = propertiesAdded && isPropertiesTableOpen ? { text: "Select Budget Template", icon: GlobalConstants.Icons.SAVE, handleClick: () => setIsPropertiesTableOpen(false) } : "close";
  const prevClick = useFormInstanceStep2?.inputs?.loanPropertyIds?.length > 0 ? [{ text: "Previous", icon: GlobalConstants.Icons.PREV, handleClick: handlePrevClick }] : [];
  const myFormTwoActions = [...prevClick, closeOrNext];
  const submitActions = isReadyToSubmit  && !isPropertiesTableOpen ? [...myFormTwoActions, { action: "save-close", text: "Submit", className: "red-submit-form-button" }] : myFormTwoActions;
  const nextAction = { text: "Next", icon: GlobalConstants.Icons.SAVE } as any;

  if (useFormInstanceStep1.inputs.budgetTemplateOption === "Upload") {
    nextAction.handleClick = () => setBudgetUploadStep("upload");
  }
  else {
    nextAction.action = "save";
  }

  return <>
    {budgetUploadStep === "upload" && (
      <UploadBudgetForm loanPropertyData={null} loanId={loanId} handleClose={useFormInstanceStep1.handleClose} />
    )}
    {budgetUploadStep === "step1" && <LmsFormGenerator
      myForm={myFormStep1}
      actions={[nextAction, "close"]}
      useFormInstance={useFormInstanceStep1}
      handleSubmit={handleSubmit}
    />}
    {budgetUploadStep === "step2" && <>
      <LmsFormGenerator myForm={myFormStepTwo} useFormInstance={useFormInstanceStep2} error={error} skipActions />
      {isPropertiesTableOpen && <LmsTable
        apiSuffix={loanBudgetTempId ? `loandetails/budgets/${loanBudgetTempId}/properties` : ""}
        actionButtons={propertiesGridActions}
        title="Properties"
        columnLabelsDictionary={TABLES.PROPERTIES}
        skipClick
      />}
      {!isPropertiesTableOpen && (
        <LmsTable
          apiSuffix="budget/templates"
          actionButtons={budgetTemplatesGridActions}
          title="Budget Templates"
          onClickModalSize="fullscreen"
          onClickModalContent={(_, { clickedRowData }) => {
            return (
              <LmsTable
                apiSuffix={`budget/templates/${clickedRowData?.id}/details`}
                actionButtons={["export"]}
                title="Details"
              />
            );
          }}
          columnClickOptions={{
            name: { idProp: "id", isModalContentLink: true }
          }}
        />
      )}
      <LmsFormGenerator myForm={myFormStepTwo} useFormInstance={useFormInstanceStep2} actions={submitActions} onlyActions />
    </>}
  </>;
};

const EditBudget = ({ id, loanDetailsId }) => {
  const budgetSuffix = `loandetails/budgets/${id}`;
  const [{ data, isLoading, error }] = useFetchData(budgetSuffix);
  const dataUploadId = data?.dataUploadId;
  const displayBridgeToBuild = data?.bridgeToBuildPhaseId && data?.bridgeToBuildPhaseId !== data?.BridgeToBuildPhase2Id;
  const isStatusApproved = data?.loanBudgetStatusId === GlobalConstants.LoanBudgetStatus.APPROVED;
  const isStatusPlaceholder = data?.loanBudgetStatusId === GlobalConstants.LoanBudgetStatus.PLACEHOLDER;
  const approvedStatuses = isStatusApproved ? "metadata/list-loan-budget-statuses-exclude-placeholder" : `metadata/list-loan-budget-statuses/${isStatusPlaceholder ? "false" : "true"}`;

  const myForm = new BCFormController(data, {
    values: { loanBudgetId: id },
    submitSuffix: "loandetails/budgets",
    clearSuffixesOnSubmit: [budgetSuffix],
    allDropdownOptionsAreReady: !!data
  });

  myForm.addGroupFields([
    myForm.getDropdownField("Borrower", "metadata/list-borrower-organizations", { disabled: true, name: "borrowerOrganizationId" }),
    myForm.getDisplayField("Application #", { name: "applicationNumberDisplay" }),
    myForm.getDisplayField("Loan #", { name: "loanNumberDisplay" }),
  ]);

  const budgetGroup = [
    myForm.getDropdownField("Loan Budget Template", "budget/templates", { name: "loanBudgetTemplateId", disabled: true }),
    myForm.getDropdownField("Loan Budget Status", approvedStatuses, { disabled: isStatusPlaceholder }),
  ] as any;

  if (displayBridgeToBuild) {
    budgetGroup.push(myForm.getLabelField("BTB Phase I Budget", {
      position: "left-center",
    }));
  }
  else {
    budgetGroup.push(myForm.getBlankField());
  }

  myForm.addGroupFields(budgetGroup);

  const documentLinkGroup = [] as any[];
  if (dataUploadId) {
    documentLinkGroup.push(
      myForm.getLinkField("Budget created from", {
        linkType: "DownloadFile",
        name: "attachmentFileName",
        link: dataUploadId ? `budget/uploads/${dataUploadId}/export` : "",
        method: "POST",
        width: 8
      }),
    );
  }
  else {
    documentLinkGroup.push(
      myForm.getDisplayField("Budget created from", {
        value: "Manual entry",
        width: 8
      }),
    );
  }

  myForm.addGroupFields(documentLinkGroup, { widths: 8 });

  const handleMarkBudgetAsApproved = async (inputs, handleSubmit, useFormInstance) => {
    useFormInstance.initSubmittingStates("Mark Budget Approved");
    const { data } = await useFormInstance?.fetchData?.(`loandetails/budgets/${id}/mark-loan-budget-complete`);
    const submitFunc = () => handleSubmit(`loandetails/budgets/${id}/mark-loan-budget-complete`, {
      buttonText: useFormInstance?.buttonText,
      method: "PUT",
      onSuccessFunc: () => useFormInstance?.handleClose(),
    } as IHandleSubmitOptions);

    if (data?.confirmationDescription) {
      const parsedMessage = data?.confirmationDescription?.replaceAll("<br>", " ");
      useFormInstance?.handleConfirmFunc(submitFunc, parsedMessage, false, "Mark Budget Approved");
    }
    else {
      submitFunc();
    }
  };

  const formActions = ["save-close", "close"] as any[];

  if (!isStatusApproved) {
    formActions.unshift({
      text: "Mark Budget Approved",
      icon: GlobalConstants.Icons.CARET_UP,
      handleClick: handleMarkBudgetAsApproved,
      isDisabled: () => isStatusPlaceholder
    });
  }

  const budgetDetailsActions = [
    { action: "add", path: `/loandetails/addbudgetdetails?loanBudgetId=${id}` },
    { action: "edit", path: `/loandetails/editbudgetdetails/{id}?loanDetailsId=${loanDetailsId}` },
    "remove",
    "export"
  ];

  const budgetTabs = {
    Details: <LmsTable
      actionButtons={isStatusApproved || !data ? ["export"] : budgetDetailsActions}
      apiSuffix={`loandetails/budgets/${id}/details`}
      onClickPath={`/loandetails/${isStatusApproved ? "viewbudgetdetails" : "editbudgetdetails"}/{id}?loanDetailsId=${loanDetailsId}`}
      deleteSuffix="loandetails/budgets/details"
      calculatedColumns={["amount", "disbursed", "remaining", "fundAtClosingAmount"]}
      columnLabelsDictionary={{
        fundAtClosingAmount: "Amount Require At Closing",
        percentWorkCompleteWooverride: "Percent Work Complete WO Override"
      }}
      currencyFields={["remaining", "fundAtClosingAmount"]}
    />,
    "Draw Summary": <LmsTable actionButtons={["export"]} apiSuffix={`loandetails/budgets/${id}/draws`} skipClick />,
  };

  return <React.Fragment>
    <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={formActions} />
    {data && <LmsTabPages tabPages={budgetTabs} />}
  </React.Fragment>;
};

const AddVendorForm = ({ setIsModalOpen, options }) => {
  const handleSuccess = (response) => {
    const payeeOrganizationIds = options?.useFormInstance?.inputs?.payeeOrganizationIds || [];
    options?.handleFormChange(null, { name: "payeeOrganizationIds", value: [...payeeOrganizationIds, response?.vendroRequestId] }, true);
  };

  const myForm = new BCFormController({}, {
    values: {
      loanBudgetId: options?.useFormInstance?.inputs?.loanBudgetId,
      loanBudgetDetailId: options?.useFormInstance?.inputs?.id
    },
    submitSuffix: "budget/vendor-request",
    onSubmitSuccess: handleSuccess
  });
  myForm.addCenteredGroup([myForm.getInputField("Vendor Name", { name: "name", required: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Email", { validations: ["required", "email"] })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Message", { name: "description", required: true })]);

  const formActions = [{ action: "save-close", text: "Submit" }, "close"];

  return <LmsFormGenerator myForm={myForm} actions={formActions} handleClose={() => setIsModalOpen(false)}/>;
};

const addBudgetDetailsFields = (myForm, isView = false) => {
  const addVendorOptions: IFormGeneratorFieldOptions = {
    actionLabel: "Add Vendor",
    modalContent: (setIsAddFormModalOpen, options) => <AddVendorForm setIsModalOpen={setIsAddFormModalOpen} options={options}/>,
    actionIcon: GlobalConstants.Icons.CARET_UP,
    name: "payeeOrganizationIds",
    dropdownMultiple: true
  };

  if (!isView) {
    myForm.addCenteredGroup([myForm.getDisplayField("Borrower", { name: "borrowerOrganizationDisplay", disabled: true })]);
    myForm.addCenteredGroup([myForm.getDropdownDisplayField("Special Purpose Entity", { name: "specialPurposeEntity" })]);
    myForm.addCenteredGroup([myForm.getDisplayField("Application #", { name: "applicationNumberDisplay" })]);
    myForm.addCenteredGroup([myForm.getDisplayField("Loan #", { name: "loanNumberDisplay" })]);
    myForm.addCenteredGroup([myForm.getDropdownField("Loan Property", "loandetails/list-loan-properties/{id}", {
      dependencyField: "loanId",
      name: "loanPropertyId",
      required: true,
      disabled: true
    })]);
  }
  myForm.addCenteredGroup([myForm.getDropdownField("Loan Budget Category", "budget/categories", {
    disabled: myForm?.values?.isPlaceholder,
    required: true
  })]);
  myForm.addCenteredGroup([myForm.getInputField("Loan Budget Sub Category", {
    disabled: myForm?.values?.isPlaceholder
  })]);
  myForm.addCenteredGroup([myForm.getCurrencyField("Budget Amount", { name: "budgetAmount", required: true })]);
  myForm.addCenteredGroup([myForm.getCurrencyField("Amount Require at Closing", { name: "fundAtClosingAmount" })]);
  myForm.addCenteredGroup([myForm.getNumberField("Month Of Remaining Expenditure")]);
  myForm.addCenteredGroup([myForm.getDropdownField("Payee", "vendors/list-organizations", !isView ? addVendorOptions : {
    dropdownMultiple: true,
    name: "payeeOrganizationIds"
  })]);

  if (!isView) {
    if (myForm?.values?.isPlaceholder) {
      myForm.addCenteredGroup([
        myForm.getWarningTextField("You cannot change Category/Sub Category in a Placeholder budget", { isCompact: true }),
      ], {
        isCompact: true
      });
    }
    myForm.addCenteredGroup([
      myForm.getWarningTextField("Changing Budget Amount will change LTC", { isCompact: true }),
    ], {
      isCompact: true,
      isHiddenFunc: ({ inputs }) => !inputs?.budgetAmount || inputs?.budgetAmount === myForm?.values?.budgetAmount
    });
  }

  return myForm;
};

const AddBudgetDetails = ({ loanBudgetId }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`loandetails/budgets/${loanBudgetId}/details/initial-data`);
  const myForm = addBudgetDetailsFields(new BCFormController(removeProp(data, "id"), {
      values: {
        loanId: data?.loanId,
        loanBudgetId,
        isPlaceholder: data?.loanBudgetTemplateName === "Placeholder"
      },
      submitSuffix: "loandetails/budgets/details",
      blankWidth: 4,
      excludeFromSubmit: ["loanId"],
      styles: { minHeight: "1150px" }
    }
  ));

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} />;
};

const EditBudgetDetails = ({ id, loanDetailsId, isView = false }) => {
  const [{ data: loanBudgetDetails, isLoading, error }] = useFetchData(`loandetails/budgets/details/${id}`);
  const [{ data: loanDetailsData = {} }] = useFetchData(loanDetailsId ? `loandetails/${loanDetailsId}` : "");
  const myForm = addBudgetDetailsFields(new BCFormController({ ...loanDetailsData, ...loanBudgetDetails }, {
    values: {
      loanBudgetId: loanBudgetDetails?.loanBudgetId,
      hidden: true,
      loanId: loanBudgetDetails?.loanId,
      isPlaceholder: loanBudgetDetails?.loanBudgetTemplateName === "Placeholder",
    },
    submitSuffix: "loandetails/budgets/details",
    deleteSuffix: "loandetails/budgets/details",
    blankWidth: 4,
    initIsDisabled: isView,
    excludeFromSubmit: ["hidden", "isPlaceholder"],
    styles: { minHeight: "1150px" }
  }), isView);

  const formActions = isView ? ["close"] : undefined;
  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={formActions} />;
};

const addBudgetBankAccountFields = (myForm) => {
  myForm.addCenteredGroup([myForm.getDropdownField("Borrower")]);
  myForm.addCenteredGroup([myForm.getDropdownField("Special Purpose Entity")]);
  myForm.addCenteredGroup([myForm.getInputField("Application #")]);
  myForm.addCenteredGroup([myForm.getInputField("Loan #")]);
  myForm.addCenteredGroup([myForm.getInputField("Property", { disabled: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Title Company Bank Information")]);
  return myForm;
};

const AddBudgetBankAccountForm = () => {
  const myForm = addBudgetBankAccountFields(new BCFormController({})); // TODO: Add submit suffix
  const formActions = ["save", "save-close", "close"];
  return <LmsFormGenerator myForm={myForm} actions={formActions}/>;
};

const EditBudgetBankAccountForm = ({ id }) => {
  if (FeatureFlags.IS_DEBUG) {
     console.log(id);
  }

  const myForm = addBudgetBankAccountFields(new BCFormController({})); // TODO: Add submit suffix
  const formActions = ["save", "save-close", "close"];
  return <LmsFormGenerator myForm={myForm} actions={formActions}/>;
};

const LmsLoanDetailsProject = ({ id, loanDetails }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(id ? `loandetails/project/${id}` : "");
  const listBuilderOrganizations = loanDetails?.borrowerOrganizationId ? `loandetails/list-builder-organizations/${loanDetails?.borrowerOrganizationId}` : "";

  const myForm = new BCFormController(data, {
    initIsDisabled: true,
    values: { loanId: data?.loanId },
    submitSuffix: "loandetails/project",
    clearSuffixesOnSubmit: [`loandetails/project/${id}`],
    exitEditOnClose: true
  });

  myForm.addGroupFields([
    myForm.getInputField("Property Address", { name: "street", disabled: true }),
    myForm.getDropdownField("Builder", listBuilderOrganizations, { name: "builderOrgId" }),
    myForm.getInputField("Project Name"),
    myForm.getNumberField("# Properties", { name: "numberOfProperties", disabled: true })
  ]);

  myForm.addGroupFields([
    myForm.getNumberField("# Units", { name: "numberOfUnits", disabled: true }),
    myForm.getDisplayField("Zip Code", { name: "zip" }),
    myForm.getDropdownField("State", "orgs/list-states", { name:"stateName", width: "4", disabled: true }),
    myForm.getDropdownField("City", "orgs/list-cities", { name:"cityId", width: "4", disabled: true }),
  ]);

  myForm.addGroupFields([
    myForm.getInputField("Permit Jurisdiction", { name: "jurisdiction", width: 4 }),
    myForm.getDropdownField("County", "orgs/list-counties", { name: "countyId",  width: 4, disabled: true }),
    myForm.getInputField("Project Status", { width: 8 }),
  ]);

  myForm.addGroupFields([
    myForm.getInputField("Submarket"),
    myForm.getNumberField("Number Of Active Listings"),
    myForm.getBlankField(),
    myForm.getBlankField()
  ]);

  myForm.addGroupFields([
    myForm.getNumberField("Number Of Pending Sales"),
    myForm.getInputField("Tax Rate", { name: "taxPercentRate", fieldType: "percent" }),
    myForm.getNumberField("Number Of Closed Sales"),
    myForm.getNumberField("Absorption Per Month"),
  ]);

  myForm.addGroupFields([
    myForm.getNumberField("Avg Sold Market Time (Days)", { name: "averageSoldMarketTime" }),
    myForm.getNumberField("Market Date Time Frame (Months)", { name: "timeFrame" }),
    myForm.getNumberField("Mile Radius"),
    myForm.getInputField("Appraisal Type", { disabled: true }),
  ]);

  myForm.addGroupFields([
    myForm.getCurrencyField("Underwritten Value", { disabled: true }),
    myForm.getInputField("Budget Required", { disabled: true }),
    myForm.getCurrencyField("Project As Is Value"),
    myForm.getCurrencyField("As Repaired Value", { disabled: true }),
  ]);

  myForm.addGroupFields([
    myForm.getCurrencyField("Estimated Budget", { disabled: true }),
    myForm.getCurrencyField("Actual Budget", { disabled: true }),
    myForm.getInputField("Budget Status", { disabled: true }),
    myForm.getCurrencyField("Land Cost/Value", { name: "landCost", disabled: true }),
  ]);

  myForm.addGroupFields([
    myForm.getCurrencyField("Land Finance Amount", { disabled: true }),
    myForm.getCurrencyField("Subordinate Financing", { name: "subordinateFinancingAmount", disabled: true }),
    myForm.getCurrencyField("Down Payment", { disabled: true }),
    myForm.getTextareaField("Down Payment Source", { name: "sourceOfDownPayment" }),
  ]);

  myForm.addGroupFields([
    myForm.getInputField("Profit Margin %", { name: "profitMargin", fieldType: "percent" }),
    myForm.getCurrencyField("Out Of Pocket", { disabled: true }),
    myForm.getCheckboxField("Permits Available", { disabled: true }),
    myForm.getCheckboxField("Plans Available", { disabled: true }),
  ]);

  myForm.addGroupFields([
    myForm.getCheckboxField("Specifications Available",{ disabled: true }),
    myForm.getCheckboxField("Horizontal Development"),
    myForm.getInputField("BLA/Subdivision/Lot Confirmation", { name: "blasubdivisionLotConfirmation", field: "checkbox" }),
    myForm.getCheckboxField("Zoning Change"),
  ]);

  myForm.addGroupFields([
    myForm.getCheckboxField("Demolition"),
    myForm.getCheckboxField("Vertical Construction"),
    myForm.getCheckboxField("Stacked Condo"),
  ], { widths: 4 });
  myForm.addFormInputField("Other Parking Spaces", { width: 12, name: "parkingSpaces" });
  myForm.addFormInputField("View", { width: 12, name: "projectView" });
  myForm.addFormInputField("Building Style", { width: 12 });
  myForm.addGroupFields([
    myForm.getDropdownField("Are there district reimbursements available?", "metadata/list-borrower-application-inputs", {
      width: 5,
      name: "districtReimbursementsAvailableBorrowerApplicationInputId"
    }),
    myForm.getTextareaField("Please describe available district reimbursements", { name: "districtReimbursementsAvailableDescription", width: 11 }),
  ], { widths: 1 });
  myForm.addGroupFields([
    myForm.getDropdownField("Are there bonding requirements?", "metadata/list-borrower-application-inputs", { width: 5, name: "bondingRequirementsBorrowerApplicationInputId" }),
    myForm.getInputField("Please explain bonding requirements", { field: "textarea", width: 11, name: "bondingRequirementsDescription" }),
  ], { widths: 1 });
  myForm.addGroupFields([
    myForm.getDropdownField("Are there seasonality concerns?", "metadata/list-borrower-application-inputs", { width: 5, name: "seasonalityConcernsBorrowerApplicationInputId" }),
    myForm.getInputField("Please explain seasonality concerns", { field: "textarea", width: 11, name: "seasonailtyConcernsDescription" }),
  ], { widths: 1 });

  myForm.addGroupFields([
    myForm.getNumberField("How many months after closing will development begin?", { name: "developmentBeginsAfterClosingInMonths" }),
    myForm.getInputField("What is the size of the current property?", { name: "sizeOfCurrentProperty" }),
  ]);

  myForm.addGroupFields([
    myForm.getInputField("What is the size of the proposed lots?", { name: "sizeOfProposedLot" }),
    myForm.getNumberField("How many proposed lots for the subject loan?", { name: "noOfProposedLots" }),
  ]);

  myForm.addGroupFields([
    myForm.getNumberField("Time Frame to develop (months)?", { name: "timeFrameToDevelopInMonths" }),
    myForm.getCurrencyField("What is the horizontal development budget?", { name: "horizontalDevelopmentBudget" }),
    myForm.getCurrencyField("Cash Out Amount Requested")
  ]);

  return (
    <div className="loan-details-terms-container">
      <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={["edit", "save", "save-close", "close"]} hideBottomActionsOnEdit />
    </div>
  );
};

const LmsLoanDetailsProposal = ({ loanId, loanDetailsId, loanDetails }) => {
  const isServicingPhase = loanDetails?.loanPhase === "Servicing";
  const isPaidOffPhase = loanDetails?.loanPhase === "Paid Off";
  const isApplicationStatus = loanDetails?.loanStatus === "Application";
  const isSubmittedToUwStatus = loanDetails?.loanStatus === "Submitted to UW";
  const isUnderwritingStatus = loanDetails?.loanStatus === "Underwriting";
  const isLRCVotingStatus = loanDetails?.loanStatus === "LRC Voting";
  const isProcessingStatus = loanDetails?.loanStatus === "Processing";
  const isPreclosingStatus = loanDetails?.loanStatus === "Preclosing";
  const isClosingStatus = loanDetails?.loanStatus === "Closing";
  const isPreFundingStatus = loanDetails?.loanStatus === "Pre-Funding";
  const isFundingStatus = loanDetails?.loanStatus === "Funding";
  const isActiveStatus = loanDetails?.loanStatus === "Active";
  const isInactiveStatus = loanDetails?.loanStatus === "Inactive";
  const isLrcRestructureStatus = loanDetails?.loanStatus === "LRC Restructure";
  const isActiveSubStatus = loanDetails?.loanSubStatus === "Active";
  const isDirectUwSubStatus = loanDetails?.loanSubStatus === "Direct UW";
  const isFullUwSubStatus = loanDetails?.loanSubStatus === "Full UW";
  const isApprovedSubStatus = loanDetails?.loanSubStatus === "Approved";
  const isDocsSubStatus = loanDetails?.loanSubStatus === "Docs";
  const isDocsoutSubStatus = loanDetails?.loanSubStatus === "Docsout";
  const isCoSubStatus = loanDetails?.loanSubStatus === "CO";
  const isRevolverMaster = loanDetails?.loanProgram?.toLowerCase().startsWith("revolver");
  const isApplicationActive = (isApplicationStatus && isActiveSubStatus);
  const isSubmittedToUWActive = (isSubmittedToUwStatus && isActiveSubStatus);
  const isUnderwritingActive = (isUnderwritingStatus && isActiveSubStatus);
  const isLrcVotingDirectUw = (isLRCVotingStatus && isDirectUwSubStatus);
  const isLrcVotingFullUw = (isLRCVotingStatus && isFullUwSubStatus);
  const isProcessingApproved = (isProcessingStatus && isApprovedSubStatus);
  const isPreclosingActive = (isPreclosingStatus && isActiveSubStatus);
  const isClosingDocs = (isClosingStatus && isDocsSubStatus);
  const isPreFundingDocsout = (isPreFundingStatus && isDocsoutSubStatus);
  const isFundingActive = (isFundingStatus && isActiveSubStatus);
  const isServicingActive = (isServicingPhase && isActiveStatus);
  const isPaidOffActive = (isPaidOffPhase && isActiveStatus);
  const isPaidOffInactive = (isPaidOffPhase && isInactiveStatus);
  const isLrcRestructureCo = (isLrcRestructureStatus && isCoSubStatus);
  const isStatusGroup1 = [isApplicationActive, isSubmittedToUWActive].some(status => status);
  const isStatusGroup2 = [isUnderwritingActive].some(status => status);
  const isStatusGroup3 = [
    isLrcVotingDirectUw, 
    isLrcVotingFullUw, 
    isProcessingApproved, 
    isPreclosingActive, 
    isClosingDocs,
    isPreFundingDocsout,
    isFundingActive
  ].some(status => status);

  const isStatusGroup4 = [
    isServicingActive, 
    isPaidOffActive, 
    isPaidOffInactive
  ].some(status => status);

  const isStatusGroup5 = [
    isLrcRestructureCo
  ].some(status => status);

  const conditionsSuffix = `loandetails/conditions/${loanId}`;
  const [isEditMode, setIsEditMode] = useState(false);
  const [isProformaMismatch, setIsProformaMismatch] = useState(false);
  const [{ data: listUnderwritingDispositionData, isLoading: isUnderwritingMetadataLoading }] = useFetchData(loanId ? "loandetails/list-underwriting-disposition" : "");
  const [{ data, isLoading: isDataLoading, error, fetchData }] = useFetchData((listUnderwritingDispositionData && loanId) ? `loandetails/proposal/${loanId}` : "");
  const isLoading = isUnderwritingMetadataLoading || isDataLoading;
  let underwritingDispositionMetadata = listUnderwritingDispositionData;
  const underwritingDispositionApprovedId = "4f93f861-c823-4aef-bf1f-a6a500eae2ea";
  const underwritingDispositionApprovedResubmitId = "e8d3cd77-1abf-4bc4-9182-a8c500aaf9b1";
  if (isStatusGroup2 || isStatusGroup3 || isStatusGroup4) {
    underwritingDispositionMetadata = underwritingDispositionMetadata?.filter(disposition => {
      return disposition.name === "Approved";
    });
  }
  else if (isStatusGroup5) {
    underwritingDispositionMetadata = underwritingDispositionMetadata?.filter(disposition => {
      return disposition.name === "Approved - Resubmit to LRC";
    });
  }

  const handleNextLoanStatusOptionChange = async (options, lastUpdated) => {
    const inputs = options?.inputs;
    if (lastUpdated === "nextLoanStatusOption") {
      const isProformaMismatchData = await fetchData(`loandetails/proposal/${loanId}/proforma-mismatch?loanId=${loanId}&ltvpercentage=${options?.inputs?.ltvpercentage}&cltvpercentage=${options?.inputs?.cltvpercentage}&ltcpercentage=${options?.inputs?.ltcpercentage}`);
      setIsProformaMismatch(isProformaMismatchData);
    }
    
    if (lastUpdated === "approvedLiquidityPercentage"){
      const approvedLiquidityPercentage = parseFloat(inputs?.approvedLiquidityPercentage || 0);
      if (approvedLiquidityPercentage > 999.99) {
        inputs.approvedLiquidityPercentage = 999.99;
      }

      if (approvedLiquidityPercentage === parseFloat(inputs?.currentLiquidity || 0)) {
        inputs.isHideLiquidityPercentageChangeReason = true;
        inputs.LiquidityPercentChangeReason = "";
      }
      else {
        inputs.isHideLiquidityPercentageChangeReason = false;
      }
    }
  };
  
  const myForm = new BCFormController(data, { 
    values: { 
      loanId, 
      isHideLiquidityPercentageChangeReason: !data?.liquidityPercentageChangeReason,
      readOnlyUnderwritingDisposition: data?.readOnlyUnderwritingDisposition,
      changeLiquidityPercentage: data?.changeLiquidityPercentage,
      disabledLrcSubmissionType: !(data?.underwritingDispositionId !== underwritingDispositionApprovedResubmitId && data?.underwritingDispositionId !== underwritingDispositionApprovedId)
    }, 
    initIsDisabled: true, 
    submitSuffix: "loandetails/loan-proposal", 
    exitEditOnClose: true,
    allDropdownOptionsAreReady: (isLoading === false && underwritingDispositionMetadata?.length > 0)
  });

  myForm.addGroupFields([
    myForm.getCalendarField("Final Review Date", { name: "underwritingSubmissionDate", disabled: true }),
    myForm.getDropdownField("Analyst Recommendation", getDropdownSemanticOptionsFromData(underwritingDispositionMetadata), { 
      name: "underwritingDispositionId", 
      isDisabledOnFunc: ({ inputs }) => ((inputs?.readOnlyUnderwritingDisposition === true) || isStatusGroup1 || isStatusGroup4)
    }),
    myForm.getDropdownField("Loan Owner", "loans/list-loan-owners", { name: "ownerOrganizationId", required: true, disabled: true }),
    myForm.getCalendarField("LRC Submission Date", { name: "underwritingDecisionDate", disabled: true }),
  ]);

  myForm.addGroupFields([
    myForm.getDropdownField("LRC Submission Type", "metadata/list-lrc-submission-types", { 
      name: "lrcsubmissionTypeId", 
      required: true, 
      width: "4", 
      isDisabledOn: { disabledLrcSubmissionType: true }
    }),
    myForm.getLabelField("Next Loan Status", { required: true, width: "2" }),
    myForm.getGroupRadioField("Loan Committee Voting"),
  ], {
    radioGroupName: "nextLoanStatusOption", 
    radioGroupValue: data?.nextLoanStatus,
    isHiddenFunc: ({ inputs, isDisabled }) => !data || !(inputs?.underwritingDispositionId === underwritingDispositionApprovedId  && !isDisabled) || !!inputs?.underwritingDispositionId
  });

  myForm.addGroupFields([
    myForm.getTextareaField("Please explain why this loan is being submitted without the proforma and the LMS fields being in sync", { name: "proformaComments", required: true }), // TODO: Figure out name
  ], {
    isHiddenFunc: ({ inputs }) => !data || !(inputs.nextLoanStatusOption === "Loan Committee Voting" && isProformaMismatch)
  });

  myForm.addGroupFields([
    myForm.getDropdownField("LRC Submission Type", "metadata/list-lrc-submission-types", { 
      name: "lrcsubmissionTypeId", 
      required: true, 
      width: "4", 
      isDisabledOn: { disabledLrcSubmissionType: true },
      isHiddenFunc: ({ inputs }) => ( inputs?.underwritingDispositionId !== underwritingDispositionApprovedId )
    }),
    myForm.getTextareaField("Comments", { 
      name: "comments", 
      required: true
    })
  ], { isHiddenFunc: ({ isDisabled, inputs }) => !data || isDisabled || inputs?.underwritingDispositionId !== underwritingDispositionApprovedResubmitId });

  myForm.addGroupFields([
    myForm.getCalendarField("Analyst Suspense Date", { name: "underwritingSuspenseDate", disabled: true }),
    myForm.getCalendarField("Loan Review Approval Date", { name: "underwritingApprovalDate", disabled: true }),
    myForm.getCalendarField("Fund Approval Date", { disabled: true }),
    myForm.getInputField("Current Liquidity", { fieldType: "float", disabled: true }),
  ]);

  myForm.addGroupFields([
    myForm.getInputField("Underwritten Global LTC", { fieldType: "float", name: "day1GlobalLtc", disabled: true, isHiddenFunc: () => isRevolverMaster }),
    myForm.getInputField("Original Projected IRR", { fieldType: "float", name: "projectedIrroriginal", disabled: isStatusGroup4 }),
    myForm.getInputField("Approved Liquidity Percentage", { fieldType: "float", required: (isStatusGroup3 || isStatusGroup4), isDisabledOn: { changeLiquidityPercentage: false } }),
    myForm.getInputField("Current Global LTC", { fieldType: "float", name: "globalLtcpercentage", disabled: true, isHiddenFunc: () => isRevolverMaster }),
    myForm.getInputField("Global LTC", { fieldType: "float", name: "globalLtc", disabled: true, isHiddenFunc: () => !isRevolverMaster }),
    myForm.getBlankField({ isHiddenFunc: () => !isRevolverMaster })
  ]);

  myForm.addGroupFields([
    myForm.getInputField("Liquidity Percent Change Reason", { required: true, isDisabledOn: { changeLiquidityPercentage: false } }),
  ], {
    isHiddenFunc: ({ inputs }) => !data || inputs?.isHideLiquidityPercentageChangeReason
  });

  myForm.addGroupFields([
    myForm.getInputField("Approved Revolver LTV", { fieldType: "float", name: "approvedRevolverLtv", disabled: true }),
    myForm.getInputField("Approved Dev LTV", { fieldType: "float", name: "approvedDevLtv", disabled: true }),
    myForm.getInputField("Approved Lot LTV", { fieldType: "float", name: "approvedLotLtv", disabled: true }),
    myForm.getInputField("Approved Vertical LTV", { fieldType: "float", name: "approvedVerticalLtv", disabled: true }),
  ], { isHiddenFunc: () => !isRevolverMaster });

  myForm.addGroupFields([
    myForm.getInputField("Underwritten LTV", { fieldType: "float", name: "day1Ltv", disabled: true }),
    myForm.getInputField("Underwritten CLTV", { fieldType: "float", name: "day1Cltv", disabled: true }),
    myForm.getInputField("Underwritten LTC", { fieldType: "float", name: "day1Ltc", disabled: true }),
    myForm.getBlankField()
  ], { isHiddenFunc: () => isRevolverMaster });

  myForm.addGroupFields([
    myForm.getInputField("Current LTV", { fieldType: "float", name: "ltvpercentage", disabled: true, width: 4 }),
    myForm.getInputField("Current CLTV", { fieldType: "float",  name: "cltvpercentage", disabled: true, width: 4 }),
    myForm.getInputField("Current LTC", { fieldType: "float", name: "ltcpercentage", disabled: true, width: 4 }),
    myForm.getInputField("Guaranty Option", {
      name:"guarantorList",
      disabled: true,
      isHiddenFunc: ({ inputs, isDisabled }) => !inputs?.guarantorList || !isDisabled,
    }),
  ], { widths: 4 });

  myForm.addGroupFields([
    myForm.getInputField("BC Exposure Number Loans", { name:"bcexposureNumberLoans", fieldType: "int", disabled: isStatusGroup4 }),
    myForm.getInputField("BC Exposure Amount", { name: "bcexposureAmount", fieldType: "dollar", disabled: isStatusGroup4 }),
    myForm.getNumberField("# of Simultaneous Loans", { name: "noSimultaneousLoans", disabled: isStatusGroup4 }),
    myForm.getInputField("Cost By Square Footage", { disabled: true }),
  ]);

  myForm.addGroupFields([
    myForm.getTextareaField("LRC Overview", { width: 8, name: "lrcoverview", disabled: isStatusGroup4 }),
    myForm.getTextareaField("Transaction Narrative", { width: 8, name: "projectNarrative", disabled: isStatusGroup4, isHiddenFunc: () => isRevolverMaster }),
  ], { widths: 4 });
  myForm.addGroupFields([
    myForm.getTextareaField("Borrowing Entity Narrative", { name: "borrowerNarrative", width: 8, disabled: isStatusGroup4 }),
    myForm.getTextareaField("Property Narrative", { width: 8, disabled: isStatusGroup4, isHiddenFunc: () => isRevolverMaster }),
  ], { widths: 4 });
  myForm.addGroupFields([
    myForm.getTextareaField("Market Narrative", { width: 8, disabled: isStatusGroup4, isHiddenFunc: () => isRevolverMaster }),
    myForm.getTextareaField("Analyst Narrative", { name: "underwriterNarrative", width: 8, disabled: isStatusGroup4 }),
  ], { widths: 4 });

  const onEditCloseClick = (options) =>{
    const inputs = options?.inputs;
    setIsEditMode(options.isDisabled);
    if (!inputs.approvedLiquidityPercentage) {
      inputs.approvedLiquidityPercentage = inputs?.currentLiquidity || 0;
    }
  };

  const formTopActions = [
    "edit",
    { action: "download", text: "Generate Loan Proposal", path: `loandetails/proposal/${loanId}/generate-loan-proposal`, isHidden: () => isEditMode },
    { action: "download", text: "Generate Global Loan Proposal", path: `loandetails/proposal/${loanId}/generate-global-loan-proposal`, isHidden: () => isEditMode },
    { action: "download", text: "Generate U/W Application", path: `loandetails/proposal/${loanId}/generate-uw-application`, isHidden: () => isEditMode },
  ];

  const formActions = [
    ...(isPaidOffPhase || isServicingPhase) ? [] : ["save", "save-close"],
    { action: "download", text: "Generate Loan Proposal", path: `loandetails/proposal/${loanId}/generate-loan-proposal`, isHidden: () => !(isPaidOffPhase || isServicingPhase)},
    { action: "download", text: "Generate Global Loan Proposal", path: `loandetails/proposal/${loanId}/generate-global-loan-proposal`, isHidden: () => !(isPaidOffPhase || isServicingPhase)},
    { action: "download", text: "Generate U/W Application", path: `loandetails/proposal/${loanId}/generate-uw-application`, isHidden: () => !(isPaidOffPhase || isServicingPhase) },
    "close",
  ];

  return (
    <div className="loan-details-terms-container">
      <LmsFormGenerator
        myForm={myForm}
        isLoading={isLoading}
        error={error}
        actions={formActions}
        topActions={formTopActions}
        hideTopActionsOnEdit
        onEditClick={onEditCloseClick}
        onCloseClick={(options) => setIsEditMode(options.isDisabled)}
        onFormChanged={handleNextLoanStatusOptionChange}
      />
      {!isEditMode && <LmsLoanConditionsExportOnlyTable suffix={conditionsSuffix} title="Conditions" />}
      {isEditMode && <LmsLoanApplicationConditionsTable suffix={conditionsSuffix} title="Conditions" source="Underwriting" loanDetailsId={loanDetailsId} />}
    </div>
  );
};

const ConfirmSendSummaryForm = ({ id = "", setIsSummaryConfirmationOpen }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`loandetails/loan-closing/${id}/slt-recipients`);
  const formatData = (formData) => {
    const selectedGuarantors = [] as string[];
    data?.guarantors?.map(gu => {
      if (gu.id && formData[gu.id]) {
        selectedGuarantors.push(gu.id);
      }
    });

    return { guarantorIds: selectedGuarantors, id };
  };

  const myForm = new BCFormController(data, { submitSuffix: "loandetails/loan-closing/send-summary-of-loan-terms", dataFormatterFunc: formatData });
  myForm.addCenteredGroup([myForm.getInputField("Credit Officer")]);
  myForm.addCenteredGroup([myForm.getInputField("Loan Officer")]);
  myForm.addCenteredGroup([myForm.getInputField("Broker")]);
  myForm.addCenteredGroup([myForm.getLabelField("Guarantor(s)")]);

  if (data?.guarantors) {
    data?.guarantors.map(gu => {
      myForm.addCenteredGroup([myForm.getCheckboxField("", { value: true, name: gu.id, checkboxText: gu.display, isCompact: true })]);
    });
  }

  const actions = [{ action: "save", text: "Confirm & Send" }, "close"];
  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={actions} handleClose={() => setIsSummaryConfirmationOpen(false)}/>;
};

const LmsLoanDetailsClosing = ({ id, loanId, loanDetailsId, loanDetails }) => {
  const conditionsSuffix = `loandetails/conditions/${loanId}`;
  const isProcessingStatus = loanDetails?.loanStatus === "Processing";
  const isApprovedSubStatus = loanDetails?.loanSubStatus === "Approved";
  const isProcessingApprovedLabel = (isProcessingStatus && isApprovedSubStatus) ? "red" : undefined;
  const isApplicationStatus = loanDetails?.loanStatus === "Application";
  const isActiveSubStatus = loanDetails?.loanSubStatus === "Active";
  const isApplicationActive = (isApplicationStatus && isActiveSubStatus);
  const isApplicationActiveLabel = isApplicationActive ? "red" : undefined;
  const isServicingPhase = loanDetails?.loanPhase === "Servicing";
  const isActiveStatus = loanDetails?.loanStatus === "Active";
  const isServicingActive = (isServicingPhase && isActiveStatus);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSummaryConfirmationOpen, setIsSummaryConfirmationOpen] = useState(false);
  const [{ data = {}, isLoading, error, fetchData }, reFetchStatusHistory] = useFetchData(`loandetails/loan-closing/${id}`);
  let wipPaidToMetadata = WipPaidToOptions;
  if (!data?.wipatClosing) {
    wipPaidToMetadata = wipPaidToMetadata.filter(option => {
      return option.key !== "N/A";
    });
  }
  else {
    wipPaidToMetadata = wipPaidToMetadata.filter(option => {
      return option.key === "N/A";
    });
  }

  const myForm = new BCFormController(data, {
    values: { showReportButtons: data.showReportButtons, loanTerm: data?.loanTerm },
    excludeFromSubmit: ["showReportButtons"],
    initIsDisabled: true,
    submitSuffix: "loandetails/loan-closing",
    clearSuffixesOnSubmit: [`loandetails/loan-closing/${id}`],
    exitEditOnClose: true,
    onSubmitSuccess: () => { reFetchStatusHistory(); }
  });

  myForm.addGroupFields([
    myForm.getCalendarField("Est. Closing Date", { name: "estimatedClosingDate", disabled: isServicingActive }),
    myForm.getCalendarField("Recording Date", { name: "actualClosingDate", maxDate: 0 }),
    myForm.getCalendarField("Maturity Date Original", { disabled: true }),
    myForm.getCalendarField("Note Date", { disabled: isServicingActive }),
  ]);

  myForm.addGroupFields([
    myForm.getCalendarField("Release To Record Date", { disabled: isServicingActive }),
    isApplicationActive ? myForm.getLabelField("Fund", { name: "closingFundOrganizationAbbreviation" }) : myForm.getInputField("Fund", { name: "closingFundOrganizationAbbreviation", disabled: isServicingActive }),
    myForm.getDropdownField("Closing Doc Lender Name", "metadata/list-lender-orgs", { name: "lenderOrganizationId", disabled: isServicingActive }),
    myForm.getCalendarField("Estimated 1st Draw", { name: "estimated1DrawDate" }),
  ]);

  myForm.addGroupFields([
    myForm.getCalendarField("SLT Sent On", { name: "sltsentOn", disabled: true }),
    myForm.getCalendarField("SLT Completed On", { name: "sltcompletedOn", disabled: true }),
    myForm.getCurrencyField("Wire Amount", { disabled: true }),
    myForm.getCalendarField("Estimated Payoff Date")
  ]);

  myForm.addGroupFields([
    myForm.getCalendarField("Wire Date", { disabled: true }),
    myForm.getCalendarField("Estimated Construction Completion", { name: "estimatedConstructionCompletionDate" }),
    myForm.getInputField("Title #", { name: "titleNumber", labelColor: isProcessingApprovedLabel }),
    myForm.getInputField("DOT Recording #", { name: "recordingNumber" }),
  ]);

  myForm.addGroupFields([
    myForm.getCheckboxField("Broker In", { width: "4", disabled: true }),
    myForm.getCheckboxField("Broker Out", { width: "4", disabled: true }),
    myForm.getCheckboxField("Common Legal Description", { width: "4", disabled: isServicingActive }),
  ]);

  myForm.addGroupFields([
    myForm.getInputField("Escrow #", { name: "escrowNumber", width: 4, labelColor: isProcessingApprovedLabel }),
    myForm.getTextareaField("Legal Description", { width: 12, disabled: isServicingActive }),
  ]);

  myForm.addGroupFields([
    myForm.getCheckboxField("LLC Assignment Required", { name: "llcassignment", width: 4, disabled: isServicingActive }),
    myForm.getTextareaField("Legal Description Abbreviated", { width: 12, disabled: isServicingActive }),
  ]);

  myForm.addGroupFields([
    myForm.getCurrencyField("Land Financed", { name: "landFinanceAmount", width: 4, disabled: true }),
    myForm.getTextareaField("Sweep Provision", { width: 12, disabled: isServicingActive }),
  ]);

  myForm.addGroupFields([
    myForm.getInputField("WIP At Closing", { name: "wipatClosing", width: 4, disabled: true }),
    myForm.getInputField("Additional Collateral Provision", { width: 12, disabled: isServicingActive }),
  ]);

  myForm.addGroupFields([
    myForm.getDropdownField("WIP Paid To", wipPaidToMetadata, { name: "wippaidToOption", width: 4, disabled: true }),
    myForm.getTextareaField("Partial Deed Release Provision", { width: 12, disabled: isServicingActive }),
  ]);

  myForm.addGroupFields([
    myForm.getCurrencyField("Remaining Budget", { width: 4, disabled: true }),
    myForm.getCurrencyField("Origination Fee", { width: 4, disabled: true }),
    myForm.getCurrencyField("Original Int Reserve Amount", { name: "interestReserveOriginal", width: 4, disabled: true }),
    myForm.getCurrencyField("Remaining Fees", { width: 4, disabled: true }),
  ]);

  myForm.addGroupFields([
    myForm.getCurrencyField("Draw Administration Fee", { width: 4, disabled: true }),
    myForm.getInputField("Exit Fee %", { name: "exitFeePercentage", fieldType: "percent", width: 4, disabled: isServicingActive }),
    myForm.getCurrencyField("Calculated Loan Amount", { name: "loanAmountCalculated", width: 4, disabled: true, labelColor: isApplicationActiveLabel }),
    myForm.getCurrencyField("Original Loan Amount", { name: "loanAmountOriginal", width: 4, disabled: true }),
  ], { widths: 4 });

  myForm.addGroupFields([
    myForm.getCurrencyField("Difference of Original and Calculated", { name: "loanAmountBalance", width: 4, disabled: true, labelColor: isApplicationActiveLabel })
  ], { widths: 4 });

  const onEditCloseClick = (options) =>{
    setIsEditMode(options.isDisabled);
  };

  const handleInternalSettlementDownload = async (handleDownloadFile, options) => {
    const { data: internalSettlementId } = await fetchData(`loandetails/loan-closing/${id}/generate-internal-settlement-statement/pdf`, { displayError: true });
    if (internalSettlementId) {
      await handleDownloadFile(`accounting/download-doc/${internalSettlementId}`);
    }

    options.resetSubmittingStates(true);
  };

  const handleClosingInstructionDownload = async (handleDownloadFile, options) => {
    const { data: closingInstructionId } = await fetchData(`loandetails/loan-closing/${id}/generate-closing-instructions/pdf`, { displayError: true });
    if (closingInstructionId) {
      await handleDownloadFile(`accounting/download-doc/${closingInstructionId}`);
    }

    options.resetSubmittingStates(true);
  };

  // TODO: Put permission logic. Anyone who is Edit and has the permisson "Loan_Closing"
  const isShowReportButtons = (isDisabled, options) => {
    if (isDisabled) return true;
    if (options?.inputs?.showReportButtons) return false;
    return true;
  };

  const onFormChanged = async ({ inputs, setInputs }, lastUpdated) => {
    if (lastUpdated === "noteDate") {
      const noteDate = new Date(inputs?.noteDate);
      const maturityDateOriginal = new Date(noteDate.getFullYear() ,noteDate.getMonth(), noteDate.getDate() + (inputs?.loanTerm * 30));
      setInputs({ ...inputs, maturityDateOriginal: maturityDateOriginal.toISOString().substring(0,10) });
    }
  };

  const topFormActions = [
    "edit",
    { text: "Send Summary Of Loan Terms", icon: GlobalConstants.Icons.CARET_UP, handleClick: () => setIsSummaryConfirmationOpen(true), isHidden: (disabled) => !disabled || isEditMode, isDisabled: () => data?.showReportButtons === false },
    { action: "download", text: "Summary Of Loan Terms", path: `loandetails/loan-closing/${id}/generate-summary-of-loan-terms`, method: "POST", isHidden: () => isEditMode, isDisabled: () => data?.showReportButtons === false },
    { text: "Internal Settlement Statement", handleDownload: handleInternalSettlementDownload, icon: GlobalConstants.Icons.CARET_UP, isHidden: () => isEditMode, isDisabled: () => data?.showReportButtons === false },
    { text: "Closing Instruction", handleDownload: handleClosingInstructionDownload, icon: GlobalConstants.Icons.CARET_UP, isHidden: () => isEditMode, isDisabled: () => data?.showReportButtons === false }
  ] as any;

  const formActions = [
    "save",
    "save-close",
    { action: "download", text: "Summary Of Loan Terms", path: `loandetails/loan-closing/${id}/generate-summary-of-loan-terms`, method: "POST", isHidden: () => !isEditMode, isDisabled: (isDisabled, options) => isShowReportButtons(isDisabled, options) },
    { text: "Internal Settlement Statement", handleDownload: handleInternalSettlementDownload, icon: GlobalConstants.Icons.CARET_UP, isHidden: () => !isEditMode, isDisabled: (isDisabled, options) => isShowReportButtons(isDisabled, options) },
    { text: "Closing Instruction", handleDownload: handleClosingInstructionDownload, icon: GlobalConstants.Icons.CARET_UP, isHidden: () => !isEditMode, isDisabled: (isDisabled, options) => isShowReportButtons(isDisabled, options) },
    { action: "close", isHidden: (disabled) => disabled }
  ] as any;

  return (
    <div className="loan-details-terms-container">
      <LmsFormGenerator
        myForm={myForm}
        isLoading={isLoading}
        error={error}
        actions={formActions}
        topActions={topFormActions}
        onEditClick={onEditCloseClick}
        onCloseClick={onEditCloseClick}
        onFormChanged={onFormChanged}
      />
      {!isEditMode && <LmsLoanConditionsExportOnlyTable suffix={conditionsSuffix} title="Conditions" />}
      {isEditMode && <LmsLoanApplicationConditionsTable suffix={conditionsSuffix} title="Conditions" source="Closing" loanDetailsId={loanDetailsId}/>}
      <BCModal
        isModalOpen={isSummaryConfirmationOpen}
        size="large"
        closeIcon
        onClose={() => setIsSummaryConfirmationOpen(false)}
        content={<ConfirmSendSummaryForm id={id} setIsSummaryConfirmationOpen={setIsSummaryConfirmationOpen}/>}
      />
    </div>
  );
};

const workInspectionCompanyFields = (myForm) => {
  myForm.addCenteredGroup([myForm.getDropdownDisplayField("Loan", { name: "loanDisplay" })]);
  myForm.addCenteredGroup([myForm.getDropdownDisplayField("Borrower", { name: "borrowerOrganizationDisplay" })]);
  myForm.addCenteredGroup([myForm.getDropdownDisplayField("Special Purpose Entity", { name: "specialPurposeEntityDisplay" })]);
  myForm.addCenteredGroup([myForm.getDisplayField("Current Work Inspection Company")]);
  myForm.addCenteredGroup([myForm.getCalendarField("Effective Date", { required: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Work Inspection Company Organization", "loandetails/list-work-inspection-company-orgs", { required: true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Description", { required: true })]);
  return myForm;
};

const AddWorkInspectionCompanyHistoryForm = ({ setIsAddFormModalOpen, loanId }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(loanId ? `loandetails/work-inspection-company-history/${loanId}/initial-data` : "");
  const myForm = workInspectionCompanyFields(new BCFormController({ 
    ...data,
    effectiveDate: "",
    workInspectionCompanyOrganizationId: ""
   }, {
    values: { loanId: loanId },
    submitSuffix: "loandetails/work-inspection-company-history",
    submitMethod: "POST",
    clearSuffixesOnSubmit: [`loandetails/work-inspection-company-histories/${loanId}`],
  }));

  return (
    <LmsFormGenerator
      isLoading={isLoading}
      error={error}
      myForm={myForm}
      handleClose={() => setIsAddFormModalOpen(false)}
      actions={["save-new", "save-close", "close"]}
    />
  );
};

const EditWorkInspectionCompanyHistoryForm = ({ historyId, setIsEditFormModalOpen, loanId = "" }) => {
  const workInspectionSuffix = `loandetails/work-inspection-company-history/${historyId}`;
  const [{ data = {}, isLoading, error }] = useFetchData(workInspectionSuffix);
  const myForm = workInspectionCompanyFields(new BCFormController({ ...data }, {
    values: { loanId: loanId },
    submitSuffix: "loandetails/work-inspection-company-history",
    submitMethod: "PUT",
    deleteSuffix: "loandetails/work-inspection-company-histories",
    clearSuffixesOnSubmit: [workInspectionSuffix, `loandetails/work-inspection-company-histories/${loanId}`],
  }));

  return (
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      error={error}
      handleClose={() => setIsEditFormModalOpen(false)}
      actions={["save", "save-close", "delete", "close"]}
    />
  );
};

const WorkInspectionCompanyHistoryModal = ({ suffix, servicingData }) => {
  const [isAddInspectionCompanyHistoryOpen, setIsAddInspectionCompanyHistoryOpen] = useState(false);
  const [isEditInspectionCompanyHistoryOpen, setIsEditInspectionCompanyHistoryOpen] = useState(false);
  const [inspectionCompanyHistoryId, setInspectionCompanyHistoryId] = useState(false);
  const actionButtons = [
  { text: "Add", handleClick: () => setIsAddInspectionCompanyHistoryOpen(true) },
  { text: "Edit", handleClick: (options) => { setInspectionCompanyHistoryId(options.id); setIsEditInspectionCompanyHistoryOpen(true); } },
    "remove", 
    "export"
  ];

  return <>
    <BCModal
      isModalOpen={isAddInspectionCompanyHistoryOpen}
      size="large"
      closeIcon
      onClose={() => setIsAddInspectionCompanyHistoryOpen(false)}
      content={<AddWorkInspectionCompanyHistoryForm
        loanId={servicingData?.loanId}
        setIsAddFormModalOpen={setIsAddInspectionCompanyHistoryOpen}
      />}
    />
    <BCModal
      isModalOpen={isEditInspectionCompanyHistoryOpen && inspectionCompanyHistoryId}
      size="large"
      closeIcon
      onClose={() => setIsEditInspectionCompanyHistoryOpen(false)}
      content={<EditWorkInspectionCompanyHistoryForm
        historyId={inspectionCompanyHistoryId}
        setIsEditFormModalOpen={setIsEditInspectionCompanyHistoryOpen}
        loanId={servicingData?.loanId}
      />}
    />
    {(!isAddInspectionCompanyHistoryOpen && !isEditInspectionCompanyHistoryOpen) && <LmsTable
      apiSuffix={suffix}
      title="Loan Work Inspection Company History"
      deleteSuffix="loandetails/work-inspection-company-histories"
      actionButtons={actionButtons}
      handleClick={(options) => {
        setInspectionCompanyHistoryId(options.id);
        setIsEditInspectionCompanyHistoryOpen(true);
      }}
    />}
  </>;
};

const AddServiceFeeHistoryForm = ({ setIsAddFormModalOpen, servicingData, serviceFeeHistoryId = "" }) => {
  const [{ data }] = useFetchData(serviceFeeHistoryId ? `loandetails/service-fee-history/${serviceFeeHistoryId}` : "");
  const { loanName, borrowerOrganizationDisplay, loanId } = servicingData;
  const myForm = new BCFormController(serviceFeeHistoryId ? { ...data } : { loanName, borrowerOrganizationDisplay }, {
    values: { loanId: loanId },
    submitSuffix: "loandetails/service-fee-history",
    deleteSuffix: "loandetails/service-fee-history",
    clearSuffixesOnSubmit: [`loandetails/service-fee-histories/${loanId}`],
    dataFormatterFunc: (data) => { return { ...data, serviceFeeOption: "Custom" }; },
    initIsDisabled: serviceFeeHistoryId ? true : false
  });

  myForm.addCenteredGroup([myForm.getDropdownField("Loan", "loans/list-loans", { required: true, disabled: true })]);
  myForm.addCenteredGroup([myForm.getDropdownDisplayField("Borrower", { name: "borrowerOrganizationDisplay" })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", { name: "specialPurposeEntityId", dependencyField: "borrowerOrganizationId" })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Service Fee Method", "metadata/list-service-fee-methods", { name: "serviceFeeMethodId", required: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Service Fee Percent", { 
    isHiddenFunc: ({ inputs }) => inputs?.serviceFeeMethodId !== LmsServiceFeeMethod.BASED_ON_LOAN_AMOUNT && inputs?.serviceFeeMethodId !== LmsServiceFeeMethod.BASED_ON_LOAN_OUTSTANDING_AMOUNT,
    required: true
  })]),
  myForm.addCenteredGroup([myForm.getInputField("Service Fee Amount", { 
    isHiddenFunc: ({ inputs }) => inputs?.serviceFeeMethodId !== LmsServiceFeeMethod.FIXED_FEE,
    required: true 
  })]),
  myForm.addCenteredGroup([myForm.getCalendarField("Effective Date", { required: true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Description", { required: true })]);

  return (
    <LmsFormGenerator
      myForm={myForm}
      handleClose={() => setIsAddFormModalOpen(false)}
      actions={["save-new", "save-close", "close"]}
    />
  );
};

const ServiceFeeHistoryModal = ({ suffix, servicingData }) => {
  const [isAddServiceFeeHistoryOpen, setIsAddServiceFeeHistoryOpen] = useState(false);
  const [serviceFeeHistoryId, setServiceFeeHistoryId] = useState("");
  const actionButtons = [
    { text: "Add", handleClick: () => {setIsAddServiceFeeHistoryOpen(true); setServiceFeeHistoryId(""); } },
      "remove", 
      "export"
  ];

  return <>
    <BCModal
      isModalOpen={isAddServiceFeeHistoryOpen}
      size="large"
      closeIcon
      onClose={() => setIsAddServiceFeeHistoryOpen(false)}
      content={<AddServiceFeeHistoryForm
        servicingData={servicingData}
        serviceFeeHistoryId={serviceFeeHistoryId}
        setIsAddFormModalOpen={setIsAddServiceFeeHistoryOpen}
      />}
    />
    {!isAddServiceFeeHistoryOpen && <LmsTable
      apiSuffix={suffix}
      title="Loan Service Fee History"
      deleteSuffix="loandetails/service-fee-histories"
      actionButtons={actionButtons}
      handleClick={(options) => {
        setServiceFeeHistoryId(options.id);
        setIsAddServiceFeeHistoryOpen(true);
      }}
    />}
  </>;
};

const setInterestRateFields = (myForm, isAdd = false) => {
  const changeTypeOptions = [
    getDropdownSemanticOption("Fixed Rate Change"),
    getDropdownSemanticOption("Variable Index Rate Change")
  ];

  myForm.addCenteredGroup([myForm.getDropdownDisplayField("Borrower", { name: "borrowerOrganizationDisplay" })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", { name: "specialPurposeEntityId", dependencyField: "borrowerOrganizationId" })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Loan", "loans/list-loans", { required: true, disabled: true })]);
  myForm.addCenteredGroup([myForm.getDisplayField("Current Interest Rate", { fieldType: "percent", name: "interestRateCurrent", disabled: true })]);
  myForm.addCenteredGroup([myForm.getCalendarField("Effective Date", { required: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Change Type Option", changeTypeOptions, {
    name: "changeTypeOption",
    required: true,
    value: isAdd ? changeTypeOptions[0].value : undefined
  })]);
  myForm.addCenteredGroup([myForm.getInputField("Revised Interest Rate", { 
    fieldType: "percent", 
    name: "interestRate", 
    required: true, 
  })], { isHidden: { changeTypeOption: changeTypeOptions[0].value }, isHiddenOperation: "notequal" });
  myForm.addCenteredGroup([
    myForm.getDisplayField("Variable Index", { name: "variableRateIndex" }),
    myForm.getDisplayField("Variable Index Rate", { name: "baseRate" }),
    myForm.getDisplayField("Variable Interest Rate", { name: "variableInterestRateDisplay" }),
  ], { isHidden: { changeTypeOption: changeTypeOptions[1].value }, isHiddenOperation: "notequal" });
  myForm.addCenteredGroup([
    myForm.getDisplayField("Margin", { name: "spreadRate" }),
    myForm.getDisplayField("Floor", { name: "rateFloor" }),
    myForm.getDisplayField("Ceiling", { name: "rateCeiling" }),
  ], { isHidden: { changeTypeOption: changeTypeOptions[1].value }, isHiddenOperation: "notequal" });
  myForm.addCenteredGroup([myForm.getDropdownField("Change Reason", "metadata/list-loan-interest-change-reasons", {
    name: "interestRateChangeReasonId",
    disabled: !isAdd,
    required: true
  })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Comments", { name: "description", required: true })]);
  return myForm;
};

const interestRateDataFormatter = (data, servicingData) => {
  const currentInterestRate = servicingData?.interestRateCurrent;
  const revisedInterestRate = data?.interestRate;
  const rateChangeAmount = !isNaN(currentInterestRate - revisedInterestRate) ? currentInterestRate - revisedInterestRate : currentInterestRate;
  return {
    rateChangeAmount,
    ...data
  };
};

const AddInterestRateForm = ({ setIsAddFormModalOpen, servicingData, refreshTableData }) => {
  const myForm = setInterestRateFields(new BCFormController(removeProp(servicingData, "id"), {
    values: { loanId: servicingData?.loanId },
    submitSuffix: "loandetails/interest-rate-history",
    dataFormatterFunc: (data) => interestRateDataFormatter(data, servicingData),
    displayMessageLocally: true,
    onSubmitSuccess: () => refreshTableData()
  }), true);

  return <LmsFormGenerator
    myForm={myForm}
    actions={["save-new", "save-close", "close"]}
    handleClose={() => setIsAddFormModalOpen(false)}
  />;
};

const EditInterestRateForm = ({ id, setIsAddFormModalOpen, servicingData, refreshTableData }) => {
  const interestRateSuffix = `loandetails/interest-rate-history/${id}`;
  const [{ data = {}, isLoading, error }] = useFetchData(interestRateSuffix);
  const myForm = setInterestRateFields(new BCFormController({ ...servicingData, ...data }, {
    values: { loanId: servicingData?.loanId },
    submitSuffix: "loandetails/interest-rate-history",
    dataFormatterFunc: (data) => interestRateDataFormatter(data, servicingData),
    displayMessageLocally: true,
    onSubmitSuccess: () => refreshTableData(),
    clearSuffixesOnSubmit: [interestRateSuffix]
  }));

  return (
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      error={error}
      actions={["save-close", "close"]}
      handleClose={() => setIsAddFormModalOpen(false)}
    />
  );
};

const InterestRateHistoryTable = ({ loanId, servicingData }) => {
  const actionButtons: any = [
    {
      action: GlobalConstants.TableDefaultActions.MODAL_BUTTON,
      text: "Add",
      icon: GlobalConstants.Icons.ADD,
      modalContent: (setIsAddFormModalOpen, { refreshTableData }) => (
        <AddInterestRateForm setIsAddFormModalOpen={setIsAddFormModalOpen} servicingData={servicingData} refreshTableData={refreshTableData} />
      ),
      modalSize: "fullscreen",
      isDisabled: () => false
    } as ITableActionCustomButton,
    {
      action: GlobalConstants.TableDefaultActions.MODAL_BUTTON,
      text: "Edit",
      icon: GlobalConstants.Icons.EDIT,
      modalContent: (setIsAddFormModalOpen, { firstCheckedRow, refreshTableData }) => (
        <EditInterestRateForm
          id={firstCheckedRow.id}
          setIsAddFormModalOpen={setIsAddFormModalOpen}
          servicingData={servicingData}
          refreshTableData={refreshTableData}
        />
      ),
      modalSize: "fullscreen"
    } as ITableActionCustomButton,
    "export"
  ];
  return <>
    <LmsTable
      apiSuffix={`loandetails/interest-rate-histories/${loanId}`}
      onClickModalContent={(setIsAddFormModalOpen, { clickedRowData, refreshTableData }) => (
        <EditInterestRateForm
          id={clickedRowData.id}
          setIsAddFormModalOpen={setIsAddFormModalOpen}
          servicingData={servicingData}
          refreshTableData={refreshTableData}
        />
      )}
      actionButtons={actionButtons}
      columnLabelsDictionary={{
        rateCeiling: "Ceiling",
        rateFloor: "Floor",
        spreadRate: "Margin",
        baseRate: "Variable Index Rate",
      }}
      dataRules={[
        { rule: "format", display: "percent", field: "rateChangeAmount" },
        { rule: "format", display: "percent", field: "rateCeiling" },
        { rule: "format", display: "percent", field: "rateFloor" },
        { rule: "format", display: "percent", field: "spreadRate" },
        { rule: "format", display: "percent", field: "baseRate" }
      ]}
    />
  </>;
};

const setCommitmentCapFields = (myForm) => {
  myForm.addCenteredGroup([myForm.getInputField("Loan #", { name: "display", disabled: true, required: true })]);
  myForm.addCenteredGroup([myForm.getCalendarField("Start Date", { required: true })]);
  myForm.addCenteredGroup([myForm.getCalendarField("End Date", { required: true })]);
  myForm.addCenteredGroup([myForm.getCurrencyField("Commitment Cap Amount", { name: "amount", required: true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Comment", { name: "description", required: true })]);
  return myForm;
};

const AddCommitmentCapForm = ({ setIsAddFormModalOpen, servicingData, refreshTableData, loanId }) => {
  const [{ data, isLoading, error }] = useFetchData(loanId ? `loandetails/servicing/commitment-caps/${loanId}` : "");
  const myForm = setCommitmentCapFields(new BCFormController({ ...data, display: servicingData?.loanNumberDisplay }, {
    values: { loanId: servicingData?.loanId },
    submitSuffix: "loandetails/servicing/commitment-cap",
    onSubmitSuccess: refreshTableData,
    displayMessageLocally: true
  }));

  return (
    <LmsFormGenerator
      myForm={myForm}
      actions={["save-new", "save-close", "close"]}
      isLoading={isLoading}
      error={error}
      handleClose={() => setIsAddFormModalOpen(false)}
    />
  );
};

const EditCommitmentCapForm = ({ id, setIsAddFormModalOpen, refreshTableData, servicingData }) => {
  const commitmentCapSuffix = `loandetails/servicing/commitment-cap/${id}`;
  const [{ data = {}, isLoading, error }] = useFetchData(commitmentCapSuffix);
  const myForm = setCommitmentCapFields(new BCFormController({ ...data }, {
    values: { loanId: servicingData?.loanId },
    submitSuffix: "loandetails/servicing/commitment-cap",
    onSubmitSuccess: refreshTableData,
    clearSuffixesOnSubmit: [commitmentCapSuffix],
    displayMessageLocally: true
  }));

  return (
    <LmsFormGenerator
      myForm={myForm}
      actions={["save", "save-close", "close"]}
      isLoading={isLoading}
      error={error}
      handleClose={() => setIsAddFormModalOpen(false)}
    />
  );
};

const CommitmentCapTable = ({ loanId, servicingData }) => {
  const actionButtons: any = [
    {
      action: GlobalConstants.TableDefaultActions.MODAL_BUTTON,
      text: "Add",
      icon: GlobalConstants.Icons.ADD,
      modalContent: (setIsAddFormModalOpen, { refreshTableData }) => (
        <AddCommitmentCapForm setIsAddFormModalOpen={setIsAddFormModalOpen} servicingData={servicingData} refreshTableData={refreshTableData} loanId={loanId} />
      ),
      modalSize: "fullscreen",
      isDisabled: () => false
    } as ITableActionCustomButton,
    {
      action: GlobalConstants.TableDefaultActions.MODAL_BUTTON,
      text: "Edit",
      icon: GlobalConstants.Icons.EDIT,
      modalContent: (setIsAddFormModalOpen, { firstCheckedRow, refreshTableData }) => (
        <EditCommitmentCapForm
          id={firstCheckedRow.id}
          servicingData={servicingData}
          setIsAddFormModalOpen={setIsAddFormModalOpen}
          refreshTableData={refreshTableData}
        />
      ),
      modalSize: "fullscreen"
    } as ITableActionCustomButton,
    "export"
  ];

  return <>
    <LmsTable
      apiSuffix={`loandetails/servicing/commitment-caps/${loanId}`}
      onClickModalContent={(setIsAddFormModalOpen, { clickedRowData, refreshTableData }) => (
        <EditCommitmentCapForm
          id={clickedRowData.id}
          servicingData={servicingData}
          setIsAddFormModalOpen={setIsAddFormModalOpen}
          refreshTableData={refreshTableData}
        />
      )}
      actionButtons={actionButtons}
    />
  </>;
};

const LmsLoanDetailsServicingBulkTransactionForm = ({ setIsModalOpen, mainOptions, servicingData }) => {
  const { id, loanServicingTempId } = servicingData;
  const mainFormInputs = mainOptions?.inputs;
  const [{ fetchData }] = useFetchData("");
  const handleInitLoad = async ( modalOptions ) => {
    const noTempId = loanServicingTempId === "00000000-0000-0000-0000-000000000000";
    const actuaLloanServicingTempId = noTempId ? guid() : loanServicingTempId;
    const { data, error } = await fetchData(noTempId ? `loandetails/bulk-transactions/${id}/${actuaLloanServicingTempId}` : `loanDetails/bulk-transactions/${loanServicingTempId}`, { fetchMethod: "GET", skipCache: true });
    if (!error && data) {
      mainOptions.setInputs({
        ...mainFormInputs,
        bulkTransactions: [...data],
        loanServicingTempId: actuaLloanServicingTempId
      });
    }
    else {
      modalOptions?.useFormInstance?.setFormErrorMessage(error);
    }
  };

  const handleFormatSubmitTransactionData = (inputs) => {
    const updatedModalData = [] as any;
    mainFormInputs?.bulkTransactions?.forEach((item, i) => {
      if (inputs[i]?.isUpdated) {
        updatedModalData.push({ ...inputs[i], id: item?.id });
      }
    });
    
    return updatedModalData;
  };

  const myForm = new BCFormController({}, {
      isCompact: true, 
      styles: { minWidth: "1600px" },
      submitSuffix: "loandetails/bulk-transactions",
      submitMethod: "PUT",
      dataFormatterFunc: (data) => handleFormatSubmitTransactionData(data),
      displayMessageLocally: true,
      clearSuffixesOnSubmit: [`loandetails/bulk-transactions/${id}/${mainFormInputs?.loanServicingTempId}`, `loanDetails/bulk-transactions/${mainFormInputs?.loanServicingTempId}`]
  });

  myForm.addGroupFields([
    myForm.getLabelField("Transaction Category", { width: 2, position: "center", wrapLabel: true }),
    myForm.getLabelField("Transaction Type", { width: 3, position: "center" }),
    myForm.getLabelField("Transaction Effective Date", { width: 2, position: "center", wrapLabel: true }),
    myForm.getLabelField("Transaction Amount", { width: 2, position: "center" }),
    myForm.getLabelField("Custom Amount", { width: 2, position: "center" }),
    myForm.getLabelField("Transaction Split", { width: 2, position: "center" }),
    myForm.getLabelField("A Piece", { width: 2, position: "center" }),
    myForm.getLabelField("B Piece", { width: 2, position: "center" }),
    myForm.getLabelField("Notional Piece", { width: 2, position: "center" }),
    myForm.getLabelField("LP Payment", { width: 2, position: "center" }),
  ], { widths: 1 });
  
  if (mainFormInputs?.bulkTransactions) {
    mainFormInputs?.bulkTransactions.map((rowData, i)=> {
      const { 
        transactionCategoryDisplay, 
        transactionTypeDisplay, 
        transactionEffectiveDate,
        transactionAmount,
        customAmount,
        transactionSplitOption,
        apiece,
        bpiece,
        notionalBpiece,
        lppayment
      } = rowData;
      const formRowFields = [] as any[];
      formRowFields.push(myForm.getInputField("", { width: 2, name: "category", value: transactionCategoryDisplay, nestedInputsProp: i, disabled: true }));
      formRowFields.push(myForm.getInputField("", { width: 3, name: "transactionType", value: transactionTypeDisplay, nestedInputsProp: i, disabled: true }));
      formRowFields.push(myForm.getCalendarField("", { width: 2, name: "transactionEffectiveDate", value: transactionEffectiveDate, nestedInputsProp: i, disabled: true }));
      formRowFields.push(myForm.getCurrencyField("", { width: 2, name: "transactionAmount", value: transactionAmount, nestedInputsProp: i, disabled: true }));
      formRowFields.push(myForm.getCurrencyField("", { width: 2, name: "customAmount", value: customAmount, nestedInputsProp: i }));
      formRowFields.push(myForm.getDropdownField("", LmsTransactionSplitOptions, { width: 2, name: "transactionSplitOption", value: transactionSplitOption, nestedInputsProp: i }));
      formRowFields.push(myForm.getCurrencyField("", { width: 2, name: "apiece", value: apiece, nestedInputsProp: i, disabled: true }));
      formRowFields.push(myForm.getCurrencyField("", { width: 2, name: "bpiece", value: bpiece, nestedInputsProp: i, disabled: true }));
      formRowFields.push(myForm.getCurrencyField("", { width: 2, name: "notionalBpiece", value: notionalBpiece, nestedInputsProp: i, disabled: true }));
      formRowFields.push(myForm.getCurrencyField("", { width: 2, name: "potentialLoanRevenues", value: lppayment, nestedInputsProp: i, wrapLabel: true, disabled: true }));
      myForm.addGroupFields(formRowFields, { widths: 1 });
    });
  }

  const onBulkTransactionsModalChanged = (options, lastUpdated, nestedProp) => {
    const inputs = options?.inputs || {};
    const inputPiece = inputs[nestedProp].customAmount ? parseInt(inputs[nestedProp].customAmount) : 0;
    const remainingPiece = inputs[nestedProp].transactionAmount ? inputs[nestedProp].transactionAmount - inputPiece : 0;
    const updatedNestedProp = { ...inputs[nestedProp], notionalBpiece: null, isUpdated: true };
    if (lastUpdated === "customAmount" || lastUpdated === "transactionSplitOption") {
      inputs.aPieceTotal -= inputs[nestedProp].apiece ? inputs[nestedProp].apiece : 0;
      inputs.bPieceTotal -= inputs[nestedProp].bpiece ? inputs[nestedProp].bpiece : 0;
      inputs.notionalBpieceTotal -= inputs[nestedProp].notionalBpiece ? inputs[nestedProp].notionalBpiece : 0;
      if (inputs[nestedProp].customAmount) {
        if (inputs[nestedProp].transactionSplitOption === "A Piece") {
          updatedNestedProp.apiece = inputPiece;
          updatedNestedProp.bpiece = remainingPiece;
        }
        else if (inputs[nestedProp].transactionSplitOption === "B Piece") {
          updatedNestedProp.apiece = remainingPiece;
          updatedNestedProp.bpiece = inputPiece;
        }
        else if (inputs[nestedProp].transactionSplitOption === "Split"){
          updatedNestedProp.bpiece = (inputPiece * (inputs.bpiecePercentage ? parseInt(inputs.bpiecePercentage) : 0)) / 100;
          updatedNestedProp.apiece = inputPiece - updatedNestedProp.bpiece;
        }
      }
      else {
        if (inputs[nestedProp].transactionSplitOption === "A Piece") {
          updatedNestedProp.apiece = inputs[nestedProp].transactionAmount;
          updatedNestedProp.bpiece = 0;
        }
        else if (inputs[nestedProp].transactionSplitOption === "B Piece") {
          updatedNestedProp.apiece = 0;
          updatedNestedProp.bpiece = inputs[nestedProp].transactionAmount;
        }
        else if (inputs[nestedProp].transactionSplitOption === "Split"){
          updatedNestedProp.bpiece = (inputs[nestedProp].transactionAmount * (inputs.bpiecePercentage ? parseInt(inputs.bpiecePercentage) : 0)) / 100;
          updatedNestedProp.apiece = inputs[nestedProp].transactionAmount - updatedNestedProp.bpiece;
        }
      }
    }

    options.setInputs({ ...inputs, [nestedProp]: { ...updatedNestedProp }});
  };

  return (<>
    <LmsFormGenerator
      myForm={myForm}
      actions={["export", "save", "save-close", "close"]}
      onFormChanged={onBulkTransactionsModalChanged}
      handleClose={(): void => setIsModalOpen(false)}
      onInitLoad={handleInitLoad}
    />
  </>);
};

const LmsLoanDetailsServicing = ({ id }) => {
  const [{ data, isLoading, error }] = useFetchData(id ? `loandetails/servicing/${id}` : "");
  const interestReserveOriginal = data?.loan?.loanDetail?.interestReserveOriginal;
  const interestRateOriginal = data?.loan?.loanDetail?.interestRateOriginal;
  const portfolioManager = data?.loan?.portfolioManagerOrganizationContactId;
  const [sharedInputs, setSharedInputs] = useState(undefined as any);
  const [isDisabled, setIsDisabled] = useState(true);
  const loanId = data?.loan?.id;
  const servicingData = { loanId, ...data };
  const onFormChanged = ({ inputs }) => setSharedInputs(inputs);
  const labelColor = isDisabled ? "red" : undefined;
  const myFormDatesSection = new BCFormController(data, {
    values:{ interestReserveOriginal: interestReserveOriginal, interestRateOriginal: interestRateOriginal, portfolioManagerOrganizationContactId: portfolioManager },
    initIsDisabled: isDisabled,
    styles: { minHeight: "1103px" }
  });
  myFormDatesSection.addCalendarField("Loan Active Date", { name: "loanClosedDate", disabled: true });
  myFormDatesSection.addCurrencyField("Original Budget Amount", { disabled: true });
  myFormDatesSection.addCalendarField("Estimated 1st Draw", { name: "estimated1DrawDate", disabled: true });
  myFormDatesSection.addCalendarField("Transfer Date", { name: "purchaseDate", disabled: true });
  myFormDatesSection.addCalendarField("Maturity Date Original", { disabled: true });
  myFormDatesSection.addCalendarField("Current Maturity Date", { name: "maturityDateCurrent", labelColor: labelColor, disabled: true });
  myFormDatesSection.addCalendarField("Estimated Construction Completion", { name: "estimatedConstructionCompletionDate" });
  myFormDatesSection.addCalendarField("Estimated Payoff Date");
  myFormDatesSection.addCalendarField("Payoff Date", { disabled: true });
  myFormDatesSection.addCalendarField("Reconveyed Date");
  myFormDatesSection.addCalendarField("Pledge Date");

  const mainForm = new BCFormController(data, {
    values: data,
    submitSuffix: "loandetails/servicing",
    submitMethod: "PATCH",
    dataFormatterFunc: (data) => [{...data}],
    initIsDisabled: isDisabled,
    styles: { minHeight: "1103px" },
    exitEditOnClose: true,
    allDropdownOptionsAreReady: !!loanId
  });
  mainForm.addGroupFields([
    mainForm.getCurrencyField("Original Loan Amount", { name: "loanAmountOriginal", disabled: true }),
    mainForm.getCurrencyField("Current Loan Balance", { name: "loanAmountCurrent", labelColor: labelColor, disabled: true }),
    mainForm.getInputField("Work Complete %", { fieldType: "percent", name: "percentWorkComplete", disabled: true }),
    mainForm.getCurrencyField("Remaining Budget Balance", { disabled: true }),
  ]);
  mainForm.addGroupFields([
    mainForm.getInputField("Budget Complete %", { fieldType: "percent", name: "percentBudgetComplete", disabled: true }),
    mainForm.getInputField("Original Interest Rate", { name: "interestRateOriginal", value: interestRateOriginal, fieldType: "float", disabled: true }),
    mainForm.getInputField("Current Interest Rate", { fieldType: "float", name: "interestRateCurrent", labelColor: labelColor, disabled: true }),
    mainForm.getCalendarField("Current Int Rate Effective Date", { name: "currentInterestRateEffectiveDate", labelColor: labelColor, disabled: true }),
  ]);
  mainForm.addGroupFields([
    mainForm.getCurrencyField("Original Int Reserve Amount", { name: "interestReserveOriginal", disabled: true }),
    mainForm.getCurrencyField("Current Int Reserve Balance", { name: "interestReserveCurrent", disabled: true }),
    mainForm.getDropdownField("Portfolio Manager", loanId ? `loans/list-portfolio-manager/${loanId}`: "", { name: "portfolioManagerOrganizationContactId" }),
    mainForm.getInputField("Name of Loan Officer", { name: "loanOfficer", disabled: true })
  ]);
  mainForm.addGroupFields([
    mainForm.getCurrencyField("As Is Value", { disabled: true }),
    mainForm.getCheckboxField("Exclude From Late Payment Emails", { disabled: true }),
    mainForm.getCheckboxField("Exclude From Late Fees"),
    mainForm.getCheckboxField("Override Draw Preventions"),
  ]);
  mainForm.addDividerField();
  mainForm.addGroupFields([
    mainForm.getCurrencyField("Current A Trans Balance", { name: "currentAPiece", disabled: true }),
    mainForm.getCurrencyField("Current B Trans Balance", { name: "currentBPiece", disabled: true }),
    mainForm.getCurrencyField("Current B Notional Trans Balance", { name: "currentNotionalBPiece", disabled: true }),
    mainForm.getCurrencyField("Loan Purchaser Funded Balance", { name: "fundedTotalAmount", disabled: true })
  ]);
  mainForm.addGroupFields([
    mainForm.getCurrencyField("Difference of \"A\" Trans & Funded Bal", { name: "unfundedAmountTotal", disabled: true }),
    mainForm.getCurrencyField("Default Revenues Owed", { disabled: true }),
    mainForm.getCurrencyField("Funds Requested Unpaid", { name: "fundsUnpaidTotal", disabled: true }),
    mainForm.getDropdownField("Cross Collateralized", YesNoDropdownOptions, { name: "loanCrossCollateralizedOption", disabled: true }),
  ]);
  mainForm.addGroupFields([
    mainForm.getDropdownField("Loan Owner", "loans/list-loan-owners", { name: "ownerOrganizationId", width: 4, disabled: true }),
    mainForm.getDropdownField("Future Loan Owner", "loans/list-loan-owners", { name: "futureOwnerOrganizationId", width: 4 }),
    mainForm.getCurrencyField("Total Accrued Interest to Purchaser", { name: "accruedInterest", width: 4, disabled: true }),
    mainForm.getButtonField("View/Edit Bulk Transactions", undefined, {
      width: 2,
      modalSize: "fullscreen",
      modalContent: (setIsModalOpen, options) => {
        return <LmsLoanDetailsServicingBulkTransactionForm 
          setIsModalOpen={setIsModalOpen}
          servicingData={servicingData}
          mainOptions={options}
        />;
      },
      position: "center",
      fullWidth: true
    }),
    mainForm.getButtonField("View A Funded Transactions", undefined, {
      width: 2,
      modalContent: () => <LmsTable 
        actionButtons={[
          { 
            text: "Add", 
            path: "accounting-loan-transactions/add",
            action: GlobalConstants.TableDefaultActions.MODAL_BUTTON,
            isIframeModal: true,
            modalSize: "fullscreen",
            height: 1200,
            isDisabled: () => false
          }, 
          "export"]} 
        apiSuffix={`funds/a-transactions/${loanId}`} 
      />,
      modalSize: "fullscreen",
      position: "center",
      fullWidth: true
    })
  ], { widths: 4 });
  mainForm.addDividerField();
  mainForm.addGroupFields([
    mainForm.getCurrencyField("Fund", { name: "fundedTotalAmount", disabled: true }),
    mainForm.getInputField("Original Projected IRR", { name: "projectedIRROriginal",  value: data?.loan?.loanUnderwriting?.projectedIrroriginal || 0 , fieldType: "float", disabled: true }),
    mainForm.getInputField("Estimated Final IRR", { name: "finalIrr", fieldType: "float", disabled: true }),
    mainForm.getInputField("Advance Rate", { name: "loanAdvanceRate", fieldType: "float" }),
  ]);
  mainForm.addGroupFields([
    mainForm.getCurrencyField("Borrowing Base Cert Limiter", { name: "baseBorrowingClassLimiter", labelColor: labelColor }),
    mainForm.getDropdownField("Collateral Bank", "loandetails/servicing/list-collateral-banks", { name: "collateralBankOrganizationId", labelColor: labelColor }),
    mainForm.getInputField("# Payoffs Requested", { name: "numberOfPayoffRequested", disabled: true }),
    mainForm.getCurrencyField("Reconveyance Fee"),
  ]);
  mainForm.addGroupFields([
    mainForm.getTextareaField("Reconveyance Instructions", { width: 8 }),
    mainForm.getDropdownField("Default Bank Information", loanId ? `loandetails/servicing/list-default-banks/${loanId}`: "", { width: 4 }),
    mainForm.getDropdownField("Capital Partner Exception Option", YesNoDropdownOptions, {  name: "capitalPartnerExceptionOption", width: 4 }),
  ], { widths: 4 });
  mainForm.addGroupFields([
    mainForm.getTextareaField("Capital Partner Exception Comment", { name: "capitalPartnerExceptionComment", width: 8 }),
  ], { widths: 4 });
  mainForm.addGroupFields([
    mainForm.getBlankField({ width: 1 }),
    mainForm.getButtonField("Work Inspection Company History", undefined, {
      width: 3,
      fullWidth: true,
      modalContent: () => (
        <WorkInspectionCompanyHistoryModal suffix={`loandetails/work-inspection-company-histories/${loanId}`} servicingData={servicingData} />
      ),
      modalSize: "fullscreen"
    }),
    mainForm.getButtonField("Service Fee History", undefined, {
      width: 3,
      fullWidth: true,
      modalContent: () => <ServiceFeeHistoryModal suffix={`loandetails/service-fee-histories/${loanId}`} servicingData={servicingData} />,
      modalSize: "fullscreen"
    }),
    mainForm.getButtonField("View Interest Rate History", undefined, {
      width: 3,
      fullWidth: true,
      buttonIcon: GlobalConstants.Icons.CARET_UP,
      modalContent: () => <InterestRateHistoryTable loanId={loanId} servicingData={servicingData} />,
      modalSize: "fullscreen"
    }),
    mainForm.getButtonField("View Amendments", undefined, {
      width: 3,
      fullWidth: true,
      buttonIcon: GlobalConstants.Icons.CARET_UP,
      modalContent: () => <AmendmentsTable 
        suffix={`loans/amendments/${loanId}`} 
        servicingData={servicingData}
        isLoanDetails={true}
      />,
      modalSize: "fullscreen"
    }),
    mainForm.getButtonField("View Commitment Caps", undefined, {
      width: 3,
      fullWidth: true,
      buttonIcon: GlobalConstants.Icons.CARET_UP,
      modalContent: () => <CommitmentCapTable loanId={loanId} servicingData={servicingData} />,
      modalSize: "fullscreen"
    }),
    mainForm.getBlankField({ width: 1 })
  ], { widths: 1 });
  mainForm.addGroupFields([
    mainForm.getBlankField({ width: 1 }),
    mainForm.getLabelField("", { width: 3, labelFunc : (options) => { return options?.inputs?.workInspectionCompanyHistory || null; } }),
    mainForm.getLabelField("", { width: 3, labelFunc : (options) => { return options?.inputs?.serviceFee || null; } }),
    mainForm.getBlankField({ width: 3 }),
    mainForm.getBlankField({ width: 3 }),
    mainForm.getBlankField({ width: 3 }),
    mainForm.getBlankField({ width: 1 })
  ], { widths: 1 });

  const topActions: FormActionType[] = [
    {
      text: "View Payoff Quote History",
      icon: GlobalConstants.Icons.CARET_UP,
      modalContent: () => <LmsTable apiSuffix={`payoffquotes/histories/${loanId}`} actionButtons={["export"]} />,
      modalSize: "fullscreen"
    },
    "edit"
  ];

  const bottomActions = [
    "save",
    "save-close",
    "close"
  ];

  return (
    <div className="loan-details-servicing-page-container">
      <div>
        <LmsFormGenerator
          myForm={myFormDatesSection}
          isLoading={isLoading}
          inputs={sharedInputs}
          isDisabled={isDisabled}
          skipActions
          onFormChanged={onFormChanged}
        />
      </div>
      <div>
        <LmsFormGenerator
          myForm={mainForm}
          error={error}
          isLoading={isLoading}
          isDisabled={isDisabled}
          inputs={sharedInputs}
          topActions={topActions}
          actions={bottomActions}
          hideTopActionsOnEdit
          onFormChanged={onFormChanged}
          onEditClick={() => setIsDisabled(false)} // TODO: logic when adding Persona, if lockedByUserAccountId && lockedByUserAccountId !== user.id, error message: The Loan is locked by another user.
          onCloseClick={() => setIsDisabled(true)}
        />
      </div>
    </div>
  );
};

const ViewLoanOfficeDetailForm = ({ loanDetailsData }) => {
  const myForm = new BCFormController(loanDetailsData, { initIsDisabled: true, isCompact: true });
  myForm.addGroupFields([
    myForm.getInputField("App #", { name: "applicationNumberDisplay" }),
    myForm.getInputField("Loan #", { name: "loanNumberDisplay" }),
    myForm.getInputField("Primary Borrower Name", { name: "borrowerOrganizationDisplay" }),
    myForm.getInputField("Partnered Loan", { name: "partneredLoan" }),
    myForm.getInputField("Phase", { name: "loanPhaseDisplay" }),
    myForm.getInputField("Status", { name: "loanStatusDisplay" }),
  ]);

  return <LmsFormGenerator myForm={myForm} skipActions />;
};

const EditLoanOfficerForm = ({ loanId }) => {
  const getLoanOfficerSuffix = `loandetails/loan-officer/${loanId}`;
  const [{ data: loanOfficerData, isLoading, error }] = useFetchData(getLoanOfficerSuffix);
  const myForm = new BCFormController(loanOfficerData, {
    values: { loanId: loanId },
    submitSuffix: "loandetails/loan-officer",
    submitMethod: "PUT",
    clearSuffixesOnSubmit: [getLoanOfficerSuffix]
  });
  myForm.addCenteredGroup([myForm.getDropdownField("Name of Loan Officer", "metadata/list-loan-officer-organization-contacts", { name: "loanOfficerOrganizationContactId" })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Closing LO", "metadata/list-loan-officer-organization-contacts", { name: "closingLoanOfficerOrganizationContactId" })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Payoff LO", "metadata/list-loan-officer-organization-contacts", { name: "payoffLoanOfficerOrganizationContactId" })]);
  const formActions = ["save", "save-close", "close"];

  return <>
    <ViewLoanOfficeDetailForm loanDetailsData={loanOfficerData} />
    <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={formActions} />
  </>;
};

const LmsLoanDetailsEntities = ({ loanId }) => {
  return <LmsTable
    title="Entities"
    apiSuffix={`loans/entities/${loanId}`}
    deleteSuffix="loans/entities"
    actionButtons={[
      { action: "add", path: `loandetails/addentity?loanId=${loanId}` },
      "remove",
      "export",
      "save",
      "cancel",
      { text: "Edit Loan Officer", action: "add", path: `loandetails/editloanofficer?loanId=${loanId}`}
    ]}
    columnClickOptions={{
      organization: { path: "organizations-borrowers", idProp: "organizationId" },
      organizationContact: { path: "organizations-contacts", idProp: "organizationContactId" },
      detailEntered: { idProp: "id", isModalContentLink: true, isClickableConditionFunc: (rowData) => rowData?.detailEntered === "Yes" || rowData?.detailEntered === "No" }
    }}
    onClickModalSize="fullscreen"
    onClickModalContent={(setIsModalOpen, { clickedRowData }) => {
      const row = clickedRowData?.clickedRow;
      if (row?.organizationContactId) {
        return <ViewAdditionalInfoForm
          contactId={row?.organizationContactId}
          additionalGuarantorInfoId={loanId}
          handleClose={() => setIsModalOpen(false)}
        />;
      }
      else if (row?.organizationId) {
        return <ViewAdditionalBorrowerInfoForm id={row?.id} orgId={row?.orgId} />;
      }
    }}
    columnLabelsDictionary={{
      applicationNumber: "Application #",
      loanNumber: "Loan #",
      organizationContact: "Name",
      detailEntered: "Detail Complete",
      borrowerOrganization: "Borrower",
      officePhone: "Business Phone"
    }}
    saveSuffix="loans/entities"
    myForm={loanDetailsEntitiesInlineTableEditingFields(new BCFormController())}
  />;
};

const LmsAddEntities = ({ loanId }) => {
  const history = useHistory();
  const getGuarantorsGrid = loanId ? `loandetails/entities/guarantors/${loanId}` : "";

  const handleFormatSelectedData = (selectedData) => {
    return selectedData?.map(({ organizationContactId, entityType }) => ({ organizationContactId, roleOption: entityType }));
  };

  const isAddGuarantorEnabled = (checkedItems, data) => {
    const someCheckedAreNotEntityTypeGuarantor = checkedItems.some(ci => {
      if (data) {
        return data[ci].entityType !== "Guarantor";
      }
    });

    return checkedItems.length !== 0 && !someCheckedAreNotEntityTypeGuarantor;
  };

  const entitiesActions = [
    "export",
    { 
      action: "submitselected",
      text: "Add Related Entity",
      submitSuffix: `loandetails/entities/${loanId}`,
      handleFormatSubmitSelectedRows: handleFormatSelectedData,
      isDisabled: (checkedItems, data) => {
        if (checkedItems?.length < 1) return true;
        return isAddGuarantorEnabled(checkedItems, data);
      }
    },
    {
      action: "submitselected",
      text: "Add Guarantor",
      submitSuffix: `loandetails/guarantors/${loanId}`,
      icon: GlobalConstants.Icons.ADD,
      refreshDataOnSuccess: true,
      clearSuffixesOnSuccess: [`loans/entities/${loanId}`],
      onSuccessFunc: () => history.goBack(),
      handleFormatSubmitSelectedRows: handleFormatSelectedData,
      isDisabled: (checkedItems, data) => !isAddGuarantorEnabled(checkedItems, data)
    }
  ];

  return <LmsTable
    actionButtons={entitiesActions}
    handleFormatSelectedData={handleFormatSelectedData}
    apiSuffix={`loandetails/entities/guarantors/${loanId}`}
    addSelectedSuffix={loanId ? `loandetails/entities/${loanId}` : ""}
    selectedIdProp="organizationContactId"
    clearSuffixesOnSuccess={[getGuarantorsGrid, `loans/entities/${loanId}`]}
    columnLabelsDictionary={{ name: "Loan Entity", detailEntered: "Detail Complete" }}
    onClickModalSize="fullscreen"
    onClickModalContent={(setIsModalOpen, { clickedRowData }) => {
      return <ViewAdditionalInfoForm
        contactId={clickedRowData?.clickedRow?.organizationContactId}
        additionalGuarantorInfoId={loanId}
        handleClose={() => setIsModalOpen(false)}
      />;
    }}
  />;
};

const LmsLoanDetailsTabs = ({ loanId, loanDetailsId = "" }) => {
  const tabPages = {
    Documents: <LmsLoanDetailsDocuments loanId={loanId} loanDetailsId={loanDetailsId} />,
    "DocuSign Document": <DocuSignTable />
  };

  return <LmsTabPages tabPages={tabPages} />;
};

const LmsLoanDetailsDocuments = ({ loanId, loanDetailsId }) => {
  const actionButtons = [
    { text: "Add", icon: GlobalConstants.Icons.ADD, path: `/documents/add?loanDetailsId=${loanDetailsId}` },
    { text: "Edit", path: "/documents" },
    "remove",
    "export",
    { text: "Add Proforma", action: "add", path: `/documents/addproforma?loanId=${loanId}` },
  ];

  return <LmsTable
    actionButtons={actionButtons}
    apiSuffix={`loandetails/${loanId}/documents`}
    deleteSuffix="document"
    onClickPath="documents"
    columnLabelsDictionary={{
      isArchived: "Archive",
      expiryDate: "Expiration Date",
      includeInLrcpolling: "Include in LRC"
    }}
  />;
};

const RedistributeCostsForm = ({ setIsRedistributeCostsFormOpen, loanTransactionIds, loanId, refreshTableData }) => {
  const [{ data: redistributionCostTempId, error }] = useFetchData(`loandetails/history/redistributecost/${loanId}`, {
    method: "POST",
    payloadData: loanTransactionIds,
    skipCache: true
  });

  const costTempDataTableSuffix = redistributionCostTempId ? `loandetails/history/redistribution-cost-temp-data/${redistributionCostTempId}` : "";
  const myForm = new BCFormController({}, {
    submitSuffix: `loandetails/history/redistributecost/${redistributionCostTempId}`,
    submitMethod: "PUT",
    dataFormatterFunc: (inputs) => inputs?.loanPropertyIds
  });

  myForm.addCenteredGroup([myForm.getDropdownField("Please select which properties to redistribute the costs to:", `loandetails/list-loan-properties/redistribute-costs/${loanId}`, {
    name: "loanPropertyIds",
    dropdownMultiple: true
  })]);

  if (redistributionCostTempId) {
    myForm.addCenteredGroup([myForm.getComponent(<LmsTable apiSuffix={costTempDataTableSuffix} isDisabled />)], { blankWidth: 1 });
  }

  return (
    <LmsFormGenerator
      myForm={myForm}
      isLoading={!redistributionCostTempId && !error}
      actions={["save-close", "close"]}
      handleClose={() => { setIsRedistributeCostsFormOpen(false); refreshTableData(); }}
    />
  );
};

const RedistributeCostsModal = ({ loanId }) => {
  const actionButtons = [
    "export",
    {
      text: "Redistribute Costs",
      icon: GlobalConstants.Icons.CARET_UP,
      isDisabled: (checkedItems) => checkedItems.length === 0,
      modalContent: (setIsRedistributeCostsFormOpen, { checkedIds, refreshTableData }) => (
        <RedistributeCostsForm 
          setIsRedistributeCostsFormOpen={setIsRedistributeCostsFormOpen} 
          loanTransactionIds={checkedIds} 
          loanId={loanId} 
          refreshTableData={refreshTableData}
        />
      ),
      modalSize: "fullscreen" as ModalSizesType
    }
  ];

  return (
    <LmsTable
      apiSuffix={`loandetails/history/allocatedcosts/${loanId}`}
      actionButtons={actionButtons}
      calculatedColumns={["allocatedCost"]}
      columnLabelsDictionary={{
        allocatedCost: "$ Assigned per Active Unit",
        allocatedPercent: "% Assigned per Active Unit",
        loanNumber: "Loan #"
      }}
      skipClick
    />
  );
};

const LmsLoanDetailsLoanHistory = ({ loanId, loanApplicationNumber = "" }) => {
  const myForm = new BCFormController();
  const onFormChanged = async (options, lastUpdated) => {
    const inputs = options?.inputs;
    if(lastUpdated === "includeAllocatedCosts") {
      if (inputs?.includeAllocatedCosts === true) {
        options.setInputs({ ...inputs, allocatedCosts: "show" });
      }
      else {
        options.setInputs({ ...inputs, allocatedCosts: "hide" });
      }
    }
  };

  const fileName = `${loanApplicationNumber} - Transaction history`;
  myForm.addGroupFields([
    myForm.getBlankField({ width: 6 }),
    myForm.getDownloadButtonField("Borrower Friendly Excel", `loandetails/history/borrower-friendly-excel/${loanId}`, {
      buttonIcon: GlobalConstants.Icons.EXCEL_OUTLINE,
      width: 2,
      fullWidth: true,
      name: fileName
    }),
    myForm.getDownloadButtonField("Internal Friendly Excel", `loandetails/history/internal-friendly-excel/${loanId}`, {
      buttonIcon: GlobalConstants.Icons.EXCEL_OUTLINE,
      width: 2,
      fullWidth: true,
      name: fileName
    }),
    myForm.getBlankField({ width: 6 }),
  ], { widths: 2 });
  myForm.addDividerField({ dividerIsHidden: true });
  myForm.addGroupFields([
    myForm.getBlankField({ width: 6 }),
    myForm.getCheckboxField("Include Allocated Costs", { width: 2, inline: true }),
    myForm.getButtonField("Redistribute Costs", undefined, {
      modalContent: () => <RedistributeCostsModal loanId={loanId} />,
      modalSize: "fullscreen",
      width: 2,
      fullWidth: true,
      isDisabledOn: { includeAllocatedCosts: true },
      isDisabledOnOperation: "notequal"
    }),
    myForm.getBlankField({ width: 6 }),
  ], { widths: 2 });
  myForm.addCenteredGroup([
    myForm.getComponent((fieldProps) => <LoanTransactionHistoriesTable key={"loan-transaction-history"} loanId={loanId} fieldProps={fieldProps} isDisabled />, {
      width: 14,
      name: "historyTable"
    }),
  ], { blankWidth: 1 });

  return <LmsFormGenerator myForm={myForm} skipActions onFormChanged={onFormChanged} />;
};

const LmsLoanCommentsTabs = ({ suffix = "", loanId, loanDetailsId = "" }) => {
  const tabPages = {
    Comments: <LmsLoanCommentsTable suffix={suffix} loanId={loanId} loanDetailsId={loanDetailsId} />,
    EmailBox: <LmsTable apiSuffix={`loans/${loanId}/email-box`} actionButtons={["export"]} />
  };

  return <LmsTabPages tabPages={tabPages} />;
};

export const AddLoanDetailsCommentModal = ({ loanId, loanDrawId, setIsModalOpen, setNewCommentId = undefined as any }) => {
  const [isAddComment, setIsAddComment] = useState(true);
  const [addedCommmentId, setAddedCommmentId] = useState("");
  const onModalAddSuccess = async (response) => {
    if (response.generateEmail) {
      setIsAddComment(false);
      setAddedCommmentId(response?.id);
    }
    else {
      setIsModalOpen(false);
    }

    if (setNewCommentId) {
      setNewCommentId(response?.id);
    }
  };

  return <>
    {isAddComment && <AddLoanDetailsCommentForm 
      loanId={loanId} 
      loanDrawId={loanDrawId} 
      onSubmitModalSuccess={onModalAddSuccess} 
      handleClose={() => setIsModalOpen(false)}
    />}
    {(!isAddComment && addedCommmentId) && <EditLoanDetailsCommentForm 
      id={addedCommmentId} 
      loanDetailId={loanDrawId} 
      handleClose={() => setIsModalOpen(false)} 
    />}
  </>;
};

export const AddLoanDetailsCommentForm = (
  {
    loanId,
    loanDrawId = "",
    onSubmitModalSuccess = undefined as any,
    submitSuffix = "loans/comment",
    handleClose = undefined as any,
  }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`loans/${loanId}`);
  const initialData = {
    loan: data?.display,
    borrowerOrganizationId: data?.borrowerOrganizationId,
    specialPurposeEntityId: data?.specialPurposeEntityId
  };

  const onSubmitFromSuccess = async (responseData, options) => {
    if (responseData.generateEmail) {
      options?.history.replace(`/loandetails/editcomment/${responseData?.id}`);
    }
    else {
      options?.history.goback();
    }
  };

  const myForm = new BCFormController(initialData, {
    values: { loanId, ...(loanDrawId ? { loanDrawId } : {}) },
    submitSuffix,
    clearSuffixesOnSubmit: [`draws/comments/${loanDrawId}`],
  });

  myForm.addCenteredGroup([myForm.getDropdownField("Borrower", "metadata/list-borrower-organizations", { required: true, name: "borrowerOrganizationId", disabled: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Special Purpose Entity", "orgs/list-spes", { name: "specialPurposeEntityId", disabled: true })]);
  myForm.addCenteredGroup([myForm.getDisplayField("Loan")]);
  myForm.addCenteredGroup([myForm.getDropdownField("Category", "loandetails/list-comment-categories", { name: "commentCategoryId", required: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Comment Subject", "metadata/list-comment-subjects", { required: true, dependencyField: "commentCategoryId" })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Comments", { required: true, name: "description" })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Generate Email?", { name: "generateEmail" })]);

  return <LmsFormGenerator 
    myForm={myForm} 
    isLoading={isLoading} 
    error={error}
    actions={[
      { text: "Save", icon: GlobalConstants.Icons.SAVE, action: "save" },
      "close"
    ]} 
    onSubmitSuccess={onSubmitModalSuccess || onSubmitFromSuccess}
    {...handleClose ? { handleClose: handleClose } : {}}
  />;
};

const EditLoanDetailsCommentForm = ({ id, loanDetailId = "", handleClose = undefined as any }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`loandetails/comment/${id}`);
  const myForm = new BCFormController(data, { submitSuffix: "loandetails/comments", clearSuffixesOnSubmit: [`loandetails/comment/${id}`] });
  myForm.addCenteredGroup([myForm.getDropdownField("Category", "metadata/list-comment-categories", { name: "commentCategoryId", required: true, disabled: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Comment Subject", "metadata/list-comment-subjects", { dependencyField: "commentCategoryId", required: true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Comments", { required: true, name: "description" })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Generate Email?", { disabled: true, name: "generateEmail" })]);
  const loanDetailsData = {
    applicationNumber: data?.applicationNumberDisplay,
    loanNumber: data?.loanNumberDisplay,
    borrowerOrganization: data?.borrowerOrganizationDisplay,
    loanPhase: data?.loanPhase,
    loanStatus: data?.loanStatus,
    projectName: data?.projectName
  };
  const history = useHistory();
  const handleBackToLoanDetails = async () => {
    history.push(`loandetails/${loanDetailId}`);
  };

  const tabPages = {
    Contacts: <LmsTable 
      actionButtons={[
        "export", 
        {
          action: GlobalConstants.TableDefaultActions.SUBMIT_SELECTED,
          text: "Send Email",
          submitSuffix: `loandetails/send-comment-email/${id}`,
          method: "POST",
          icon: GlobalConstants.Icons.CARET_UP,
          handleFormatSubmitSelectedRows: (data) => {
            return [...data.map(d => d.id)];
          },
        }
      ]} 
      skipClick 
      apiSuffix={`loandetails/comment-contacts/${id}`} 
      columnLabelsDictionary={{ lmstab: "LMS Tab" }} 
    />
  } ;

  return <>
    <ViewDetailsForm loanDetailsData={loanDetailsData} isShortVersion={true} />
    <LmsFormGenerator 
      myForm={myForm} 
      isLoading={isLoading} 
      error={error} 
      actions={["save", "save-close", "close"]} 
      handleClose={handleClose || handleBackToLoanDetails} 
    />
    {data?.generateEmail && <LmsTabPages tabPages={tabPages}/>}
  </>;
};

const ModifyBudgetsModal = ({ loanDetailsId, budgetId, setBudgetId }) => {
  const handleEdit = (row) => {
    setBudgetId(row.id);
  };

  return <>
    {!budgetId && <LmsTable
      apiSuffix={`draws/budget-properties/${loanDetailsId}`}
      handleClick={handleEdit}
      actionButtons={[{action: "edit", handleClick: handleEdit }, "export"]}
    />}
    {budgetId && <LmsModifyBudget id={budgetId} handleClose={() => setBudgetId("")}/>}
  </>;
};

const LmsLoanDetailsDraws = ({ loanDetailsId, loanId }) => {
  const [isDrawManagementOpen, setIsDrawManagementOpen] = useState(false);
  const [isModifyBudgetOpen, setIsModifyBudgetOpen] = useState(false);
  const [budgetId, setBudgetId] = useState("");
  const onClosePath = `loandetails/${loanDetailsId}?tab=draws`;

  const tabPages = {
    Draws: <MainServicingDrawTable suffix={`draws/loan/${loanId}`} loanId={loanId}/>,
    "Inspection Company Messages": <LmsServicingInspectionCompanyMessagesMainPage suffix={`draws/nwccmessages/${loanId}`} loanId={loanId} onClosePath={onClosePath}/>,
  };

  const handleModifyBudgetsClose = () => {
    if (budgetId) {
      setBudgetId("");
    }
    else {
      setIsModifyBudgetOpen(false);
    }
  };

  return <React.Fragment>
    <div className="lms-flex-centered-row-container">
      <Button
        basic={true}
        onClick={() => setIsDrawManagementOpen(true)}
      >
        <Icon className={GlobalConstants.Icons.CARET_UP}/>
        Draw Management
      </Button>
      <Button
        basic={true}
        onClick={() => setIsModifyBudgetOpen(true)}
      >
        <Icon className={GlobalConstants.Icons.CARET_UP}/>
        Modify Budget
      </Button>
    </div>
    <LmsTabPages tabPages={tabPages} isNested />
    <BCModal
      isModalOpen={isDrawManagementOpen}
      size="fullscreen"
      closeIcon
      onClose={() => setIsDrawManagementOpen(false)}
      content={<DrawManagement loanId={loanId}/>}
    />
    <BCModal
      isModalOpen={isModifyBudgetOpen}
      size="fullscreen"
      closeIcon
      onClose={handleModifyBudgetsClose}
      content={<ModifyBudgetsModal loanDetailsId={loanDetailsId} budgetId={budgetId} setBudgetId={setBudgetId}/>}
    />
  </React.Fragment>;
};

const LmsLoanDetailsAssignments = ({ loanId }) => {
  const actionButtons = ["export"];

  return <LmsTable
    title="Assignments"
    actionButtons={actionButtons}
    apiSuffix={`loandetails/assignments/${loanId}`}
    onClickPath="loandetails/viewassignment"
    columnLabelsDictionary={{
      loanNumber: "Loan #",
      description: "Comments",
      spe: "SPE"
    }}
  />;
};

const setEmailConfigFields = (myForm, emailConfig) => {
  myForm.addCenteredGroup([
    myForm.getLabelField("Role", { width: 2 }),
    myForm.getLabelField("Entity", { width: 3 }),
    myForm.getLabelField("Items Needed", { width: 1 }),
  ], { widths: 1 });

  if (emailConfig) {
    emailConfig.loanConditionEmailConfigs?.map(({ roleOption, loanEntity, itemsNeeded }, i) => {
      myForm.addCenteredGroup([
        myForm.getInputField("", { name: "roleOption", value: roleOption, nestedInputsProp: i, disabled: true, width: 2 }),
        myForm.getInputField("", { name: "entity", value: loanEntity?.display, nestedInputsProp: i, disabled: true, width: 3 }),
        myForm.getCheckboxField("", { name: "itemsNeeded", value: itemsNeeded, nestedInputsProp: i, width: 1, disabled: !loanEntity?.display }),
      ], { widths: 1 });
    });

    myForm.addCenteredGroup([myForm.getInputField("Box Link")]);
    myForm.addCenteredGroup([myForm.getCheckboxField("Send Daily Items Needed Email")]);
  }

  return myForm;
};

const LmsLoanDetailsConditions = ({ loanId }) => {
  const initialSuffix = `loandetails/loan-condition-email-config/${loanId}`;
  const [{ data, isLoading, fetchData }, refreshEmailConfigData] = useFetchData(initialSuffix);
  const dataFormatterFunc = (inputs) => {
    const updatedConfigs = data?.loanConditionEmailConfigs?.map(({ id, roleOption }, i) => {
      return { id, roleOption, itemsNeeded: inputs[i]?.itemsNeeded };
    });

    const updatedData: any = {
      ...data,
      sendDailyItemsNeededEmail: inputs?.sendDailyItemsNeededEmail,
      loanConditionEmailConfigs: updatedConfigs,
      boxLink: inputs?.boxLink
    };

    return updatedData;
  };

  const myForm = setEmailConfigFields(new BCFormController(data, {
    submitSuffix: "loandetails/loan-condition-email-config",
    submitMethod: "PUT",
    dataFormatterFunc: dataFormatterFunc,
    isCompact: true,
    initIsDisabled: true,
    exitEditOnClose: true,
    clearSuffixesOnSubmit: [initialSuffix]
  }), data);

  const handleSendEmailClick = async (_, handleSubmit, options) => {
    const { error } = await fetchData("loandetails/loan-condition-email-config", { fetchMethod: "PUT", data: dataFormatterFunc(options?.inputs) });
    if (!error) {
      handleSubmit(`loandetails/loan-condition-send-email/${loanId}`, {
        buttonText: options.buttonText,
        data: {},
        method: "POST"
      } as IHandleSubmitOptions);
    }
  };

  const isSendEmailDisabled = (options) => {
    const { updatedArr } = getArrayFromInputsWithProps(options?.inputs);
    return !updatedArr.some(conf => conf.itemsNeeded);
  };

  const sendEmailAction = { text: "Send Email", handleClick: handleSendEmailClick, icon: GlobalConstants.Icons.CARET_UP, isDisabled: (_, options) => isSendEmailDisabled(options) };
  const topActions = ["edit", sendEmailAction];
  const formActions = [{ action: "save-new", text: "Save" }, "save-close", sendEmailAction, "close"];

  return <>
    <LmsFormGenerator
      myForm={myForm}
      isLoading={isLoading}
      topActions={topActions}
      actions={formActions}
      hideTopActionsOnEdit
      onSubmitSuccess={refreshEmailConfigData}
    />
    <LmsLoanConditionsTable suffix={`loandetails/conditions/${loanId}`} source="Loan" title="Conditions" loanId={loanId} />
  </>;
};

const BorrowerApplicationPropertyUnit = ({ id, isAdd = false, isEdit = false, propertyId = "" }) => {
  const [{ data = {}, isLoading, error }] = useFetchData((!isAdd && id) ? `/borrowerapplication/units/${id}`: "");
  const [{ data: propertyData, isLoading: isPropertyDataLoading }] = useFetchData(propertyId ? `/borrowerapplication/property/${propertyId}`: "");
  const myForm = new BCFormController(data, {
    values: { 
      borrowerApplicationPropertyId : propertyId || data?.borrowerApplicationPropertyId, 
      property: propertyData?.display || data?.property,
      isActive: data?.isActive || true
    },
    initIsDisabled: !(isAdd || isEdit),
    submitSuffix: "borrowerapplication/property-units",
    submitMethod: isAdd ? "POST" : "PATCH",
    dataFormatterFunc: (data) => { return [{ ...data }]; },
    clearSuffixesOnSubmit: [`/borrowerapplication/units/${id}`, `/borrowerapplication/property/${propertyId}`],
  });

  myForm.addGroupFields([
    myForm.getDisplayField("Borrower Application Property", { name: "property" }),
    myForm.getInputField("Unit Number", { required: true })
  ]);

  myForm.addGroupFields([
    myForm.getInputField("Number of Bedrooms", { name: "numberOfBedroom", fieldType: "number" }),
    myForm.getInputField("Number of Bathrooms", { fieldType: "number" })
  ]);

  myForm.addGroupFields([
    myForm.getInputField("Number of Garage Bays", { name: "numberOfCarGarage", fieldType: "number" }),
    myForm.getInputField("Square Footage", { fieldType: "number" })
  ]);

  myForm.addGroupFields([
    myForm.getInputField("Months Before Start", { fieldType: "number" }),
    myForm.getInputField("Months to finish construction", { name: "buildTimeInMonths", fieldType: "number" })
  ]);

  myForm.addGroupFields([
    myForm.getInputField("Time Frame to Sell or Refinance in Months", { fieldType: "number" }),
    myForm.getInputField("Month of First Draw", { fieldType: "number" })
  ]);

  myForm.addGroupFields([
    myForm.getCheckboxField("Is Active"),
  ]);

  myForm.addGroupFields([
    myForm.getTextareaField("Description")
  ]);

  const formActions = isAdd ? ["save-new", "save-continue", "save-close", "close"] : 
    isEdit ? ["save", "save-close", "delete", "close"] : ["close"];
  return (
    <LmsFormGenerator error={error} myForm={myForm} actions={formActions} isLoading={isLoading || isPropertyDataLoading} />
  );
};

const BorrowerApplicationPropertyForm = ({ id = "", isAdd = false, isEdit = false, borrowerApplicationId = "" }) => {
  const [{ data = {}, isLoading, error }] = useFetchData((!isAdd && id) ? `/borrowerapplication/property/${id}`: "");
  const myForm = new BCFormController(isAdd ? { isActive: data?.isActive || true } : data, 
    { 
      initIsDisabled: !(isAdd || isEdit),
      submitSuffix: "borrowerapplication/properties",
      submitMethod: isAdd ? "POST" : "PATCH",
      dataFormatterFunc: (data) => { return [{ data, borrowerApplicationId: borrowerApplicationId }]; }
    });

  [
    [
      ...(isAdd
        ? [myForm.getDropdownField("Borrower Application", LmsEndpoints.Metadata.GET.LIST_BORROWER_APPLICATIONS, { disabled: true })]
        : [myForm.getDropdownDisplayField("Borrower Application")]),
      myForm.getCheckboxField("Collateral Property"),
    ],
    [
      myForm.getInputField("Address (or a unique identifier)", { name: "display" }),
      ...(isAdd ? [myForm.getZipCodeField("Zip Code", { validations: ["zipcode"] })] : [myForm.getDisplayField("Zip")]),
    ],
    [...(isAdd 
      ? [myForm.getStateField("State", { name: "state", required: false }), myForm.getCityField("City", { name: "city", required: false })]
      : [myForm.getDropdownDisplayField("State", { name: "state", required: false }), myForm.getDropdownDisplayField("City", { name: "city", required: false })])],
    [...(isAdd ? [myForm.getCountyField("County", { name: "county" })] : [myForm.getDropdownDisplayField("County")]), myForm.getCheckboxField("Is Active")],
    [myForm.getTextareaField("Parcel Number")],
    [myForm.getInputField("Lot Number"), myForm.getInputField("Zoning")],
    [
      myForm.getInputField("Number of Units"),
      myForm.getDropdownField("Existing Structure Property Type", "metadata/list-property-types")
    ],
    [myForm.getDropdownField("New Structure Property Type", "metadata/list-property-types"), myForm.getDropdownField("Current Work Stage", "metadata/list-work-stage", { name: "workStageId" })],
    [myForm.getTextareaField("Description")],
  ].forEach((fields) => {
    myForm.addCenteredGroup(fields);
  });

  const formActions = isAdd ? ["save-new", "save-continue", "save-close", "close"] : 
    isEdit ? ["save", "save-close", "delete", "close"] : ["close"];
  return (
    <>
      <LmsFormGenerator error={error} myForm={myForm} actions={formActions} isLoading={isLoading} />
      {!isAdd && <LmsTabPages
        tabPages={{
          Unit: (
            <LmsTable
              actionButtons={isEdit ? [
                { text: "Add", path: `/loandetails/addunit?propertyId=${id}`},
                { text: "Edit", path: "/loandetails/editunit" },
                "remove", 
                "export"
              ] : ["export"]}
              onClickPath={isEdit ? "loandetails/editunit" : "loandetails/viewunit"}
              apiSuffix={`/borrowerapplication/property/list-property-units/${id}`}
              deleteSuffix="borrowerapplication/property-units"
              columnLabelsDictionary={{
                numberOfCarGarage: "Number of Garage Bays",
                buildTimeInMonths: "Months to finish construction",
                afterRepairValue: "Completed Value (ARV)"
              }}
            />
          ),
        }}
      />}
    </>
  );
};

const BorrowerApplicationCostPaidForm = ({ id = "", isAdd = false, isEdit = false, borrowerApplicationId = "" }) => {
  const initDisabled = (isAdd === false && isEdit === false);
  const [{ data = {}, isLoading, error }] = useFetchData((!isAdd && id) ? `/borrowerapplication/cost-paid/${id}`: "");
  const myForm = new BCFormController(isAdd ? { borrowerApplicationId: borrowerApplicationId } : data, { 
    initIsDisabled: initDisabled,
    submitSuffix: "borrowerapplication/cost-paids",
    submitMethod: isAdd ? "POST" : "PUT",
    dataFormatterFunc:  (data) => { return [{...data}]; }
  });
  
  myForm.addCenteredGroup([myForm.getDropdownField("Borrower Application", LmsEndpoints.Metadata.GET.LIST_BORROWER_APPLICATIONS, { disabled: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Project Cost Paid to Date", "metadata/list-gmr-project-costs", { 
    name: "gmrprojectCostId",
  })]);
  myForm.addCenteredGroup([myForm.getCurrencyField("Amount")]);
  myForm.addCenteredGroup([myForm.getTextareaField("Description")]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Expect Reimbursement At Closing")]);
  myForm.addCenteredGroup([myForm.getCurrencyField("Reimbursement Amount", { isHiddenFunc: ({ inputs }) => inputs.expectReimbursementAtClosing !== true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Reimbursement Description", { isHiddenFunc: ({ inputs }) => inputs.expectReimbursementAtClosing !== true })]);
  const formActions = isAdd ? ["save-new", "save-continue", "save-close", "close"] : ["close"];
  return (
    <LmsFormGenerator error={error} myForm={myForm} actions={formActions} isLoading={isLoading} />
  );
};

const LmsBorrowerApplicationCostPaidTable = ({ suffix, isEdit = false }) => {
  const borrowerApplicationId = suffix?.split("/")?.[2] || "";
  return <LmsTable
    actionButtons={isEdit ? [
      { text: "Add", path: `/loandetails/addcostpaid?borrowerApplicationId=${borrowerApplicationId}`},
      { text: "Edit", path: "/loandetails/editcostpaid" },
      "remove",
      "export"
    ] : ["export"]}
    onClickPath="loandetails/viewcostpaid"
    apiSuffix={suffix}
    deleteSuffix="borrowerapplication/cost-paids"
    columnLabelsDictionary={{ gmrprojectCost: "Project Cost Paid to Date" }}
  />;
};

const LmsBorrowerApplicationEntityTable = ({ suffix }) => {
  return <LmsTable
    actionButtons={["export"]}
    onClickPath="loandetails/viewentity"
    apiSuffix={suffix}
    columnLabelsDictionary={{ legalText3DocumentReference: "Background Check and/or Consumer Credit Report Authorization" }}
  />;
};

const LmsBorrowerApplicationProperty = ({ suffix, isEdit = false }) => {
  const borrowerApplicationId = suffix?.split("/")?.[2] || "";
  return <LmsTable
    actionButtons={isEdit ? [
      { text: "Add", path: `/loandetails/addproperty?borrowerApplicationId=${borrowerApplicationId}`},
      { text: "Edit", path: "/loandetails/editproperty" },
      "remove",
      "export"
    ] : ["export"]}
    onClickPath={isEdit ? "loandetails/editproperty" : "loandetails/viewproperty"}
    apiSuffix={suffix}
    deleteSuffix="borrowerapplication/properties"
    columnLabelsDictionary={{ street: "Address (or a unique identifier)", workStage: "Current Work Stage" }}
  />;
};

const BorrowerApplicationViewEntity = ({ id }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`/borrowerapplication/entity/${id}`);
  const myForm = new BCFormController(data, { initIsDisabled: true });

  [
    [myForm.getDisplayField("Name"), myForm.getDropdownDisplayField("Borrower Application")],
    [
      myForm.getDropdownDisplayField("Organization"),
      myForm.getDisplayField("Role Option"),
    ],
    [
      myForm.getDropdownDisplayField("Organization Contact"),
      myForm.getDisplayField("Confirmation Status"),
    ],
    [
      myForm.getCalendarField("Confirmation Date"),
      myForm.getCalendarField("Last Confirmation Request Date"),
    ],
    [
      myForm.getDropdownDisplayField("Decline Borrower Application Input"),
      myForm.getDisplayField("Other Decline Reason"),
    ],
    [myForm.getCheckboxField("Is Active"), myForm.getBlankField()],
    [myForm.getTextareaField("Description")],
  ].forEach((fields) => {
    myForm.addCenteredGroup(fields);
  });

  return (
    <LmsFormGenerator error={error} myForm={myForm} actions={["close"]} isLoading={isLoading} />
  );
};

const getRowFieldBorrowerAppProps = (
  props: IFormGeneratorFieldOptions = {}
): IFormGeneratorFieldOptions => Object.assign({ label: "", width: 4 }, props);

const LmsLoanDetailsBorrowerApplication = ({ loanId, loanDetailsData = {} as any }) => {
  const [isEditModeOn, setIsEditModeOn] = useState(false);
  const [{ data = {}, isLoading, error }, reFetchStatusHistory] = useFetchData(`/borrowerapplication/${loanId}`);
  const myForm = new BCFormController(data, {
    submitSuffix: "borrowerapplication",
    submitMethod: "PATCH",
    dataFormatterFunc: (data) => { return [{ ...data }]; },
    clearSuffixesOnSubmit: [`borrowerapplication/${loanId}`],
    isCompact: true,
    initIsDisabled: true,
    exitEditOnClose: true,
    onSubmitSuccess: () => { reFetchStatusHistory(); }
  });
  [
    {
      question: "What type of loan are you looking for?",
      inputField: myForm.getDropdownField(
        "",
        "metadata/list-loan-programs?includeRevolver=true",
        getRowFieldBorrowerAppProps({ name: "loanProgramId", disabled: true })
      )
    },
    {
      question: "What type of structure(s) will you be building?",
      inputField: myForm.getDropdownField(
        "",
        "metadata/list-property-types",
        getRowFieldBorrowerAppProps({ name: "newStructurePropertyTypeId" })
      ),
      displayOnLoanPrograms: [
        GlobalConstants.LoanProgramIds.ALL_IN_ONE,
        GlobalConstants.LoanProgramIds.NEW_CONSTRUCTION,
        GlobalConstants.LoanProgramIds.BRIDGE_TO_BUILD,
        GlobalConstants.LoanProgramIds.FIX_AND_FLIP
      ]
    },
    {
      question: "What type of structure(s) exists on the property?",
      inputField: myForm.getDropdownField(
        "",
        "metadata/list-property-types",
        getRowFieldBorrowerAppProps({ name: "existingStructurePropertyTypeId" })
      ),
    },
    {
      question: "How long have you owned the property?",
      inputField: myForm.getDropdownField(
        "",
        "metadata/list-borrower-application-inputs",
        getRowFieldBorrowerAppProps({ name: "howLongOwnedBorrowerApplicationInputId" })
      ),
    },
    {
      question: "The original purchase price was:",
      inputField: myForm.getCurrencyField("", getRowFieldBorrowerAppProps({ name: "originalPurchasePrice" })),
      displayOnLoanPrograms: [
        GlobalConstants.LoanProgramIds.ALL_IN_ONE,
        GlobalConstants.LoanProgramIds.NEW_CONSTRUCTION,
        GlobalConstants.LoanProgramIds.BRIDGE_TO_BUILD,
        GlobalConstants.LoanProgramIds.DEVELOPMENT
      ]
    },
    {
      question: "The amount currently owed is:",
      inputField: myForm.getCurrencyField("", getRowFieldBorrowerAppProps({ name: "amountOwed" }))
    },
    {
      question: "The purchase price is:",
      inputField: myForm.getCurrencyField("", getRowFieldBorrowerAppProps({ name: "purchasePrice" })),
    },
    {
      question: "Is there a seller carryback or subordinated debt?",
      inputField: myForm.getCheckboxField("", getRowFieldBorrowerAppProps({ name: "subordinatedDebt" })),
      displayOnLoanPrograms: [
        GlobalConstants.LoanProgramIds.ALL_IN_ONE,
        GlobalConstants.LoanProgramIds.NEW_CONSTRUCTION,
        GlobalConstants.LoanProgramIds.BRIDGE_TO_BUILD,
        GlobalConstants.LoanProgramIds.DEVELOPMENT,
        GlobalConstants.LoanProgramIds.FIX_AND_FLIP
      ]
    },
    {
      question: "Amount of Subordinated Debt/Seller Carryback",
      inputField: myForm.getCurrencyField("", getRowFieldBorrowerAppProps({ name: "subordinatedDebtAmount" })),
      displayOnLoanPrograms: [
        GlobalConstants.LoanProgramIds.ALL_IN_ONE,
        GlobalConstants.LoanProgramIds.NEW_CONSTRUCTION,
        GlobalConstants.LoanProgramIds.BRIDGE_TO_BUILD,
        GlobalConstants.LoanProgramIds.DEVELOPMENT,
        GlobalConstants.LoanProgramIds.FIX_AND_FLIP
      ]
    },
    {
      question: "What is the name of your project?",
      inputField: myForm.getInputField("", getRowFieldBorrowerAppProps({ name: "name" })),
      isRequired: true
    },
    {
      question: "How many lots will be developed?",
      inputField: myForm.getInputField("", getRowFieldBorrowerAppProps({ name: "noOfProposedLots" })),
      displayOnLoanPrograms: [GlobalConstants.LoanProgramIds.ALL_IN_ONE, GlobalConstants.LoanProgramIds.DEVELOPMENT]
    },
    {
      question: "How many months after closing will development begin?",
      inputField: myForm.getInputField("", getRowFieldBorrowerAppProps({ name: "developmentBeginsAfterClosingInMonths" })),
      displayOnLoanPrograms: [GlobalConstants.LoanProgramIds.ALL_IN_ONE, GlobalConstants.LoanProgramIds.DEVELOPMENT]
    },
    {
      question: "How many months will development last?",
      inputField: myForm.getInputField("", getRowFieldBorrowerAppProps({ name: "timeFrameToDevelopInMonths" })),
      displayOnLoanPrograms: [GlobalConstants.LoanProgramIds.ALL_IN_ONE, GlobalConstants.LoanProgramIds.DEVELOPMENT]
    },
    {
      question: "How much is the horizontal development budget? Only the development portion of the budget. If applicable, we will ask for the vertical budget details later.",
      inputField: myForm.getCurrencyField("", getRowFieldBorrowerAppProps({ name: "horizontalDevelopmentBudget" })),
      displayOnLoanPrograms: [GlobalConstants.LoanProgramIds.ALL_IN_ONE, GlobalConstants.LoanProgramIds.DEVELOPMENT]
    },
    {
      question: "What is the size of the current property (acres)? A ballpark is fine. We can get specific details from the appraisal.",
      inputField: [
        myForm.getInputField("", getRowFieldBorrowerAppProps({ width: 3, name: "sizeOfCurrentProperty" })),
        myForm.getDropdownField("", unitDropdownOptions, getRowFieldBorrowerAppProps({ width: 1, name: "sizeOfCurrentPropertyUomdisplayOption" }))
      ],
      displayOnLoanPrograms: [GlobalConstants.LoanProgramIds.ALL_IN_ONE, GlobalConstants.LoanProgramIds.DEVELOPMENT]
    },
    {
      question: "What is the size of the proposed lots (square feet)? Your best estimate is all we need right now.",
      inputField: [
        myForm.getInputField("", getRowFieldBorrowerAppProps({ width: 3, name: "sizeOfProposedLot" })),
        myForm.getDropdownField("", unitDropdownOptions, getRowFieldBorrowerAppProps({ width: 1, name: "sizeOfProposedLotUomdisplayOption" }))
      ],
      displayOnLoanPrograms: [GlobalConstants.LoanProgramIds.ALL_IN_ONE, GlobalConstants.LoanProgramIds.DEVELOPMENT]
    },
    {
      question: "Are there district reimbursements available?",
      inputField: myForm.getDropdownField("", "metadata/list-borrower-application-inputs", getRowFieldBorrowerAppProps({ name: "districtReimbursementsAvailableBorrowerApplicationInputId" })),
      displayOnLoanPrograms: [GlobalConstants.LoanProgramIds.ALL_IN_ONE, GlobalConstants.LoanProgramIds.DEVELOPMENT]
    },
    {
      question: "Please describe available district reimbursements",
      inputField: myForm.getInputField("", getRowFieldBorrowerAppProps({ name: "districtReimbursementsAvailableDescription" })),
      displayOnLoanPrograms: [GlobalConstants.LoanProgramIds.ALL_IN_ONE, GlobalConstants.LoanProgramIds.DEVELOPMENT]
    },
    {
      question: "Are there bonding requirements?",
      inputField: myForm.getDropdownField("", "metadata/list-borrower-application-inputs", getRowFieldBorrowerAppProps({ name: "seasonalityConcernsBorrowerApplicationInputId" })),
      displayOnLoanPrograms: [GlobalConstants.LoanProgramIds.ALL_IN_ONE, GlobalConstants.LoanProgramIds.DEVELOPMENT]
    },
    {
      question: "Please describe bonding requirements",
      inputField: myForm.getInputField("", getRowFieldBorrowerAppProps({ name: "bondingRequirementsDescription" })),
      displayOnLoanPrograms: [GlobalConstants.LoanProgramIds.ALL_IN_ONE, GlobalConstants.LoanProgramIds.DEVELOPMENT]
    },
    {
      question: "Are there seasonality concerns?",
      inputField: myForm.getDropdownField("", "metadata/list-borrower-application-inputs", getRowFieldBorrowerAppProps({ name: "seasonalityConcernsBorrowerApplicationInputId" })),
      displayOnLoanPrograms: [GlobalConstants.LoanProgramIds.ALL_IN_ONE, GlobalConstants.LoanProgramIds.DEVELOPMENT]
    },
    {
      question: "Please describe any seasonality concerns",
      inputField: myForm.getInputField("", getRowFieldBorrowerAppProps({ name: "seasonailtyConcernsDescription" })),
      displayOnLoanPrograms: [GlobalConstants.LoanProgramIds.ALL_IN_ONE, GlobalConstants.LoanProgramIds.DEVELOPMENT]
    },
    {
      question: "Permit Status",
      inputField: myForm.getDropdownField(
        "",
        "metadata/list-permit-status",
        getRowFieldBorrowerAppProps({ name: "permitStatusId" })
      ),
      displayOnLoanPrograms: [
        GlobalConstants.LoanProgramIds.ALL_IN_ONE,
        GlobalConstants.LoanProgramIds.BRIDGE_TO_BUILD,
        GlobalConstants.LoanProgramIds.NEW_CONSTRUCTION
      ]
    },
    {
      question: "How many properties are being built? At Builders Capital, a property equals a foundation with a vertical budget.",
      inputField: myForm.getInputField("", getRowFieldBorrowerAppProps({ name: "numberOfPropertiesOrUnits" })),
      displayOnLoanPrograms: [
        GlobalConstants.LoanProgramIds.ALL_IN_ONE,
        GlobalConstants.LoanProgramIds.NEW_CONSTRUCTION,
        GlobalConstants.LoanProgramIds.BRIDGE_TO_BUILD
      ]
    },
    {
      question: "Would you prefer to make monthly interest payments or use interest reserve? With interest reserve, your payments are made on your behalf as part of your loan.",
      inputField: myForm.getDropdownField("", "metadata/list-borrower-application-inputs", getRowFieldBorrowerAppProps({ name: "useInterestReserveBorrowerApplicationInputId" }))
    },
    {
      question: "What is your preferred loan term?",
      inputField: myForm.getInputField("", getRowFieldBorrowerAppProps({ name: "requestedLoanTermInMonths" }))
    },
    {
      question: "Have you paid for any costs on the project? Include anything from engineering plans and permits, to purchasing the lot.",
      inputField: myForm.getCheckboxField("", getRowFieldBorrowerAppProps({ name: "haveYouPutAnyMoneyIntoTheProject" }))
    },
    {
      question: "How much money do you plan to put down?",
      inputField: myForm.getCurrencyField("", getRowFieldBorrowerAppProps({ name: "cashDown" }))
    },
    {
      question: "Who will be doing the construction work?",
      inputField: myForm.getDropdownField(
        "",
        "metadata/list-borrower-application-inputs",
        getRowFieldBorrowerAppProps({ name: "whoWillDoConstructionBorrowerApplicationInputId" })
      )
    },
    {
      question: "Builder",
      inputField: myForm.getDropdownField(
        "",
        "metadata/list-builder-borrower-orgs",
        getRowFieldBorrowerAppProps({ name: "builderOrganizationId" })
      )
    },
    {
      question: "Payoff Strategy",
      inputField: myForm.getDropdownField(
        "",
        "metadata/list-payoff-strategy",
        getRowFieldBorrowerAppProps({ name: "payoffStrategyId" })
      )
    },
    {
      question: "Payoff Strategy Description",
      inputField: myForm.getTextareaField("", getRowFieldBorrowerAppProps({ name: "payoffStrategyDescription" })),
    },
    {
      question: "What is your desired closing date?",
      inputField: myForm.getCalendarField("", getRowFieldBorrowerAppProps({ name: "requestedClosingDate" })),
    },
    {
      question: "Is there anything you'd like to add about the project?",
      inputField: myForm.getTextareaField("", getRowFieldBorrowerAppProps({ name: "projectComment" }))
    },
    {
      question: "Loan Officer",
      inputField: myForm.getDropdownField(
        "",
        "salesteam/list-loan-officers",
        getRowFieldBorrowerAppProps({ name: "loanOfficerOrganizationContactId" })
      )
    }
  ].forEach(({ question, inputField, isRequired, displayOnLoanPrograms }) => {
    if (displayOnLoanPrograms === undefined || displayOnLoanPrograms?.includes(data?.loanProgramId)) {
      const isWrapLabel = question?.split(" ")?.length > 15;
      myForm.addGroupFields(
        [
          myForm.getLabelField(question as string, {
            position: "right-center",
            width: 10,
            required: isRequired ? true : false,
            wrapLabel: isWrapLabel
          }),
          ...(Array.isArray(inputField) ? [...inputField] : [inputField]),
        ],
        { widths: 16 }
      );
    }
  });

  const viewOnlyTagPages = {
    "Cost Paid": <LmsBorrowerApplicationCostPaidTable suffix={`borrowerapplication/list-cost-paid/${loanId}`} />,
    Entity: <LmsBorrowerApplicationEntityTable suffix={`borrowerapplication/entities/${loanId}`} />,
    Property: <LmsBorrowerApplicationProperty suffix={`borrowerapplication/list-property/${loanId}`} />
  };

  const editTagPages = {
    "Cost Paid": <LmsBorrowerApplicationCostPaidTable suffix={`borrowerapplication/list-cost-paid/${loanId}`} isEdit />,
    Entity: <LmsBorrowerApplicationEntityTable suffix={`borrowerapplication/entities/${loanId}`} />,
    Property: <LmsBorrowerApplicationProperty suffix={`borrowerapplication/list-property/${loanId}`} isEdit />
  };

  return (
    <>
      <LmsFormGenerator
        error={error}
        myForm={myForm}
        isLoading={isLoading}
        {...(loanDetailsData?.loanStatus === "Application" && loanDetailsData?.loanSubStatus === "Active" 
          ? {topActions: ["edit"], actions: ["save","save-close","close"]} 
          : {actions: []}
        )}
        hideTopActionsOnEdit
        onEditClick={() => setIsEditModeOn(true)}
        onCloseClick={() => setIsEditModeOn(false)}
      />
      {!isEditModeOn && <LmsTabPages tabPages={viewOnlyTagPages} isNested />}
      {isEditModeOn && <LmsTabPages tabPages={editTagPages} isNested />}
    </>
  );
};

const PreviousRecordingDetailTable = ({ assigmentId }) => {
  return <LmsTable
    title="Previous Recording Detail"
    apiSuffix={`loans/assignments/${assigmentId}/history`}
    isDisabled
  />;
};

const RecordingDetailTable = ({ assigmentId }) => {
  const [{ data: loanAssignment }] = useFetchData(assigmentId ? `loans/assignments/${assigmentId}` : "");
  return <LmsTable
    title="Recording Detail"
    data={[loanAssignment]}
    columnLabelsDictionary={{
      assignmentTypeDisplay: "Assignment Type",
      currentOrganizationDisplay: "Current Organization",
      newOrganizationDisplay: "New Organization",
    }}
    displayOnlyProps={["assignmentTypeDisplay", "currentOrganizationDisplay", "newOrganizationDisplay", "recordingNumber", "recordingDate"]}
    isDisabled
  />;
};

const ViewAssignmentForm = ({ id }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`loans/assignments/${id}`);

  const myForm = new BCFormController(data);
  myForm.addCenteredGroup([myForm.getDisplayField("Loan #", { name: "loanNumber" })]);
  myForm.addCenteredGroup([myForm.getDisplayField("Borrower")]);
  myForm.addCenteredGroup([myForm.getDisplayField("Special Purpose Entity", { name: "spe" })]);
  myForm.addCenteredGroup([myForm.getDisplayField("Property Address")]);
  myForm.addCenteredGroup([myForm.getDisplayField("Project Name")]);
  myForm.addCenteredGroup([myForm.getDisplayField("Parcel #", { name: "parcelNumbers" })]);
  myForm.addCenteredGroup([myForm.getDisplayField("Assignment Order")]);
  myForm.addCenteredGroup([myForm.getDisplayField("Assignment Type", { name: "assignmentTypeDisplay" })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Assignment Created", { disabled: true })]);
  myForm.addCenteredGroup([myForm.getCalendarField("Transfer Date", { disabled: true })]);
  myForm.addCenteredGroup([myForm.getComponent(<PreviousRecordingDetailTable assigmentId={id}/>, { width: 12 })], { blankWidth: 2 });
  myForm.addCenteredGroup([myForm.getComponent(<RecordingDetailTable assigmentId={id}/>, { width: 12 })], { blankWidth: 2 });
  myForm.addCenteredGroup([myForm.getDisplayField("Fedex Number")]);
  myForm.addCenteredGroup([myForm.getDisplayField("Fedex Sent To")]);
  myForm.addCenteredGroup([myForm.getCalendarField("Request To Be Returned Date", { disabled: true })]);
  myForm.addCenteredGroup([myForm.getCalendarField("Received Asset Back Date", { disabled: true })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Assignment Not Required", { disabled: true })]);

  return <>
    <LmsFormGenerator myForm={myForm} actions={["close"]} isLoading={isLoading} error={error}/>
    <LmsTable actionButtons={["export"]} apiSuffix={`loans/assignments/${id}/document-log`}/>
  </>;
};

const ServicingForm = ({ loanId= "" }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`loans/${loanId}`);
  const myForm = new BCFormController(data, { values: { loanId }, submitMethod: "POST", submitSuffix: "loandetails/interest-rate-history" });
  myForm.addCenteredGroup([myForm.getDropdownField("Borrower", "metadata/list-borrower-organizations", { disabled: true,  name: "borrowerOrganizationId" })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Special Purpose Entity", "orgs/list-spes", { name: "specialPurposeEntityId", disabled: true })]);
  myForm.addCenteredGroup([myForm.getDropdownDisplayField("Loan", { name: "display" })]);
  myForm.addCenteredGroup([myForm.getInputField("Current Interest Rate")]);
  myForm.addCenteredGroup([myForm.getCalendarField("Effective Date", { required: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Change Type Option", LmsVotingFixedRateChangeOptions, { required: true, name: "ChangeTypeOption" })]);
  myForm.addCenteredGroup([myForm.getInputField("Revised Interest Rate", { required: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Change Reason", "metadata/list-loan-interest-change-reasons", { required: true, name: "InterestRateChangeReasonId" })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Comments", { name: "Description", required: true })]);
  return <>
    <LmsFormGenerator myForm={myForm} actions={["save", "close"]} isLoading={isLoading} error={error}/>
  </>;
};

const ViewDetailsForm = ({ id = "", loanId = "", loanDetailsData = {} as any, isShortVersion = false }) => {
  const { width } = useWindowSize();
  const [{ data, isLoading }] = useFetchData(loanId ? `loans/${loanId}` : id && !loanDetailsData ? `loandetails/${id}` : "");
  const myForm = new BCFormController(data || loanDetailsData, { initIsDisabled: true, isCompact: true });
  if (isShortVersion) {
    myForm.addGroupFields([
      myForm.getInputField("App #", { name: "applicationNumber" }),
      myForm.getInputField("Loan #", { name: "loanNumber" }),
      myForm.getInputField("Project", { name: "projectName" }),
      myForm.getInputField("Primary Borrower Name", { name: "borrowerOrganization" }),
      myForm.getInputField("Phase", { name: "loanPhase" }),
      myForm.getInputField("Status", { name: "loanStatus" }),
    ]);
  }
  else {
    myForm.addGroupFields([
      myForm.getInputField("App #", { name: "applicationNumber" }),
      myForm.getInputField("Loan #", { name: "loanNumber" }),
      myForm.getInputField("Project", { name: "projectName" }),
      myForm.getInputField("Primary Borrower Name", { name: "borrowerOrganization" }),
      myForm.getInputField("Partnered Loan", { name: "partneredLoan" }),
      myForm.getInputField("Phase", { name: "loanPhase" }),
      myForm.getInputField("Status", { name: "loanStatus" }),
      myForm.getInputField("Sub Status", { name: "loanSubStatus" }),
      myForm.getInputField("Loan Program"),
    ]);
  }

  const myFormForSmallScreen = new BCFormController(data || loanDetailsData, { initIsDisabled: true, isCompact: true });
  myFormForSmallScreen.addGroupFields([
    myFormForSmallScreen.getInputField("App #", { name: "applicationNumber" }),
    myFormForSmallScreen.getInputField("Loan #", { name: "loanNumber" }),
    myFormForSmallScreen.getInputField("Project", { name: "projectName" }),
    myFormForSmallScreen.getInputField("Primary Borrower Name", { name: "borrowerOrganization" }),
    myFormForSmallScreen.getInputField("Partnered Loan", { name: "partneredLoan" }),
  ]);
  myFormForSmallScreen.addGroupFields([
    myFormForSmallScreen.getInputField("Phase", { name: "loanPhase" }),
    myFormForSmallScreen.getInputField("Status", { name: "loanStatus" }),
    myFormForSmallScreen.getInputField("Sub Status", { name: "loanSubStatus" }),
    myFormForSmallScreen.getInputField("Loan Program"),
    myFormForSmallScreen.getBlankField()
  ]);

  return <>
    {(width >= 1600 || isShortVersion) && <LmsFormGenerator myForm={myForm} isLoading={isLoading} skipActions />}
    {(width < 1600 && !isShortVersion) && <LmsFormGenerator myForm={myFormForSmallScreen} isLoading={isLoading} skipActions />}
  </>;
};

const LmsLoanDetails = ({ ...props }: IDefaultPagesProps) => {
  const { params = {}, history } = props;
  const allowedActions = ["accessreport"];
  const budgetSubpages = [
    "addbudget",
    "editbudget",
    "transferbudget",
    "addbudgetdetails",
    "editbudgetdetails",
    "viewbudgetdetails",
    "addbankaccount",
    "editbankaccount",
    "addentity",
    "editloanofficer",
    "addcomment",
    "editcomment",
    "viewassignment",
    "addcondition",
    "editcondition",
    "addservicing",
    "editservicing",
    "viewcostpaid",
    "addcostpaid",
    "editcostpaid",
    "viewentity",
    "viewproperty",
    "addproperty",
    "editproperty",
    "viewunit",
    "addunit",
    "editunit"
  ];
  const allowedSubpages = [...budgetSubpages];
  const { isEdit, actionLower, isMainPage, isActionAllowed, id, query } = useParseRouteActions(params, allowedActions, allowedSubpages);
  const [{ data: loanDetails, isLoading }, reFetchLoanDetails] = useFetchData(id ? `loandetails/${id}` : "");
  const loanIdFromDetails = loanDetails?.loanId || "";
  const loanClosingIdFromDetails = loanDetails?.loanClosingId || "";
  const loanServicingIdFromDetails = loanDetails?.loanServicingId || "";
  const hasBorrowerApplication = loanDetails?.hasBorrowerApplication;
  const loanApplicationNumber = loanDetails?.applicationNumber || "";
  const loanId = query.get("loanId") || loanIdFromDetails || id;
  const tempId = query.get("tempId");
  const servicingLoanId = query.get("servicingLoanId") || loanId;
  const loanDetailsId = query.get("loanDetailsId") || "";
  const loanBudgetId = query.get("loanBudgetId") || "";
  const loanDrawId = query.get("loanDrawId") || "";
  const borrowerApplicationId = query.get("borrowerApplicationId") || "";
  const propertyId = query.get("propertyId") || "";
  const tab = query.get("tab") || "";
  const conditionSource = query.get("conditionSource") || "";
  if (!isActionAllowed) {
    history.goBack();
  }

  let tabPages = {};
  if (loanIdFromDetails) {
    tabPages = {
      "Status History": <LmsLoanDetailsStatusHistoryPage loanIdFromDetails={loanIdFromDetails} />,
      "Terms": <LmsLoanDetailsTermsTab id={id} loanId={loanIdFromDetails} loanDetails={loanDetails} reFetchLoanDetails={reFetchLoanDetails} />,
      "Costs": <LmsLoanDetailsCosts loanId={loanIdFromDetails} loanDetails={loanDetails} />,
      "Property": <LmsLoanPropertyTable loanId={loanIdFromDetails} />,
      "Budget": <LmsLoanDetailsBudgets id={id} loanId={loanIdFromDetails} />,
      "Project": <LmsLoanDetailsProject id={loanIdFromDetails} loanDetails={loanDetails} />,
      "Proposal": <LmsLoanDetailsProposal loanId={loanIdFromDetails} loanDetailsId={id} loanDetails={loanDetails} />,
      "Closing": <LmsLoanDetailsClosing id={loanClosingIdFromDetails} loanId={loanIdFromDetails} loanDetailsId={id} loanDetails={loanDetails} />,
      "Servicing": <LmsLoanDetailsServicing id={loanServicingIdFromDetails} />,
      "Entities": <LmsLoanDetailsEntities loanId={loanIdFromDetails} />,
      "Loan History": <LmsLoanDetailsLoanHistory loanId={loanIdFromDetails} loanApplicationNumber={loanApplicationNumber} />,
      "Documents": <LmsLoanDetailsTabs loanId={loanIdFromDetails} loanDetailsId={id} />,
      "Comments": <LmsLoanCommentsTabs suffix={`loandetails/comments/${loanId}`} loanId={loanId} loanDetailsId={id} />,
      "Draws": <LmsLoanDetailsDraws loanId={loanIdFromDetails} loanDetailsId={id} />,
      "Assignments": <LmsLoanDetailsAssignments loanId={loanIdFromDetails} />,
      "Conditions": <LmsLoanDetailsConditions loanId={loanIdFromDetails} />,
    };

    if (hasBorrowerApplication) {
      tabPages["Borrower Application"] = (<LmsLoanDetailsBorrowerApplication loanId={loanIdFromDetails} loanDetailsData={loanDetails} />);
    }
  }

  return <React.Fragment>
    {isMainPage && <LmsLoanDetailsTable/>}
    {(!isMainPage && actionLower) && <React.Fragment>
      {isEdit && <>
        {!isLoading && loanDetails && <>
          <ViewDetailsForm loanDetailsData={loanDetails}/>
          <LmsTabPages tabPages={tabPages} menuBreakIndex={hasBorrowerApplication ? 9 : 8} initTab={tab} refreshDependency={id} />
        </>}
        {isLoading && <BCLoader/>}
      </>}
      {actionLower === "accessreport" && <AccessReportForm id={id} />}
      {actionLower === "addbudget" && <AddBudgetForm id={id} loanId={loanId} tempId={tempId} />}
      {actionLower === "addservicing" && <ServicingForm loanId={servicingLoanId} />}
      {actionLower === "editservicing" && <ServicingForm loanId={servicingLoanId} />}
      {actionLower === "editbudget" && <EditBudget id={id} loanDetailsId={loanDetailsId} />}
      {actionLower === "addbudgetdetails" && <AddBudgetDetails loanBudgetId={loanBudgetId} />}
      {actionLower === "editbudgetdetails" && <EditBudgetDetails id={id} loanDetailsId={loanDetailsId} />}
      {actionLower === "viewbudgetdetails" && <EditBudgetDetails id={id} loanDetailsId={loanDetailsId} isView={true} />}
      {actionLower === "addbankaccount" && <AddBudgetBankAccountForm />}
      {actionLower === "editbankaccount" && <EditBudgetBankAccountForm id={id} />}
      {actionLower === "addentity" && <LmsAddEntities loanId={loanId} />}
      {actionLower === "editloanofficer" && <EditLoanOfficerForm loanId={loanId} />}
      {actionLower === "addcomment" && <AddLoanDetailsCommentForm loanId={loanId} loanDrawId={loanDrawId} />}
      {actionLower === "editcomment" && <EditLoanDetailsCommentForm id={id} loanDetailId={loanDrawId} />}
      {actionLower === "viewassignment" && <ViewAssignmentForm id={id} />}
      {actionLower === "addcondition" && <AddConditionForm conditionSource={conditionSource} loanDetailsId={loanDetailsId} />}
      {actionLower === "editcondition" && <EditConditionForm id={id} />}
      {actionLower === "viewcostpaid" && <BorrowerApplicationCostPaidForm id={id} />}
      {actionLower === "addcostpaid" && <BorrowerApplicationCostPaidForm isAdd={true} borrowerApplicationId={borrowerApplicationId} />}
      {actionLower === "editcostpaid" && <BorrowerApplicationCostPaidForm id={id} isEdit={true} />}
      {actionLower === "viewentity" && <BorrowerApplicationViewEntity id={id} />}
      {actionLower === "viewproperty" && <BorrowerApplicationPropertyForm id={id} />}
      {actionLower === "addproperty" && <BorrowerApplicationPropertyForm isAdd={true} borrowerApplicationId={borrowerApplicationId} />}
      {actionLower === "editproperty" && <BorrowerApplicationPropertyForm id={id} isEdit={true} />}
      {actionLower === "viewunit" && <BorrowerApplicationPropertyUnit id={id} />}
      {actionLower === "addunit" && <BorrowerApplicationPropertyUnit id={id} isAdd={true} propertyId={propertyId} />}
      {actionLower === "editunit" && <BorrowerApplicationPropertyUnit id={id} isEdit={true} />}
    </React.Fragment>}
  </React.Fragment>;
};

export default LmsLoanDetails;